import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { PaymentConfirmationPreview, partyAndRowsForBillPayment } from "./paymentConfirmation";
import DraggableModal from "./DraggableModal";
import { printMultipleSalesReports, SalesReportPdfPreview, EmailSalesReportModal } from "./SalesReports";
import SentStatusIcon from "./SentStatusIcon";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkStatusDialog from "./BulkStatusDialog";
import SearchSelect from "./SearchSelect";
import { RejectionBadge, rejectedCasesForLot, RejectionDetailsReadOnly } from "./rejectionReport";
import { ProtectionResponsesReadOnly, hasProtectionResponses } from "./protectionReport";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import useDragReorder, { DragHandle } from "./useDragReorder";
import ExportButton from "./ExportButton";
import { num2, intOr, s } from "./xlsxExport";
import { nrmCertNum } from "./exportCertUsage";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase, getCached } from "../supabaseDB";
import useDocumentTitle from "../useDocumentTitle";
import { calcLotProfit } from "./profitCalc";
import { DISPOSITION_STATUSES, PENDING_STATUSES, deriveDeliveryStatus, deriveBillLineLiveStatus, derivePalletStatus, resolvePendingStage, buildSlPassingIndex, invoicesForPallet, twinRiverPalletStatus, twinRiverLotStatus, twinRiverProductStatus } from "./saleStatusCalc";
import FileDropZone from "./FileDropZone";
import { ALLOWED_SUPPORTING_DOC_EXT } from "./pdfMerge";

/* Per the user spec the Bills Summary export breaks "Advance Financing
   Fee" out of the general fees bucket so it is its own column. Match
   is whitespace/case-tolerant — same shape used by the importer. */
function isAdvanceFinancingFee(fe) {
  var n = fe && fe.feeType ? String(fe.feeType).trim().toLowerCase() : "";
  return n === "advance financing fee";
}
function feeName(fe) {
  if (!fe) return "";
  return fe.feeType === "__custom__" ? (fe.feeCustom || "Custom Fee") : (fe.feeType || "Fee");
}

/* Tolerance-sheet defect category colors (2026-10-15) — same values as
   ShipmentRegistry.js's TOLERANCE_CATEGORY_COLORS, duplicated per this
   codebase's per-file convention rather than imported. */
const TOLERANCE_CATEGORY_COLORS = {
  severeCondition: { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.3)", text: "#B91C1C" },
  condition: { bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.3)", text: "#B45309" },
  quality: { bg: "rgba(31,78,121,0.08)", border: "rgba(31,78,121,0.3)", text: "#1F4E79" },
  /* 2026-10-21, Arturo: "Findings" (bugs/worms/insects) — same
     medium-high tone as ShipmentRegistry.js's TOLERANCE_CATEGORY_COLORS.findings. */
  findings: { bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.4)", text: "#C2410C" },
};
/* GFI Team (internal mode) per-defect severity coloring — same as
   ShipmentRegistry.js's INTERNAL_SEVERE_DEFECT_KEYS/
   internalQualityDefectColor, duplicated per this codebase's per-file
   convention. 2026-10-15, Arturo: "los defectos de tr marcados como
   severos... mold, decay, insects... los demas... naranja", then
   "freeze damage es severo porque se pierde al igual que regression". */
const INTERNAL_SEVERE_DEFECT_KEYS = { decay: true, mold: true, insect: true, freezeDamage: true, regression: true };
function internalQualityDefectColor(key) {
  return INTERNAL_SEVERE_DEFECT_KEYS[key] ? TOLERANCE_CATEGORY_COLORS.severeCondition : TOLERANCE_CATEGORY_COLORS.condition;
}
/* Same label override as ShipmentRegistry.js's internalDefectLabel —
   "regression" reads "Mechanical Damage" (matches the real report)
   only in internal-mode render branches. */
function internalDefectLabel(e, t) {
  if (e.key === "__custom__") return e.custom || t.qcTolCustom;
  if (e.key === "regression") return t.qcTolMechanicalDamage;
  return t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
}

/* 2026-10-20, Arturo: "trabajemos en modo no edicion de un bill y lo
   replicamos en modo edicion... mismo theme" — the Bill detail modal's
   sections (teal banner + rounded gray box) never got the Brushed
   Steel treatment the Shipment modal already has (dark-silver
   gradient title plate, square corners, embossed text). Same recipe
   as EditSectionHeader/SectionHeader in ShipmentRegistry.js, just
   ported to this module's single shared SectionBox component below
   (so every section using it — Shipment Info/Product/Pallet Detail/
   Fees/Linked Invoices/My Profit, in both view and edit mode — picks
   it up at once). */
const sectionHeaderStyle = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", border: "1px solid #7d838c", padding: "9px 12px", borderRadius: 0, marginBottom: 0 };
/* 2026-10-20, Arturo: "shipment info deberia ser tipo placa metalica
   dark silver porque es solo lectura" — a SectionBox `style` override
   for sections whose body is pure read-only KV data (no inputs): the
   body continues the same dark-silver plate as the header instead of
   the plain light-gray card, with the matching embossed text color
   for anything inside that still used var(--color-text-muted)/
   var(--color-text) (illegible on silver otherwise). */
/* 2026-10-20, Arturo: "la placa no tiene textura pero no se ve como
   placa, se ve como un rectangulo gris" — the flat linear-gradient
   alone (no inset bevel, no hairline texture) read as a plain block,
   not a plate. Same hairline-texture recipe already used on the
   lighter toolbar strips (repeating-linear-gradient, white hairlines
   here since the plate itself is dark) layered under the same gray
   gradient, plus the inset highlight/shadow bevel every other plate
   in this redesign already carries (sectionHeaderStyle, the dropdown
   panels in ShipmentRegistry.js, etc.) — this body never had either. */
const readOnlyPlateBodyStyle = { background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderTop: "none", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.15)" };
const readOnlyPlateLabelStyle = { color: "#2a3542", fontSize: 10 };
const readOnlyPlateValueStyle = { color: "#000000", fontWeight: 700 };
/* 2026-10-20, Arturo: "la barra superior el color no es el correcto...
   la linea de paid, lot activity etc debe estar en la bande inferior
   donde vive received by" — exact same action-button plate recipe
   already used for Preview/Print PDF/⋮/Edit on the Shipment modal's
   own top strip (ShipmentRegistry.js DetailPanel), reused verbatim so
   the two modals read as one design, not two slightly-different ones. */
const plateActionBtnStyle = { padding: "6px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" };
/* 2026-10-20, Arturo: "los botones paid y finalized dentro del modo
   edit... no les diste el toque placa metalica del color que estan,
   color no solido sino transparente" — the status-transition buttons
   (Draft->Finalized->Paid, same spot as the status badge) were flat
   rgba-tinted backgrounds, the one corner of this header that never
   got the metal-plate gradient treatment the rest of the modal has.
   Same recipe as the blue "+ Add" plate button below (light top ->
   mid -> dark bottom gradient, dark border, white embossed text) but
   keyed by each status's own hue instead of always blue. */
const STATUS_PLATE_COLORS = {
  teal: { grad: "linear-gradient(180deg, #6FBFB2 0%, #2A7A6F 55%, #1C5048 100%)", border: "#123D37" },
  green: { grad: "linear-gradient(180deg, #6EE0B0 0%, #059669 55%, #03694A 100%)", border: "#024A34" },
  gray: { grad: "linear-gradient(180deg, #9CA3AF 0%, #6B7280 55%, #4B5563 100%)", border: "#374151" },
  /* 2026-10-20, Arturo: "protection y has rejections... color solido
     con efecto 3d... rojo metalico solido" — same recipe, extended
     with the hues the Bill title bar's own status/rejections badges
     need (mirrors InvoiceRegistry.js's identical addition). */
  red: { grad: "linear-gradient(180deg, #F08080 0%, #DC2626 55%, #9B1C1C 100%)", border: "#7F1D1D" },
  amber: { grad: "linear-gradient(180deg, #FBBF77 0%, #D97706 55%, #92400E 100%)", border: "#78350F" },
  blue: { grad: "linear-gradient(180deg, #93C5FD 0%, #2563EB 55%, #1E3A8A 100%)", border: "#1E3A8A" },
};
function statusPlateBtnStyle(colorKey, marginLeft) {
  var c = STATUS_PLATE_COLORS[colorKey] || STATUS_PLATE_COLORS.gray;
  return { padding: "3px 10px", borderRadius: 0, border: "1px solid " + c.border, background: c.grad, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.3)", cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: marginLeft || 0 };
}
/* Non-interactive badge variant — title-bar status/rejections pills.
   "los fonts como si estuvieran grabados en el metal" — white,
   engraved-looking text (dark text-shadow pressed into the plate). */
function metallicBadgeStyle(colorKey) {
  var c = STATUS_PLATE_COLORS[colorKey] || STATUS_PLATE_COLORS.gray;
  return { padding: "3px 10px", borderRadius: 0, border: "1px solid " + c.border, background: c.grad, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.4)", cursor: "default", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" };
}
function statusColorKeyFor(status) {
  if (status === "protection" || status === "pas" || status === "dumped") return "red";
  if (status === "partialProtection") return "amber";
  if (status === "goodDelivery") return "green";
  if (status === "pendingDelivery") return "blue";
  return "gray";
}
/* 2026-10-20, Arturo: "la ventana de captura esta muy alta, compactala
   y agrega el efecto placa metalica" — FeeEditDialog/CreditEditDialog/
   BalanceTransferCreator's dialog: embossed label (same recipe as
   sectionHeaderStyle's text) over a recessed field (filterFieldPlate,
   same recipe as the Filters popover), tighter gaps than a normal
   form to keep these small capture dialogs short. */
const plateFieldLabel = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", marginBottom: 3 };

/* ======================== TRANSLATIONS ======================== */
const T = {
  en: {
    title: "Bill Registry", add: "New bill", edit: "Edit bill",
    search: "Search by lot, shipper, or reference...",
    showing: "Showing", of: "of", total: "bills", noResults: "No bills match",
    lot: "Lot", date: "Date", shipper: "Shipper", proforma: "Proforma",
    shipperInvoice: "Shipper Invoice", entryNumber: "Entry #",
    exportCert: "Export Cert", reference: "Reference",
    /* 2026-10-16, Arturo's section reorg (DetailPanel view mode): each
       of these two is a new bordered box. "Shipment Info" — the
       non-editable, shipment-sourced fields (Bill Date through Entry
       #; Lot/Shipper already live in the modal's own title bar now,
       see titleBarActions below, so they're not repeated here).
       "Lot Activity" — my own recommended name for the box holding
       Rejections + Client/Protection Responses (Arturo: "la actividad
       posterior del bill... asignale un nombre a esa seccion como
       'Lot Activity' o algo asi, tu recomiendame el mejor nombre" —
       his own suggestion was already the clearest fit: concise, reads
       naturally next to Payments/My Profit, and room enough for any
       future post-delivery activity beyond just these two). */
    shipmentInfo: "Shipment Info", lotActivity: "Lot Activity",
    product: "Product", description: "Description", category: "Cat",
    cases: "Cases", pallets: "Pallets", reportedPrice: "Reported Price", paidCol: "Paid",
    palletDetail: "Pallet Manifest", palletNumCol: "Pallet #", tagIdCol: "Tag ID", varietyCol: "Variety", weightCol: "Weight",
    palletStatusCol: "Status",
    populateFromShipment: "Populate from shipment",
    /* 2026-10-23, Arturo: "terey avocados exporta fruta de gomez
       berries por tener complicaciones... una columna que se maneje
       solo a nivel interno... que viaje solo al bill" — internal-only
       note (never printed on any document) captured on the Shipment
       and read-only here, same live/stored precedence as Tag ID. */
    actualShipperInitialsCol: "Shipper (int.)",
    actualShipperInitialsHint: "Internal only — never printed on any document.",
    /* Case-loss notation (2026-09-21) — read-only mirror of the
       shipment's pallet-level note, carried in via deepCopyPalletManifest.
       See ShipmentRegistry.js's PALLET_LOSS_REASONS comment. */
    casesLostCol: "Lost", lossReasonMakeup: "Make Up", lossReasonRepack: "Re-Pack", lossReasonOther: "Other",
    /* Tolerance-sheet defects (2026-10-15) — read-only mirror of the
       shipment's per-pallet ClariFresh captures (ShipmentRegistry.js's
       qcDefects/TOLERANCE_DEFECTS). Arturo: "esto hay que hacerlo solo
       visible en cada pallet igual, debajo en el bill del embarque...
       que se propague" — same labels, same keys, duplicated per this
       file's own convention rather than imported. */
    qcDefects: "Defects",
    qcDefectTotal: "Total",
    qcCatSevereCondition: "Severe Condition", qcCatCondition: "Condition", qcCatQuality: "Quality", qcCatFindings: "Findings",
    qcTolBugs: "Bugs", qcTolWorms: "Worms", qcTolInsects: "Insects",
    qcTolDecay: "Decay", qcTolMold: "Mold", qcTolInsect: "Insect", qcTolLeaky: "Leaky", qcTolShrivel: "Shrivel", qcTolSoft: "Soft", qcTolBrokenSkin: "Broken skin",
    qcTolAttachedRemains: "Attached remains", qcTolFloralRemains: "Floral remains", qcTolOffColor: "Off color", qcTolBerryDamage: "Berry damage", qcTolRedBerries: "Red berries", qcTolGreenBerries: "Green berries", qcTolScars: "Scars", qcTolDryCell: "Dry cell",
    qcTolRedCell: "Red cell", qcTolRegression: "Regression", qcTolWhiteCells: "White cells", qcTolRedGreenDryTips: "Red/green or dry tips", qcTolAttachedCore: "Attached core",
    qcTolMechanicalDamage: "Mechanical Damage",
    qcTolRedCells: "Red cells", qcTolDryCells: "Dry cells", qcTolLeaking: "Leaking", qcTolFreezeDamage: "Freeze damage", qcTolSoftFruit: "Soft fruit",
    qcTolCustom: "Other (specify)",
    /* Lot-level regression total (2026-10-15/16) — Blackberry/ClariFresh
       only reports Regression as a single total for the whole load, not
       per pallet (see ShipmentRegistry.js's f.lotRegressionPct and its
       own view-mode badge, same label/copy mirrored here read-only). */
    lotRegressionLabel: "Total Regression shown in this load was:",
    amount: "Amount", saleStatus: "Sale Status",
    // Sale statuses
    goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection",
    pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery",
    computedFromPallets: "Computed from linked invoices' pallet status",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transfer",
    // Fees
    fees: "Fees & Deductions", addFee: "Add fee", removeFee: "Remove",
    selectAllPT: "Select all as PT (pass-through)",
    feeType: "Fee type", feeQty: "Qty", feeRate: "Rate", feeAmount: "Amount",
    selectFee: "Select fee type", customFee: "Other (custom)",
    // Commission
    commission: "Sales Commission", commissionRate: "Rate %", lineComm: "Comm %", lineCommAmt: "Comm $",
    // Summary
    grossSales: "Gross Sales (reported)", totalFees: "Total Fees",
    catCustoms: "Customs", catWarehouse: "Warehouse", catQC: "Quality/Inspection",
    catShipping: "Shipping", catFinancing: "Financing (non-operating)", catCertification: "Certification", catOther: "Other Fees",
    totalCommission: "Commission", subtotal: "Subtotal",
    lessPaid: "Less Amount Paid", amountDue: "Amount Due",
    // Payments section
    paymentsSection: "Payments", originalTotal: "Original Total", currentBalance: "Current Balance",
    noPayments: "No payments recorded", paymentRef: "Reference", paymentMethodCol: "Method",
    /* Linked Invoices section + list column */
    linkedInvoices: "Linked Invoices", linkedInvoicesCount: "{n} linked",
    noLinkedInvoices: "No invoices yet",
    linkedInvAmount: "Amount", linkedInvHeader: "INVOICE / PU",
    linkInvoice: "Link invoice", linkInvoicePh: "Pick invoice to link…", linkInvoiceCancel: "Cancel",
    linkInvoiceEmpty: "No invoices available to link", linkInvoiceLinked: "Linked",
    // Edit lock
    editLockedTitle: "Editing locked",
    editLockedMsg: "This record has payments applied. To edit, first delete the payments.",
    editLockedHint: "Click any payment below to open it in Payments and delete it.",
    // Batch payment modal
    makePayment: "Make Payment", makePayments: "Make Payments",
    batchPaymentTitle: "Batch Payment",
    concept: "Concept", conceptPayment: "Payment", conceptAdvance: "Advance",
    paymentDate: "Payment Date", paymentMethod: "Method", paymentReference: "Reference / Wire #",
    processingDate: "Processing Date", deliveryDate: "Delivery Date",
    refColumn: "Reference",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    paymentAmount: "Payment Amount",
    totalPayment: "Total Payment",
    noOpenBills: "Selected bills have no outstanding balance.",
    paymentSaved: "Payment recorded",
    overApply: "Payment cannot exceed Due Amount",
    // Actions
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    confirmDel: "Delete this bill?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "Unsaved changes. Exit?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Bill saved", deleted: "Bill deleted",
    notes: "Notes", billDate: "Bill Date", dueDate: "Due Date",
    // Filters
    all: "All", allShippers: "All shippers", allStatuses: "All statuses", allSaleStatuses: "All sale statuses", allCerts: "All certificates",
    // Status
    statusDraft: "Draft", statusFinalized: "Finalized", statusPaid: "Paid",
    statusAwaiting: "Awaiting Payment",
    status: "Status", createInvoice: "Create Invoice",
    sourceShipment: "Source Shipment",
    credits: "Credits", creditAmount: "Amount", creditBillRef: "Bill Reference", addCredit: "Add credit",
    balanceTransfers: "Balance Transfers", btDirection: "Direction", btSend: "Send my negative to…", btReceive: "Receive negative from…",
    btOtherBill: "Other bill", btAmount: "Amount", btCreate: "Create transfer", btType: "Type",
    btCredit: "Credit (received)", btDebit: "Debit (sent)", btCancel: "Cancel",
  },
  es: {
    title: "Registro de Bills", add: "Nuevo bill", edit: "Editar bill",
    search: "Buscar por lote, exportador o referencia...",
    showing: "Mostrando", of: "de", total: "bills", noResults: "Sin resultados",
    lot: "Lote", date: "Fecha", shipper: "Exportador", proforma: "Proforma",
    shipperInvoice: "Factura del Exportador", entryNumber: "Entry #",
    exportCert: "Cert. Exportación", reference: "Referencia",
    shipmentInfo: "Información del Embarque", lotActivity: "Actividad del Lote",
    product: "Producto", description: "Descripción", category: "Cat",
    cases: "Cajas", pallets: "Pallets", reportedPrice: "Precio Reportado", paidCol: "Pagado",
    palletDetail: "Manifiesto de Pallets", palletNumCol: "Pallet #", tagIdCol: "ID Tag", varietyCol: "Variedad", weightCol: "Peso",
    palletStatusCol: "Estado",
    populateFromShipment: "Traer del embarque",
    actualShipperInitialsCol: "Exportador (int.)",
    actualShipperInitialsHint: "Solo interno — nunca se imprime en ningún documento.",
    /* Notación de cajas perdidas (2026-09-21) — espejo de solo lectura
       de la nota del embarque, se arrastra vía deepCopyPalletManifest.
       Ver el comentario de PALLET_LOSS_REASONS en ShipmentRegistry.js. */
    casesLostCol: "Perdidas", lossReasonMakeup: "Make Up", lossReasonRepack: "Re-Pack", lossReasonOther: "Otra",
    qcDefects: "Defectos",
    qcDefectTotal: "Total",
    qcCatSevereCondition: "Condición Severa", qcCatCondition: "Condición", qcCatQuality: "Calidad", qcCatFindings: "Hallazgos",
    qcTolBugs: "Bichos", qcTolWorms: "Gusanos", qcTolInsects: "Insectos",
    qcTolDecay: "Pudrición", qcTolMold: "Moho", qcTolInsect: "Insecto", qcTolLeaky: "Fuga de jugo", qcTolShrivel: "Deshidratación", qcTolSoft: "Blanda", qcTolBrokenSkin: "Piel rota",
    qcTolAttachedRemains: "Restos adheridos", qcTolFloralRemains: "Restos florales", qcTolOffColor: "Color fuera de estándar", qcTolBerryDamage: "Daño en la baya", qcTolRedBerries: "Bayas rojas", qcTolGreenBerries: "Bayas verdes", qcTolScars: "Cicatrices", qcTolDryCell: "Célula seca",
    qcTolRedCell: "Célula roja", qcTolRegression: "Regresión", qcTolWhiteCells: "Células blancas", qcTolRedGreenDryTips: "Puntas rojas/verdes o secas", qcTolAttachedCore: "Núcleo adherido",
    qcTolMechanicalDamage: "Daño Mecánico",
    qcTolRedCells: "Células rojas", qcTolDryCells: "Células secas", qcTolLeaking: "Fuga de jugo", qcTolFreezeDamage: "Daño por congelamiento", qcTolSoftFruit: "Fruta blanda",
    qcTolCustom: "Otro (especificar)",
    lotRegressionLabel: "Regresión total reportada en esta carga:",
    amount: "Monto", saleStatus: "Status de Venta",
    goodDelivery: "Good Delivery", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial",
    pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega",
    computedFromPallets: "Calculado del status de pallets en las facturas vinculadas",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transferencia",
    fees: "Gastos y Deducciones", addFee: "Agregar gasto", removeFee: "Eliminar",
    selectAllPT: "Marcar todos como PT (passthrough)",
    feeType: "Tipo de gasto", feeQty: "Cant", feeRate: "Tarifa", feeAmount: "Monto",
    selectFee: "Seleccionar tipo", customFee: "Otro (personalizado)",
    commission: "Comisión de Venta", commissionRate: "Tasa %", lineComm: "Com %", lineCommAmt: "Com $",
    grossSales: "Venta Bruta (reportada)", totalFees: "Total Gastos",
    catCustoms: "Aduana", catWarehouse: "Bodega", catQC: "Calidad/Inspección",
    catShipping: "Envío", catFinancing: "Financiamiento (no operativo)", catCertification: "Certificación", catOther: "Otros Gastos",
    totalCommission: "Comisión", subtotal: "Subtotal",
    lessPaid: "Menos Pagos Realizados", amountDue: "Monto a Pagar",
    // Payments section
    paymentsSection: "Pagos", originalTotal: "Total Original", currentBalance: "Saldo Actual",
    /* Sección Facturas Vinculadas + columna de la lista */
    linkedInvoices: "Facturas Vinculadas", linkedInvoicesCount: "{n} vinculadas",
    noLinkedInvoices: "Sin facturas aún",
    linkedInvAmount: "Monto", linkedInvHeader: "FACTURA / PU",
    linkInvoice: "Vincular factura", linkInvoicePh: "Selecciona factura a vincular…", linkInvoiceCancel: "Cancelar",
    linkInvoiceEmpty: "No hay facturas disponibles para vincular", linkInvoiceLinked: "Vinculada",
    noPayments: "Sin pagos registrados", paymentRef: "Referencia", paymentMethodCol: "Método",
    // Edit lock
    editLockedTitle: "Edición bloqueada",
    editLockedMsg: "Este registro tiene pagos aplicados. Para editarlo, primero elimina los pagos.",
    editLockedHint: "Click en un pago para abrirlo en Pagos y eliminarlo.",
    // Batch payment modal
    makePayment: "Registrar Pago", makePayments: "Registrar Pagos",
    batchPaymentTitle: "Pago en Lote",
    concept: "Concepto", conceptPayment: "Pago", conceptAdvance: "Adelanto",
    paymentDate: "Fecha del Pago", paymentMethod: "Método", paymentReference: "Referencia / # Transferencia",
    processingDate: "Fecha de Procesamiento", deliveryDate: "Fecha de Entrega",
    refColumn: "Referencia",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    paymentAmount: "Monto del Pago",
    totalPayment: "Total del Pago",
    noOpenBills: "Los bills seleccionados no tienen saldo pendiente.",
    paymentSaved: "Pago registrado",
    overApply: "El pago no puede exceder el Monto a Pagar",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    confirmDel: "¿Eliminar este bill?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Cambios sin guardar. ¿Salir?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Bill guardado", deleted: "Bill eliminado",
    notes: "Notas", billDate: "Fecha del Bill", dueDate: "Fecha de Pago",
    all: "Todos", allShippers: "Todos los exportadores", allStatuses: "Todos", allSaleStatuses: "Todos los estados", allCerts: "Todos los certificados",
    statusDraft: "Borrador", statusFinalized: "Finalizado", statusPaid: "Pagado",
    statusAwaiting: "Por Pagar",
    status: "Estado", createInvoice: "Crear Factura",
    sourceShipment: "Embarque Origen",
    credits: "Créditos", creditAmount: "Monto", creditBillRef: "Bill referenciado", addCredit: "Agregar crédito",
    balanceTransfers: "Transferencias de Saldo", btDirection: "Dirección", btSend: "Enviar mi saldo negativo a…", btReceive: "Recibir saldo negativo de…",
    btOtherBill: "Otro bill", btAmount: "Monto", btCreate: "Crear transferencia", btType: "Tipo",
    btCredit: "Crédito (recibido)", btDebit: "Débito (enviado)", btCancel: "Cancelar",
  },
};

/* ======================== CONSTANTS ======================== */
const STORAGE_KEY = "gfi-bills";
const FEE_TYPES_KEY = "gfi-fee-types";
const BILL_STATUSES = ["draft", "finalized", "paid"];
const BILL_STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  finalized: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)" },
  paid: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
};
const SALE_STATUSES = ["goodDelivery", "protection", "pas", "dumped", "donated", "samples", "transfer"];
/* Status Venta filter — 2026-09-27 (Arturo: "hay status que no me
   aparecen en el filtro como pending delivery y pending pick up").
   pendingPickUp/pendingDelivery/partialProtection are never stored on
   a line (they're computed live — see the Status column render below),
   so SALE_STATUSES alone (the 7 manually-settable values) left them
   permanently unfilterable even though the column shows them. Filter
   dropdown only — SALE_STATUSES itself stays the manual-entry list. */
const FILTER_SALE_STATUSES = PENDING_STATUSES.concat(SALE_STATUSES.slice(0, 3)).concat(["partialProtection"]).concat(SALE_STATUSES.slice(3));
const SALE_STATUS_COLORS = {
  goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  /* PAS = Price After Sale. Market condition (oversupply, low prices),
     NOT a quality issue. Less severe than Protection but more severe
     than Partial Protection. Orange to distinguish from red (quality
     failure) and amber (partial protection). */
  pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" },
  /* Computed only (2026-09-12, see saleStatusCalc.js) — a bill line
     whose linked invoice lines are a mix of goodDelivery and
     protection/pas. Never manually selectable. */
  partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" },
  /* Pre-delivery lifecycle stages (2026-09-12, saleStatusCalc.js
     resolvePendingStage) — display-only, never manually selectable. */
  pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" },
  pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" },
  dumped: { bg: "rgba(239,68,68,0.10)", text: "#DC2626", border: "rgba(239,68,68,0.25)" },
  donated: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  samples: { bg: "rgba(139,92,246,0.10)", text: "#7C3AED", border: "rgba(139,92,246,0.25)" },
  /* Transfer = cases moved to another bill/lot. Excluded from gross
     here so the same cases aren't counted twice across bills. Blue
     for "informational, no financial weight on this bill". */
  transfer: { bg: "rgba(59,130,246,0.10)", text: "#2563EB", border: "rgba(59,130,246,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to every
   SALE_STATUS_COLORS badge in this file so Sale Status pills read the
   same squared, beveled way everywhere in the app. */
const SALE_STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
const DEFAULT_FEE_TYPES = [
  "Custom Broker Fee", "Warehouse Fees", "QC Inspection",
  "Temp Recorders", "Advance Financing Fee", "Wire Transfer Fee",
  "Shipping", "Repack", "Fumigation", "USDA Inspection",
  "Cold Treatment", "Palletizing", "Labels/Stickers",
];
const inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
/* 2026-10-20, Arturo: "la ventana filters quedo muy amplia, debe ser
   igual que en embarques" — same Brushed Steel "recessed field on a
   plate" treatment as ShipmentRegistry.js's own filterFieldPlate
   (duplicated here per this codebase's convention, never shared),
   passed to every SearchSelect/date-input inside the Bills Filters
   popover. */
const filterFieldPlate = {
  background: "linear-gradient(180deg, #f2f3f5 0%, #d7dadd 100%)",
  border: "1px solid #9aa0a8",
  borderRadius: 0,
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(255,255,255,0.6)",
  minHeight: 25,
};
/* 2026-10-16, Arturo: "los campos que requieran que se abran para
   capturar algo deben tener color amarillo utilizado en los
   embarques como fees, comision, % de comision, pallets, cajas,
   precio" — same amber-while-empty highlight as ShipmentRegistry.js
   /FixedPricingRegistry.js's own CAP_BG/capBg (identical color),
   applied in BillForm to the capture fields Arturo named: Fees
   table qty/rate/amount, bill-level Sales Commission rate, per-line
   commission rate, and the Product line's Cases/Pallets/Price. */
const CAP_BG = "rgba(245,158,11,0.08)";
function capBg(value) { return (value === "" || value === undefined || value === null) ? CAP_BG : inp.background; }
const EMPTY_FEE = { feeType: "", feeCustom: "", qty: 1, rate: "", amount: 0 };

/* ======================== HELPERS ======================== */
function loadLS(key, fb) { try { var d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }
/* 2026-10-02 — Arturo, live/urgent: "PORQUE LA 58047 YA NO MUESTRA
   QUE TENIA PROTECCION PARCIAL EN LA LINEA DE PRODUCTO? HACE UN
   MOMENTO SI MOSTRABA". Root cause: every sale-status recompute in
   this file (deriveBillLineLiveStatus's invoice-lines input,
   resolvePendingStage's fallback, derivePalletStatus) read invoices
   via plain loadLS("gfi-invoices") — pure localStorage, which only
   gets primed by some OTHER screen (InvoiceRegistry.js) having
   already been opened in this tab. The moment one of the lot's
   invoices gets edited/saved (e.g. a pallet just flagged Protection)
   without the Invoices screen itself being open to refresh that
   local mirror, this screen's status math keeps reading the OLD
   snapshot — same "localStorage is not the source of truth" bug
   class already fixed repeatedly elsewhere in this app (see e.g.
   ShipmentRegistry.js's saleStatusByLot). getCached("invoices") reads
   the in-memory cache loadFromSupabase() fills directly; this screen
   now also force-fetches it once on mount (see the useEffect next to
   slPassingIndex's own) so it's fresh even if Invoices was never
   opened in this tab at all. Falls back to the localStorage mirror
   only when that cache genuinely isn't populated yet. */
function freshInvoicesLS() { var cached = getCached("invoices"); return (cached && cached.length > 0) ? cached : loadLS("gfi-invoices", []); }
/* Paid amount with historical-import fallback: prefer sum(payments[])
   when present; otherwise fall back to amountPaid (used by bulk-paid
   imports that have no individual payment records). */
function paidAmount(rec) {
  var sum = ((rec && rec.payments) || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  if (sum > 0) return sum;
  return parseFloat(rec && rec.amountPaid) || 0;
}
function saveLS(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { console.error("[Bills] storage error", key, e); return false; } }
function getSuppliers() { return loadLS("gfi-suppliers", []); }
/* Builds the persisted shape for bill.extra.salesReportSent from
   EmailDialog's enriched onSent(payload) — { to, from, subject,
   attachmentNames } (see EmailDialog.js's doSend). fix-dashboard.md
   Fase 3 FEATURE 3. */
function buildSentInfo(payload) {
  return {
    sentAt: new Date().toISOString(),
    sentTo: (payload && payload.to) || [],
    sentFrom: (payload && payload.from) || "",
    subject: (payload && payload.subject) || "",
    attachmentNames: (payload && payload.attachmentNames) || [],
  };
}
/* 2026-10-23, Arturo: the internal "actual shipper" note he sets on a
   Shipment's pallet ("GB fruit exported by TA") "no se esta
   replicando al bill" — traced to the same class of bug already
   documented several times in this app's history (e.g. CLAUDE.md
   2026-09-16 Consignee/Broker mirror bug): saveToSupabase/
   loadFromSupabase only ever touch the in-memory Supabase cache, NEVER
   localStorage — this function was a pure loadLS() read, completely
   disconnected from that cache. Within the SAME session as the edit
   (ShipmentRegistry.js does mirror its own saves to localStorage),
   this usually still worked — but any OTHER tab/device, or a Bill
   opened before that mirror was ever primed, saw stale/empty data,
   same root cause as the historical bug. Now prefers the live
   in-memory cache (filled by any screen that already called
   loadFromSupabase/saveToSupabase for "shipments" this session, kept
   current automatically — no resave required) and only falls back to
   the localStorage mirror when that cache is still empty. */
function getShipments() {
  var cached = getCached("shipments");
  return (cached && cached.length > 0) ? cached : loadLS("gfi-shipments", []);
}
function getProducts() { return loadLS("gfi-products", []); }
function getFeeTypes() { return loadLS(FEE_TYPES_KEY, DEFAULT_FEE_TYPES); }
function saveFeeTypes(types) { try { localStorage.setItem(FEE_TYPES_KEY, JSON.stringify(types)); } catch {} }

function fmtDate(d) { if (!d) return "\u2014"; var dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00"); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function nextId(items) {
  var nums = items.map(function(i) { return parseInt((i.id || "").replace("BILL-", ""), 10); }).filter(function(n) { return !isNaN(n); });
  return "BILL-" + String((Math.max(0, ...nums) + 1)).padStart(4, "0");
}

function resolveProductDesc(line) {
  if (line.productDescription) return line.productDescription;
  /* Try the catalog by productId first, then SKU. Bill lines created
     from shipments inherit whichever the shipment carried; lines added
     manually may have one and not the other. Returning a built-from-
     parts string is safer than empty so the detail view always shows
     the full "Blackberry 12/6 oz OG GF Label" presentation. */
  var prods = getProducts();
  var prod = null;
  if (line.productId) prod = prods.find(function(p) { return String(p.id) === String(line.productId) || String(p._uuid) === String(line.productId); });
  if (!prod && line.sku) prod = prods.find(function(p) { return String(p.sku) === String(line.sku); });
  if (!prod) return "";
  var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
  return (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
}

/* Per-pallet weight — mirror of ShipmentRegistry's calcPalletWeight /
   InvoiceRegistry's docPalletWeight. Inlined here so this file stays
   self-contained (project convention: private helpers duplicated per
   registry rather than shared). */
var DOC_SOZ = { "6oz": 6, "12oz": 12, "18oz": 18, "1 dry pint": 16, "1lb": 16, "2lb": 32 };
function docPalletWeight(p) {
  if (!p) return { lb: 0, kg: 0 };
  var oz = (DOC_SOZ[p.size] || 0) * (parseFloat(p.unitsCase) || 0) * (parseFloat(p.casesPallet) || 0);
  if (!oz) return { lb: 0, kg: 0 };
  var lb = oz / 16;
  return { lb: lb, kg: lb * 0.453592 };
}

/* Deep copy of a shipment's pallet manifest into a bill's palletDetail
   (1A) — a plain array/object clone so later edits to either side never
   cross-mutate. Filters out blank placeholder rows the same way the
   manifest UI itself does elsewhere. */
function deepCopyPalletManifest(shipment) {
  return ((shipment && shipment.palletManifest) || [])
    .filter(function(p) { return p && (p.palletNum || p.productBase || p.casesPallet); })
    .map(function(p) { return { ...p }; });
}

function resolveShipperName(supplierId) {
  if (!supplierId) return "";
  var sup = getSuppliers().find(function(s) { return String(s.id) === String(supplierId); });
  if (!sup) return "";
  var name = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
  if (!name && (sup.ownerNombre || sup.nombre)) name = [sup.ownerNombre || sup.nombre, sup.ownerApPaterno || sup.apPaterno].filter(Boolean).join(" ");
  return name;
}

/* Bill total math: subtotal = gross − commission − fees + credits + Σ(transfers).
   Credits ADD (favor the shipper). Balance transfers carry a signed
   amount per their `type`: "credit" adds, "debit" subtracts.
   Accepts the whole bill object so call sites don't have to thread
   credits/transfers separately. Backwards-compatible signature: passing
   a bill with no credits/balanceTransfers keys returns the same result
   as before. */
function calcBillTotals(bill) {
  bill = bill || {};
  var lines = bill.lines || [];
  var fees = bill.fees || [];
  var credits = bill.credits || [];
  var transfers = bill.balanceTransfers || [];
  /* Per-line commission. Each line carries its own commissionRate
     since the line-commission migration. Lines created BEFORE the
     migration (or freshly added without an explicit rate) fall back
     to bill.commissionRate so totals match the legacy behavior
     exactly:  rate × Σ(amts) ≡ Σ(rate × amts).  Lines with status
     dumped/donated/samples are excluded from gross AND from the
     commission base, identical to the prior contract. */
  var billDefaultRate = parseFloat(bill.commissionRate) || 0;
  var grossSales = 0, commAmt = 0;
  lines.forEach(function(l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
      ? billDefaultRate
      : (parseFloat(l.commissionRate) || 0);
    grossSales += lineAmt;
    commAmt += lineAmt * (lineRate / 100);
  });
  /* Math.abs() — a fee is always a deduction by definition (credits[]
     is the separate, additive bucket); a typo'd negative fee amount
     must still SUBTRACT from the total, never flip to adding. Matches
     the same guard InvoiceRegistry.js's calcInvoiceTotal already has.
     2026-09-18, Arturo (Advance Financing Fee captured as -134.40 on
     PU 17449, transferred as-is via fromBill into the invoice's fees):
     "en la linea del fee yo por error lo puse negativo y lo esta
     agregando al total en lugar de restarlo". */
  var totalFees = 0;
  fees.forEach(function(f) { totalFees += Math.abs(parseFloat(f.amount) || 0); });
  var totalCredits = 0;
  credits.forEach(function(c) { totalCredits += parseFloat(c.amount) || 0; });
  var totalTransfers = 0;
  transfers.forEach(function(t) {
    var amt = parseFloat(t.amount) || 0;
    if (t.type === "credit") totalTransfers += amt;
    else if (t.type === "debit") totalTransfers -= amt;
  });
  var subtotal = grossSales - commAmt - totalFees + totalCredits + totalTransfers;
  return {
    grossSales: grossSales,
    totalFees: totalFees,
    totalCredits: totalCredits,
    totalTransfers: totalTransfers,
    commission: commAmt,
    subtotal: subtotal
  };
}

/* Per-line commission $ — used by the form/detail UI Comm $ column
   and the export. Returns 0 for excluded sale-status lines so the
   column matches the contribution to the total. */
function lineCommissionAmount(line, billDefaultRate) {
  if (!line) return 0;
  if (line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer") return 0;
  var amt = (parseInt(line.cases, 10) || 0) * (parseFloat(line.reportedPrice) || 0);
  var rate = (line.commissionRate === undefined || line.commissionRate === null || line.commissionRate === "")
    ? (parseFloat(billDefaultRate) || 0)
    : (parseFloat(line.commissionRate) || 0);
  return amt * (rate / 100);
}

/* All invoices whose lot matches the bill's lot. Same tolerance
   ladder as the Shipment/Bill lookups in InvoiceRegistry: exact →
   trimmed → numeric. For multi-lot invoices, also check each line's
   per-line lot (the importer preserves r.lot per row) and the
   explicit linkedLots[] override populated by the manual "Link
   Invoice" button. Sorted by PU# (numeric) so the row order is
   stable across renders. */
function findInvoicesByLot(lot, invoices) {
  if (!lot && lot !== 0) return [];
  var s = String(lot);
  var trimmed = s.trim();
  var n = parseInt(s, 10);
  function lotMatches(il) {
    if (il == null || il === "") return false;
    if (String(il) === s) return true;
    if (String(il).trim() === trimmed) return true;
    if (!isNaN(n)) {
      var iln = parseInt(il, 10);
      if (!isNaN(iln) && iln === n) return true;
    }
    return false;
  }
  var hits = (invoices || []).filter(function (inv) {
    if (!inv) return false;
    if (lotMatches(inv.lot)) return true;
    if (inv.lines && inv.lines.some(function (l) { return l && lotMatches(l.lot); })) return true;
    if (inv.linkedLots && inv.linkedLots.some(function (ll) { return lotMatches(ll); })) return true;
    return false;
  });
  hits.sort(function (a, b) {
    var an = parseInt(a.puNumber, 10); var bn = parseInt(b.puNumber, 10);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return String(a.puNumber || "").localeCompare(String(b.puNumber || ""));
  });
  return hits;
}

/* Customs / aduana fees are paid by GFI, so they never default to
   passthrough on a TR-linked bill. Matches the catalog "Custom
   Broker Fee" plus any custom-typed fee whose name contains
   customs / aduana / broker (case-insensitive). */
function isCustomsFee(feeType, feeCustom) {
  var name = String(feeType === "__custom__" ? (feeCustom || "") : (feeType || "")).toLowerCase();
  if (!name) return false;
  return name.indexOf("custom") >= 0 || name.indexOf("aduana") >= 0 || name.indexOf("broker") >= 0;
}

/* 2026-10-20, Arturo: "'From EXP-0142' no me dice nada, prefiero diga
   From Bill y el numero de factura que me dieron" — a fee transferred
   from an Expense Bill shows the VENDOR's own invoice number
   (expense bill's `reference` field), never our internal EXP-####
   id; falls back to the id only if that expense bill can't be found
   (deleted) or predates this field (legacy `fromExpense: true`). A
   fee transferred from an Invoice already shows its PU# (already
   meaningful to Arturo, left as-is). Shared by the locked-row label
   in BillForm and FeeEditDialog's own origin banner. */
function resolveFeeOriginLabel(fee, allExpenseBills, lang) {
  if (fee.fromExpense) {
    var expBill = fee.fromExpense !== true ? (allExpenseBills || []).find(function (eb) { return String(eb.id) === String(fee.fromExpense); }) : null;
    var ref = expBill && expBill.reference ? expBill.reference : (fee.fromExpense !== true ? fee.fromExpense : null);
    return ref
      ? (lang === "es" ? "De Bill " + ref : "From Bill " + ref)
      : (lang === "es" ? "Transferido de Expense Bill" : "Transferred from Expense Bill");
  }
  if (fee.fromInvoice) {
    return lang === "es" ? "De Invoice PU# " + fee.fromInvoice : "From Invoice PU# " + fee.fromInvoice;
  }
  return null;
}

/* 2026-09-28 (Arturo: "el fees de un bill de embarque, necesito
   separar de los fees el cobro de financiamiento ya que no es un
   gasto operativo sino financiero... podrias separar por categoria,
   los de customs con su propio total, los de warehouse con su propio
   total, y asi, en el catalogo de fees estan separados por
   categoria") — a Bill's fee row only stores a name (feeType/
   feeCustom), never a category; the actual category lives on the
   matching row of FeeCatalog.js's `fee_catalog` table (customs/
   warehouse/qc/shipping/financing/certification/other, same catalog
   as the Fee Catalog module) — resolved by name here rather than
   duplicating a second catalog. Falls back to keyword matching for
   any fee name not found in the catalog (deleted/renamed catalog
   entries, or a still-custom-typed fee that was never added there),
   same "never leave a real fee silently uncategorized" principle as
   the Other bucket. */
var FEE_CATEGORY_ORDER = ["customs", "warehouse", "qc", "shipping", "financing", "certification", "other"];
function categorizeFeeName(name, feeCategoryMap) {
  var key = String(name || "").trim().toLowerCase();
  if (!key) return "other";
  if (feeCategoryMap && feeCategoryMap[key]) return feeCategoryMap[key];
  if (key.indexOf("financ") >= 0) return "financing";
  if (key.indexOf("custom") >= 0 || key.indexOf("aduana") >= 0 || key.indexOf("broker") >= 0) return "customs";
  if (key.indexOf("warehouse") >= 0 || key.indexOf("bodega") >= 0 || key.indexOf("repack") >= 0) return "warehouse";
  if (key.indexOf("qc") >= 0 || key.indexOf("inspect") >= 0 || key.indexOf("fumig") >= 0 || key.indexOf("quality") >= 0 || key.indexOf("calidad") >= 0) return "qc";
  if (key.indexOf("ship") >= 0 || key.indexOf("freight") >= 0 || key.indexOf("envio") >= 0) return "shipping";
  if (key.indexOf("cert") >= 0) return "certification";
  return "other";
}
/* Same Math.abs()-as-deduction rule as calcBillTotals's totalFees, so
   the sum of every category line always reconciles exactly with the
   single "Total Fees" figure the rest of the app already relies on. */
function calcBillFeesByCategory(bill, feeCategoryMap) {
  var fees = (bill && bill.fees) || [];
  var byCategory = {};
  fees.forEach(function(f) {
    var name = f.feeType === "__custom__" ? (f.feeCustom || "") : (f.feeType || "");
    var cat = categorizeFeeName(name, feeCategoryMap);
    byCategory[cat] = (byCategory[cat] || 0) + Math.abs(parseFloat(f.amount) || 0);
  });
  return byCategory;
}
/* Shared render of the fee breakdown lines inside the Summary box —
   one line per category that actually has fees on this bill, in
   catalog order, Financing visually separated (own color) from the
   operating categories since it is a financial cost, not operational
   — replaces the old single "Total Fees" line in both the read-only
   DetailPanel and the editable BillForm. */
function FeeBreakdownLines({ bill, feeCategoryMap, t }) {
  var byCategory = calcBillFeesByCategory(bill, feeCategoryMap);
  /* 2026-09-28 (Arturo: "el fee repack de una nueva categoria que hice
     no lo esta desglosando en los bills") — FEE_CATEGORY_ORDER only
     lists the 7 built-in categories, so any custom category Arturo
     creates in Fee Catalog's new category manager (see FeeCatalog.js)
     never showed up here at all, even though categorizeFeeName()
     correctly resolved the fee to it. Built-ins keep their fixed
     order first; any category present on this bill's fees that isn't
     one of them (a custom category) is appended after, alphabetically. */
  var customCatsPresent = Object.keys(byCategory).filter(function(c) { return FEE_CATEGORY_ORDER.indexOf(c) === -1 && byCategory[c] > 0; }).sort();
  var cats = FEE_CATEGORY_ORDER.filter(function(c) { return byCategory[c] > 0; }).concat(customCatsPresent);
  if (cats.length === 0) return null;
  var catLabelKey = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  return cats.map(function(c) {
    var isFinancing = c === "financing";
    return (
      <div key={c} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: isFinancing ? "#B45309" : "#DC2626" }}>
        <span>− {t[catLabelKey[c]] || c}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(byCategory[c])})</span>
      </div>
    );
  });
}

/* Twin River-specific rule: when a bill is linked (via lot) to any
   invoice whose client is Twin River, fees default to passthrough
   (TR pays the shipper directly, GFI passes the cost through).
   Customs is the lone exception — see isCustomsFee. */
function billIsLinkedToTwinRiver(lot) {
  if (!lot && lot !== 0) return false;
  var invs = freshInvoicesLS();
  var linked = findInvoicesByLot(lot, invs);
  return linked.some(function (inv) {
    return String(inv.clientName || "").toLowerCase().indexOf("twin river") >= 0;
  });
}

/* "PU 123, PU 124, PU 125" — used for the auto-populated reference
   field and the INVOICES column in the bills list. */
function formatLinkedInvoiceRef(invoices) {
  return invoices.map(function (inv) { return "PU " + (inv.puNumber || inv.id || "?"); }).join(", ");
}

/* ======================== TOAST ======================== */
function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function() {
    var fadeT = setTimeout(function() { setFading(true); }, 2700);
    var closeT = setTimeout(function() { onClose(); }, 3000);
    return function() { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* ======================== DETAIL PANEL ======================== */
/* Shared payments-history block. Renders Total / list of payments / balance.
   Each row navigates to PaymentRegistry and opens the payment by id. */
function goToPayment(paymentId) {
  if (window.__gfiNavigate) window.__gfiNavigate("payments");
  setTimeout(function() {
    if (typeof window.__gfiPaymentOpen === "function") window.__gfiPaymentOpen(paymentId);
  }, 220);
}

/* 2026-10-16, Arturo: "todas las secciones son desplegables... por
   default... payments... contraido" — `collapsible`/`defaultOpen`
   are optional so the OTHER caller (BillForm's own read-only payments
   display, inside Edit mode) keeps behaving exactly as before:
   always expanded, header not clickable. Only DetailPanel's view
   mode opts in. */
/* 2026-10-20, Arturo: "la seccion payments esta off, checa la barra de
   header, alineala como my profit" — `bare` renders just this
   component's content (totals row / table / balance row), no header
   and no wrapping box of its own, for the view-mode call (BillRegistry
   .js's DetailPanel) which now wraps it in the same SectionBox every
   other section there uses (id="payments", matching My Profit's own
   plate exactly). The edit-mode call (BillForm) keeps its own
   collapsible header/box untouched — out of scope for this pass. */
function PaymentsSection({ payments, total, t, collapsible, defaultOpen, bare }) {
  var [open, setOpen] = useState(defaultOpen !== false);
  var paidSum = (payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var balance = total - paidSum;
  var hasPayments = (payments || []).length > 0;
  var headerStyle = collapsible ? { ...sectionHeaderStyle, width: "100%", display: "flex", alignItems: "center", gap: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" } : sectionHeaderStyle;
  var content = (
    <>
      {/* 2026-10-20, Arturo: "payments & my profit, aplica lo mismo"
          — same rule as Fees & Deductions: standard 11px, no bold,
          var(--color-text) black for everything except a genuinely
          negative amount (none in this block — payments received are
          never negative; the one legitimate red is Current Balance
          still owed, kept below). */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, marginTop: (collapsible && !bare) ? 10 : 0, fontSize: 11 }}>
        <span style={{ color: "var(--color-text)" }}>{t.originalTotal}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{fmtMoney(total)}</span>
      </div>
      {!hasPayments ? (
        <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>{t.noPayments}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 6 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"PAY#"}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"Date"}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.paymentMethodCol}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.paymentRef}</th>
            <th style={{ padding: "5px 6px", textAlign: "right", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"Amount"}</th>
          </tr></thead>
          <tbody>{(payments || []).map(function(p, i) {
            return (
              <tr key={i}
                  onClick={function() { goToPayment(p.paymentId); }}
                  title={"Open " + (p.paymentId || "")}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{p.paymentId || "—"}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{fmtDate(p.date)}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)", textTransform: "capitalize" }}>{p.method || "—"}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{p.reference || "—"}</td>
                <td style={{ padding: "6px", textAlign: "right", fontSize: 11, color: "var(--color-text)" }}>{fmtMoney(p.amount)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "2px solid var(--color-accent)", fontSize: 11 }}>
        <span style={{ color: "var(--color-text)" }}>{t.currentBalance}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{fmtMoney(balance)}</span>
      </div>
    </>
  );
  if (bare) return content;
  return (
    <div style={{ marginTop: 18, padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
      {collapsible ? (
        <button type="button" onClick={function () { setOpen(function (v) { return !v; }); }} style={headerStyle}>
          <span style={{ fontSize: 10, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"▶"}</span>
          <span style={{ flex: 1 }}>{t.paymentsSection}</span>
        </button>
      ) : (
        <div style={headerStyle}>{t.paymentsSection}</div>
      )}
      {(!collapsible || open) && content}
    </div>
  );
}

/* Locks edit when a record has payments applied. Lists each payment with a
   click-through to PaymentRegistry so the user can delete them and re-edit. */
function EditLockDialog({ record, t, onClose }) {
  var payments = record.payments || [];
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ background: "#fff", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{"\u{1F512}"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#B45309" }}>{t.editLockedTitle}</h3>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.5 }}>{t.editLockedMsg}</p>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.editLockedHint}</p>

        <div style={{ borderRadius: 8, border: "1px solid var(--color-border)", overflow: "hidden", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "var(--color-bg)", borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>PAY#</th>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Date</th>
              <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Amount</th>
            </tr></thead>
            <tbody>{payments.map(function(p, i) {
              return (
                <tr key={i}
                    onClick={function() { goToPayment(p.paymentId); onClose(); }}
                    title={"Open " + (p.paymentId || "")}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{p.paymentId || "—"} →</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.close}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Read-only mini-preview of an invoice. Opened from the "Linked
   Invoices" mini-table in the bill detail panel. Mirrors the
   BillPreviewModal in InvoiceRegistry but pointed the other way
   (bill → invoice). Has an "Open Invoice" button that pops the
   Invoices module in a new browser tab via the URL-hash deeplink
   (App.js handles the hash on mount). */
function InvoicePreviewModal({ invoice, lang, onClose }) {
  var t = lang === "es"
    ? { title: "Factura · Vista previa", puNum: "PU #", date: "Fecha", dueDate: "Fecha de Pago", client: "Cliente", clientRef: "Ref Cliente (PO)", lot: "Lote", shipper: "Proveedor", product: "Producto", description: "Descripción", category: "Cat", cases: "Cant", price: "Precio Venta", lineTotal: "Total", saleStatus: "Status Venta", paymentStatus: "Estado de Pago", noLines: "Sin líneas de producto", openBtn: "Abrir Factura", closeBtn: "Cerrar", statusDraft: "Borrador", statusSent: "Enviada", statusPaid: "Pagada", statusOverdue: "Vencida", gross: "Ventas Brutas", commission: "Comisión", fees: "Fees & Deducciones", credits: "Créditos", subtotal: "Subtotal", paid: "Pagado", balance: "Saldo" }
    : { title: "Invoice · Preview", puNum: "PU #", date: "Date", dueDate: "Due Date", client: "Client", clientRef: "Customer Ref (PO)", lot: "Lot", shipper: "Shipper", product: "Product", description: "Description", category: "Cat", cases: "Qty", price: "Sale Price", lineTotal: "Total", saleStatus: "Sale Status", paymentStatus: "Payment Status", noLines: "No product lines", openBtn: "Open Invoice", closeBtn: "Close", statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid", statusOverdue: "Overdue", gross: "Gross Sales", commission: "Commission", fees: "Fees & Deductions", credits: "Credits", subtotal: "Subtotal", paid: "Paid", balance: "Balance" };

  /* Reproduce InvoiceRegistry's calcInvoiceTotal locally — read-only
     and avoids the cross-module import. Same per-line commission
     contract with bill-level fallback. */
  var invDefaultRate = parseFloat(invoice.commissionRate) || 0;
  var gross = 0, commission = 0;
  (invoice.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? invDefaultRate : (parseFloat(l.commissionRate) || 0);
    gross += amt;
    commission += amt * (rate / 100);
  });
  var totalFees = 0; (invoice.fees || []).forEach(function (fe) { totalFees += Math.abs(parseFloat(fe.amount) || 0); });
  var totalCredits = 0; (invoice.credits || []).forEach(function (cr) { totalCredits += Math.abs(parseFloat(cr.amount) || 0); });
  var paid = (invoice.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var subtotal = gross - commission - totalFees + totalCredits;
  var balance = subtotal - paid;
  var paymentStatus = invoice.status || "draft";
  if (paymentStatus !== "draft" && paid > 0 && balance <= 0.01) paymentStatus = "paid";

  function openInNewTab() {
    /* Deeplink: opens index.html in a new tab with #open=invoice:ID;
       App.js parses on mount and calls window.__gfiInvoiceOpen(id). */
    var url = window.location.origin + window.location.pathname + window.location.search + "#open=invoice:" + encodeURIComponent(invoice.id);
    window.open(url, "_blank");
  }

  /* Resolve client name from gfi-clients when invoice.clientName
     isn't set. */
  var clientName = invoice.clientName;
  if (!clientName && invoice.clientId) {
    try {
      var clients = JSON.parse(localStorage.getItem("gfi-clients") || "[]");
      var c = clients.find(function (x) { return String(x.id) === String(invoice.clientId); });
      if (c) clientName = c.shortName || c.fullName || c.name || c.companyName || "";
    } catch (e) {}
  }

  var th = { padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid var(--color-border)" };
  var statusBadge = function (st) {
    var styles = {
      draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)", label: t.statusDraft },
      sent: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)", label: t.statusSent },
      overdue: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.25)", label: t.statusOverdue },
      paid: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)", label: t.statusPaid },
    };
    var s = styles[st] || styles.draft;
    return <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: s.bg, color: s.text, border: "1px solid " + s.border, boxShadow: SALE_STATUS_BEVEL }}>{s.label}</span>;
  };

  return (
    <DraggableModal title={t.title + " · PU# " + (invoice.puNumber || "—")} onClose={onClose} initialWidth={Math.min(1000, Math.floor(window.innerWidth * 0.8))} initialHeight={Math.min(720, window.innerHeight - 80)}>
      <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "#c65d00" }}>PU# {invoice.puNumber || "—"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{clientName || "—"} · LOT {invoice.lot || "—"}</div>
          <div style={{ marginTop: 8 }}>{statusBadge(paymentStatus)}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={openInNewTab} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #c65d00", background: "#c65d00", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>{"↗"} {t.openBtn}</button>
          <button onClick={onClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.closeBtn}</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px", fontSize: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px 18px", marginBottom: 18 }}>
          {[
            [t.puNum, invoice.puNumber, true],
            [t.date, invoice.date ? new Date(invoice.date + "T00:00:00").toLocaleDateString("en-US") : "—"],
            [t.dueDate, invoice.dueDate ? new Date(invoice.dueDate + "T00:00:00").toLocaleDateString("en-US") : "—"],
            [t.lot, invoice.lot, true],
            [t.client, clientName],
            [t.clientRef, invoice.clientRef, true],
            [t.shipper, invoice.shipperName],
            [t.paymentStatus, t["status" + paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1)] || paymentStatus],
          ].map(function (arr, i) {
            return (
              <div key={i}>
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{arr[0]}</div>
                <div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", marginTop: 2 }}>{arr[1] || "—"}</div>
              </div>
            );
          })}
        </div>

        {(invoice.lines || []).length === 0 ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", fontSize: 12 }}>{t.noLines}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
            <thead><tr>
              <th style={th}>{t.product}</th>
              <th style={th}>{t.description}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.category}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.cases}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.price}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.lineTotal}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
            </tr></thead>
            <tbody>
              {(invoice.lines || []).map(function (l, i) {
                var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
                var sc = SALE_STATUS_COLORS[l.saleStatus] || null;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "7px 8px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}</td>
                    <td style={{ padding: "7px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productDescription || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.category ? <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: l.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{l.category}</span> : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.salePrice ? fmtMoney(l.salePrice) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{amt > 0 ? fmtMoney(amt) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.saleStatus && sc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL }}>{l.saleStatus}</span> : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 10, padding: "14px 18px", background: "var(--color-bg)", borderRadius: 10, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, fontSize: 12 }}>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.gross}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(gross)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.commission}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(commission)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.fees}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(totalFees)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.credits}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(totalCredits)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.subtotal}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(subtotal)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.paid}</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(paid)}</div></div>
          <div style={{ gridColumn: "span 2" }}><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>{t.balance}</div><div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#c65d00" }}>{fmtMoney(balance)}</div></div>
        </div>
      </div>
    </DraggableModal>
  );
}

/* Shared collapsible section box — one bordered rectangle, a tinted
   clickable header (chevron rotates open/closed) and its content.
   Used by both DetailPanel's view mode AND BillForm's edit mode (via
   the `open`/`toggle` props, lifted to DetailPanel and passed down to
   BillForm so the SAME sections, in the SAME open/closed state,
   survive the view↔edit toggle — 2026-10-16, Arturo: "todas las
   secciones son desplegables... al entrar a editar se mantenga las
   mismas secciones segmentadas... todo igual en modo editar".
   `headerExtra` is an optional node rendered at the right end of the
   header row (e.g. Linked Invoices' "+ Link Invoice" button) — the
   header itself is a <div role="button"> rather than a real <button>
   so headerExtra can safely contain its own interactive buttons
   (invalid HTML otherwise); headerExtra's own onClick must call
   stopPropagation so clicking it doesn't also toggle the section. */
function SectionBox({ id, title, open, toggle, headerExtra, children, style }) {
  var isOpen = !!(open && open[id]);
  return (
    <div style={{ marginBottom: 18, ...(style && style.outerMargin ? style.outerMargin : {}) }}>
      <div
        role="button"
        tabIndex={0}
        onClick={function () { toggle(id); }}
        onKeyDown={function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(id); } }}
        style={{ ...sectionHeaderStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      >
        <span style={{ fontSize: 10, color: "#000000", textShadow: sectionHeaderStyle.textShadow, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"▶"}</span>
        <span style={{ flex: 1 }}>{title}</span>
        {headerExtra}
      </div>
      {isOpen && <div style={{ padding: 14, background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, ...(style || {}) }}>{children}</div>}
    </div>
  );
}

/* LOT ACTIVITY — title-bar button + popover (2026-10-16, Arturo: "no
   me gusta que lot activity esta arriba, me gustaria entrar a traves
   de otro menu que estuviera en el title bar y desde ahi abrir esa
   seccion con un click... y si hay rechazos o respuestas un marcador
   de que ese lote tiene rechazos o respuestas pero muy pequeño...
   lot activity encierralo en un rectangulo y colocalo en el title
   bar"). Replaces the full-width box that used to sit at the top of
   the body — now just a small button next to Sales Report/Create
   Invoice/Edit, with a red dot badge when the lot actually has
   rejections and/or protection responses (rejectedCasesForLot/
   hasProtectionResponses — the same cheap checks the badge elsewhere
   in the app already uses, so this never has to render the full
   lists just to know whether there's anything to show). Portaled to
   document.body (position:fixed from the button's own rect) rather
   than a plain CSS dropdown, since .dm-window has overflow:hidden —
   a simple absolute-positioned popover would get clipped by the
   modal's own edge. Still shows the existing *ReadOnly components
   verbatim — both remain read-only mirrors of data captured on the
   Invoice side; this button only changes WHERE you view them, not
   whether they're editable here. */
function LotActivityButton({ invoices, lot, lang, t, style }) {
  var [open, setOpen] = useState(false);
  var [pos, setPos] = useState(null);
  var btnRef = useRef(null);
  var hasActivity = rejectedCasesForLot(lot, invoices) > 0 || hasProtectionResponses(lot, invoices);

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-lot-activity-panel]")) return;
      setOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  var toggleOpen = function () {
    if (!open && btnRef.current) {
      var r = btnRef.current.getBoundingClientRect();
      var panelW = 360;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8)) });
    }
    setOpen(function (v) { return !v; });
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        title={t.lotActivity}
        style={{ position: "relative", padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: open ? "var(--color-accent)" : "#fff", color: open ? "#fff" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", ...(style || {}) }}
      >
        {"📋"} {t.lotActivity}
        {hasActivity && <span style={{ position: "absolute", top: -3, right: -3, width: 8, height: 8, borderRadius: "50%", background: "#DC2626", border: "1.5px solid #fff" }} />}
      </button>
      {open && pos && createPortal(
        <div data-lot-activity-panel style={{ position: "fixed", top: pos.top, left: pos.left, width: 360, maxHeight: "70vh", overflowY: "auto", background: "#fff", borderRadius: 10, border: "1px solid var(--color-border)", boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 10060, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--color-border)" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)" }}>{t.lotActivity}</span>
            <button type="button" onClick={function () { setOpen(false); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: 14, lineHeight: 1 }}>{"×"}</button>
          </div>
          {hasActivity ? (
            <>
              <RejectionDetailsReadOnly invoices={invoices} lot={lot} lang={lang} />
              <ProtectionResponsesReadOnly invoices={invoices} lot={lot} lang={lang} />
            </>
          ) : (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", padding: "6px 0" }}>{lang === "es" ? "Sin actividad registrada para este lote." : "No activity recorded for this lot."}</div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

/* SUPPORTING DOCUMENTS (price adjustments) — title-bar button + popover,
   same portaled-popover scaffold as LotActivityButton above. 2026-10-21,
   Arturo: "necesito agregarle documentos de soporte para cuando haya
   ajustes en el precio y que esos documentos se anexen al reporte pdf
   dentro del mismo no como archivo separado... la primer hoja y las
   siguientes sean de documentos anexos de evidencias, pueden ser
   varios archivos o un pdf con mas de una hoja".

   Files live ONLY in IndexedDB (gfiDB.js), one array of raw File/Blob
   objects per bill, keyed "bill-adjustment-docs-{billId}" — same
   precedent as InvoiceRegistry.js's protectionFiles (IndexedDB stores
   Blob/File natively via structured clone, no base64 encoding
   needed). SalesReports.js's buildSalesReportBlob reads this exact
   key and merges every file in as extra pages via pdfMerge.js, so
   whatever's attached here automatically rides along on every
   download/email/preview of this bill's Sales Report — no separate
   "attach for this send" step. Only a lightweight count is mirrored
   onto the bill record itself (extra.priceAdjustmentDocsCount), purely
   for the red-dot badge — the list itself is always read live from
   IndexedDB when the popover opens, never cached in bill state. */
/* 2026-10-21, Arturo: hit a real, unresolved Supabase Storage RLS wall
   uploading straight from the browser — "new row violates row-level
   security policy" even after confirming (via direct SQL with
   `set local role authenticated`) that the policies themselves were
   correct. Routed through this Netlify Function instead
   (netlify/functions/bill-supporting-doc-upload.js), which uses the
   service_role key server-side — bypasses RLS entirely by design,
   sidestepping the mystery rather than depending on it being solved. */
var BILL_SUPPORTING_DOC_UPLOAD_FN = "/.netlify/functions/bill-supporting-doc-upload";

function fileToBase64(file) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function () {
      var result = String(reader.result || "");
      var comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = function () { reject(reader.error || new Error("FileReader failed")); };
    reader.readAsDataURL(file);
  });
}

function SupportingDocsButton({ bill, lang, t, style, onSave }) {
  var [open, setOpen] = useState(false);
  var [pos, setPos] = useState(null);
  var [busy, setBusy] = useState(false);
  var btnRef = useRef(null);
  /* 2026-10-21, Arturo: this used to cache files in the browser's local
     IndexedDB — "no vuelvas a hacer eso... nunca se debe almacenar nada
     en modo local". Moved to Supabase Storage (bucket
     "bill-supporting-docs", see migrations/2026-10-21_bill_supporting_docs_bucket.sql):
     only lightweight metadata ({name,type,path,url,addedAt}) lives on
     the bill record itself (extra.priceAdjustmentDocs), the same way
     every other field on this record already syncs through Supabase —
     so the list is read directly off the `bill` prop, no separate
     load/loading-state/local-cache of any kind, and it's identical no
     matter which device/browser opens this Bill. */
  var docs = (bill.extra && Array.isArray(bill.extra.priceAdjustmentDocs)) ? bill.extra.priceAdjustmentDocs : [];

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-supporting-docs-panel]")) return;
      setOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  var toggleOpen = function () {
    if (!open && btnRef.current) {
      var r = btnRef.current.getBoundingClientRect();
      var panelW = 380;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8)) });
    }
    setOpen(function (v) { return !v; });
  };

  var handleAddFiles = async function (fileList) {
    var accepted = [];
    var rejected = [];
    Array.from(fileList || []).forEach(function (f) {
      var ext = (f.name.split(".").pop() || "").toLowerCase();
      if (ALLOWED_SUPPORTING_DOC_EXT.indexOf(ext) >= 0) accepted.push(f); else rejected.push(f.name);
    });
    if (rejected.length > 0) {
      alert((lang === "es" ? "Solo PDF, JPG o PNG — tipo no permitido: " : "PDF, JPG or PNG only — type not allowed: ") + rejected.join(", "));
    }
    if (accepted.length === 0) return;
    setBusy(true);
    var uploaded = [];
    var failed = [];
    for (var i = 0; i < accepted.length; i++) {
      var f = accepted[i];
      try {
        var fileBase64 = await fileToBase64(f);
        var resp = await fetch(BILL_SUPPORTING_DOC_UPLOAD_FN, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "upload", billId: String(bill.id), fileName: f.name, fileType: f.type || "application/octet-stream", fileBase64: fileBase64 }),
        });
        var json = await resp.json().catch(function () { return {}; });
        if (!resp.ok || !json.success) throw new Error(json.error || ("HTTP " + resp.status));
        uploaded.push({ name: f.name, type: f.type || "application/octet-stream", path: json.path, url: json.url, addedAt: new Date().toISOString() });
      } catch (e) {
        console.error("[bill-supporting-docs] upload failed", f.name, e);
        failed.push(f.name);
      }
    }
    setBusy(false);
    if (uploaded.length > 0) {
      onSave({ ...bill, extra: { ...(bill.extra || {}), priceAdjustmentDocs: docs.concat(uploaded) } });
    }
    if (failed.length > 0) {
      alert((lang === "es" ? "No se pudo subir: " : "Could not upload: ") + failed.join(", "));
    }
  };

  var removeFile = async function (idx) {
    var target = docs[idx];
    if (!target) return;
    setBusy(true);
    try {
      await fetch(BILL_SUPPORTING_DOC_UPLOAD_FN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete", path: target.path }),
      });
    } catch (e) {
      console.error("[bill-supporting-docs] delete failed", target.path, e);
    }
    setBusy(false);
    var next = docs.filter(function (_, i) { return i !== idx; });
    onSave({ ...bill, extra: { ...(bill.extra || {}), priceAdjustmentDocs: next } });
  };

  var count = docs.length;

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        title={lang === "es" ? "Documentos de Soporte (Ajustes de Precio)" : "Supporting Documents (Price Adjustments)"}
        style={{ position: "relative", padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: open ? "var(--color-accent)" : "#fff", color: open ? "#fff" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", ...(style || {}) }}
      >
        {"📎"} {lang === "es" ? "Soporte" : "Support"}{count > 0 ? " (" + count + ")" : ""}
      </button>
      {open && pos && createPortal(
        <div data-supporting-docs-panel style={{ position: "fixed", top: pos.top, left: pos.left, width: 380, maxHeight: "70vh", overflowY: "auto", background: "linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", borderRadius: 0, border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 12px 32px rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.15)", zIndex: 10060, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #9aa0a8" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Documentos de Soporte" : "Supporting Documents"}</span>
            <button type="button" onClick={function () { setOpen(false); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#1a2636", fontSize: 14, lineHeight: 1, fontWeight: 700 }}>{"×"}</button>
          </div>
          <div style={{ fontSize: 10.5, color: "var(--color-text-secondary)", marginBottom: 8, lineHeight: 1.4 }}>
            {lang === "es"
              ? "Evidencia de ajustes de precio (ej. reporte de calidad del cliente). Se anexa automáticamente como páginas adicionales al final del Sales Report — nunca como adjunto separado. Se guarda en línea, disponible desde cualquier dispositivo."
              : "Price-adjustment evidence (e.g. the client's quality report). Automatically appended as extra pages at the end of the Sales Report — never as a separate attachment. Stored online, available from any device."}
          </div>
          <FileDropZone lang={lang} multiple={true} onFiles={handleAddFiles} disabled={busy} style={{ width: "100%", marginBottom: 8 }}>
            <label style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "#fff", color: busy ? "var(--color-text-muted)" : "var(--color-accent)", cursor: busy ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 600 }}>
              {busy ? (lang === "es" ? "Subiendo…" : "Uploading…") : ("+ " + (lang === "es" ? "Subir PDF/JPG/PNG" : "Upload PDF/JPG/PNG"))}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple disabled={busy} style={{ display: "none" }} onChange={function (e) { handleAddFiles(e.target.files); e.target.value = ""; }} />
            </label>
          </FileDropZone>
          {docs.length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin documentos de soporte." : "No supporting documents."}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {docs.map(function (file, idx) {
                return (
                  <div key={file.path || idx} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: "rgba(255,255,255,0.6)", border: "1px solid #c7cbd1", fontSize: 11 }}>
                    <span>{"📄"}</span>
                    <a href={file.url} target="_blank" rel="noreferrer" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1a2636" }} title={file.name}>{file.name}</a>
                    <button type="button" disabled={busy} onClick={function () { removeFile(idx); }} style={{ background: "transparent", border: "none", color: "#DC2626", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, padding: "0 2px" }}>{"✕"}</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

/* Unified view+edit modal. Click on row opens this in mode="view"; "Edit" button
   toggles to mode="edit" inside the SAME DraggableModal. Save returns to view
   mode without closing. Per CLAUDE.md "REGLA DE VENTANAS". */
function DetailPanel({ bill, bills, t, lang, onSave, onDelete, onEditLock, onClose, onCreateInvoice, initialMode }) {
  var [mode, setMode] = useState(initialMode || "view");
  var [showSalesReportPreview, setShowSalesReportPreview] = useState(false);
  /* 2026-11-02, Arturo: "tambien el bill y las facturas debe cambiarse
     el status desde el modo no edicion" — same clickable status pill
     + dropdown already shipped on Shipment's view-mode DetailPanel
     (ShipmentRegistry.js), ported here so draft/finalized/paid can be
     flipped without opening the full edit form (BillForm). */
  var [statusMenuOpen, setStatusMenuOpen] = useState(false);
  var fresh = bill;
  useDocumentTitle(fresh.lot ? ("Bill LOT " + fresh.lot + (function () { var n = resolveShipperName(fresh.supplierId) || fresh.shipperName; return n ? " | " + n : ""; })()) : null);
  var totals = calcBillTotals(fresh);
  /* Fee name → category (customs/warehouse/qc/shipping/financing/
     certification/other), resolved from the Fee Catalog so the
     Summary box can break "Total Fees" out per category — see
     calcBillFeesByCategory/FeeBreakdownLines above. */
  var [feeCategoryMap, setFeeCategoryMap] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("fee_catalog").then(function (rows) {
      if (cancelled) return;
      var map = {};
      (rows || []).forEach(function (r) { if (r && r.name) map[String(r.name).trim().toLowerCase()] = r.category || "other"; });
      setFeeCategoryMap(map);
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* For the read-only Pallet Detail section below (Pending Pick Up /
     Pending Delivery default, same as BillForm's own slPassingIndex —
     this view previously never showed Pallet Detail at all, so it
     never needed this). */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* 2026-10-16, Arturo: "todas las secciones son desplegables... por
     default... shipment info, product, fees and deductions
     solamente [abiertas], lo demas contraido" — one shared open/
     toggle object (same {id: bool} + toggle(id) shape ShipmentRegistry
     .js's own DetailPanel already uses for its sections), passed down
     to BillForm too so "al entrar a editar se mantenga las mismas
     secciones segmentadas... los desplegables deben estar, todo
     igual en modo editar" — same boxes, same open/closed state,
     carried across the view↔edit toggle instead of resetting. */
  /* 2026-10-20, Arturo: "todas las tarjetas deben estar abiertas al
     entrar al bill" — every section starts expanded in view mode
     (was a mix of true/false before this ask). */
  var [open, setOpen] = useState({ shipmentInfo: true, product: true, pallets: true, fees: true, billSummary: true, linked: true, payments: true, profit: true });
  var toggle = function (k) { setOpen(function (p) { return { ...p, [k]: !p[k] }; }); };
  var st = fresh.status || "draft";
  var ss = BILL_STATUS_STYLES[st] || BILL_STATUS_STYLES.draft;
  var statusKey = st === "draft" ? "statusDraft" : st === "finalized" ? "statusFinalized" : "statusPaid";

  /* Linked invoices — every invoice whose lot matches this bill's
     lot. Recomputed from gfi-invoices on every open of the detail
     panel so any newly created invoice shows up immediately. Not
     stored on the bill record (per spec).
     2026-10-20, Arturo: "quita linked invoices de ahi, ya esta
     arriba" — the standalone "Linked Invoices" section (mini-table +
     manual "+ Link Invoice" picker) was removed from the body since
     the top strip's "Links:" chips already show every linked invoice;
     linkedInvoices itself stays (still feeds those chips and the
     Product table's live Sale Status lookup below) — only the
     picker-specific state (linkVer/linkPickerOpen/unlinkedInvoices/
     linkInvoiceToBill) and the mini-table's own preview-modal state
     (previewInvoice/setPreviewInvoice) went with it, since nothing
     else in view mode triggers them anymore. */
  var linkedInvoices = useMemo(function () {
    if (!fresh.lot) return [];
    var invs = freshInvoicesLS();
    return findInvoicesByLot(fresh.lot, invs);
    /* mode triggers a fresh read after the user comes back from
       edit mode (where they may have changed the lot). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh.id, fresh.lot, mode]);

  /* 2026-10-20 — Arturo: "replicar la informacion received by, aqui no
     se edita solo se despliega, lo copia del embarque o de donde
     proviene" — read-only, resolved from the linked Shipment (same
     lot), same pattern already used elsewhere in this file for
     texasQcMode/receivedDate lookups (e.g. shpForLotDetail above). */
  var linkedShipmentForBar = useMemo(function () {
    if (!fresh.lot) return null;
    return getShipments().find(function (s) { return String(s.lot) === String(fresh.lot); }) || null;
  }, [fresh.lot]);

  /* Edit button on the Shipment Info plate sends the user to the
     Shipment itself to edit it there (never duplicated in the Bill),
     and stashes this bill's id so returning lands back on this same
     Bill (consumed by the window.__gfiBillReopenId effect above). */
  var editLinkedShipment = function () {
    if (!linkedShipmentForBar) return;
    window.__gfiBillReopenId = fresh.id;
    var shipmentId = linkedShipmentForBar.id;
    if (window.__gfiNavigate) window.__gfiNavigate("shipments");
    /* ShipmentRegistry's __gfiShipmentOpen looks up the target in its
       own already-loaded shipments list — on a cold mount of a 170+
       row Supabase fetch, 220ms (the usual cross-module convention)
       isn't always enough, and a lookup that misses fails silently.
       Fired twice (quick first try + a slower backup) rather than a
       single longer delay, so it still feels instant once cached.
       __gfiShipmentOpen reads+clears window.__gfiReturnModule on
       every call (even a failed lookup), so it has to be re-armed
       before each attempt or a successful 2nd try would open the
       shipment with no return path back to this Bill. */
    setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 900);
  };

  /* "me voy a dormir" — Arturo's deferred request to make client/
     supplier names clickable app-wide. Bill already carries supplierId
     directly (unlike Invoice), so no shipment lookup needed. Same
     open-and-return convention as editLinkedShipment above. */
  var openLinkedSupplierFromBar = function () {
    if (!fresh.supplierId) return;
    var supplierId = fresh.supplierId;
    window.__gfiBillReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("suppliers");
    setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 900);
  };

  var tryEditMode = function() {
    /* Always enter edit mode — even with payments applied. Balance
       recalculates automatically from the updated subtotal minus
       existing payments (see PaymentsSection). onEditLock left wired
       for future use but no longer triggered. */
    setMode("edit");
  };
  var handleSaveInline = function(updated) { onSave(updated); setMode("view"); };
  var handleDeleteInline = function(id) { onDelete(id); onClose(); };

  /* 2026-10-20, Arturo: "agrega el balance del bill en el titulo ...
     antes del cuadro para cerrar la ventana" \u2014 titleBarActions renders
     right-aligned in the title bar, right before the maximize/close
     window controls, exactly where he pointed. Same balance figure
     already shown as "Amount Due" in Fees & Deductions (totals
     .subtotal minus amountPaid) \u2014 view mode only, same embossed
     title-bar text treatment. */
  /* 2026-10-20, Arturo: "esto viaja a su bill y su shipment" \u2014 same
     overall delivery-status + rejections badges InvoiceRegistry.js's
     title bar now shows, mirrored here from the Bill's own live
     per-line status (deriveBillLineLiveStatus, same source the
     Products table's Sale Status column already uses) and the same
     rejectedCasesForLot() this screen's LotActivityButton already
     gates its red dot on. */
  var billOverallStatus = (function () {
    var invoiceLinesForLotBar = [];
    (linkedInvoices || []).forEach(function (inv) { (inv.lines || []).forEach(function (il) { invoiceLinesForLotBar.push(il); }); });
    var shpForBar = getShipments().find(function (s) { return String(s.lot) === String(fresh.lot); });
    var trLotStatusBar = shpForBar && shpForBar.texasQcMode === "clarifresh" ? twinRiverLotStatus(shpForBar) : null;
    var statuses = (fresh.lines || []).map(function (l) {
      return trLotStatusBar || deriveBillLineLiveStatus(l, invoiceLinesForLotBar);
    }).filter(Boolean);
    return statuses.length > 0 ? deriveDeliveryStatus(statuses.map(function (s) { return { saleStatus: s }; })) : null;
  })();
  var billHasRejections = rejectedCasesForLot(fresh.lot, freshInvoicesLS()) > 0;

  return (
    <DraggableModal
      title={(lang === "es" ? "Factura Lote " : "Bill ") + (fresh.lot || "\u2014") + " \u00b7 " + (resolveShipperName(fresh.supplierId) || fresh.shipperName || "\u2014") + (mode === "edit" ? " \u00b7 " + t.edit : "")}
      titleBarVariant="groundfresh"
      titleLeftActions={mode === "view" ? (
        <>
          {billOverallStatus && SALE_STATUS_COLORS[billOverallStatus] && (
            <span style={metallicBadgeStyle(statusColorKeyFor(billOverallStatus))}>{t[billOverallStatus] || billOverallStatus}</span>
          )}
          {billHasRejections && (
            <span style={metallicBadgeStyle("red")}><span style={{ color: "#FB923C" }}>{"\u26a0"}</span>{" " + (lang === "es" ? "Rechazos" : "Has Rejections")}</span>
          )}
        </>
      ) : null}
      titleBarActions={mode === "view" ? (
        <span style={{ marginLeft: "auto", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#059669", textShadow: "0 1px 0 rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>
          {(lang === "es" ? "Saldo del Bill: " : "Bill Balance: ") + fmtMoney(totals.subtotal - (parseFloat(fresh.amountPaid) || 0))}
        </span>
      ) : null}
      onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}
    >
      {mode === "edit" ? (
        <BillForm bill={fresh} bills={bills} t={t} lang={lang} feeCategoryMap={feeCategoryMap} onSave={handleSaveInline} onDelete={handleDeleteInline} onCancel={function() { setMode("view"); }} embedded={true} sectionsOpen={open} toggleSection={toggle} />
      ) : (
        <>
        {/* 2026-10-20, Arturo: "hay que replicar esta barra arriba ahi
            poner los linked invoices y replicar la informacion
            received by, aqui no se edita solo se despliega, lo copia
            del embarque o de donde proviene" — same brushed-metal
            strip + "Received by"/"Links:" row already shipped on the
            Shipment modal (ShipmentRegistry.js's DetailPanel top
            strip), here entirely read-only on the left (no dropdown —
            the Bill never owns this field, it's mirrored from the
            linked Shipment) and only linking to Invoices (the Bill IS
            the linked Bill, so no "Open Bill Linked" chip for itself).
            Follow-up: "la barra superior el color no es el correcto...
            la linea de paid, lot activity etc debe estar en la bande
            inferior donde vive received by" — titleBarVariant=
            "groundfresh" above now matches the Shipment modal's title
            bar color exactly, and the Paid/Lot Activity/Sales Report/
            Create Invoice/Edit buttons (previously in titleBarActions)
            move down into this strip's right side, same split and
            plate-button recipe as Shipment's own Preview/Print PDF/
            ⋮/Status/Edit row. */}
        {linkedShipmentForBar && (
          <div style={{ padding: "10px 22px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 11 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>
                {linkedShipmentForBar.texasQcMode === "clarifresh" ? (lang === "es" ? "Recepción en Twin River" : "Received by Twin River") : (lang === "es" ? "Recepción en GFI" : "Received by GFI")}
              </span>
              <span style={{ fontWeight: 600, textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>{linkedShipmentForBar.receivedDate ? fmtDate(linkedShipmentForBar.receivedDate) + (linkedShipmentForBar.receivedTime ? " " + linkedShipmentForBar.receivedTime : "") : "—"}</span>
              {linkedInvoices.length > 0 && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
                  {linkedInvoices.map(function (inv) {
                    return (
                      <button key={"inv-" + inv.id} type="button" style={{ padding: "3px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" }} onClick={function () {
                        if (window.__gfiNavigate) window.__gfiNavigate("invoices");
                        setTimeout(function () { if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(inv.id); }, 220);
                      }}>{(lang === "es" ? "Abrir Invoice Vinculado PU " : "Open Invoice Linked PU ") + (inv.puNumber || inv.id)}</button>
                    );
                  })}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {/* 2026-11-02, Arturo: "tambien el bill y las facturas
                  debe cambiarse el status desde el modo no edicion" —
                  same clickable pill + dropdown as Shipment's view-mode
                  status control (ShipmentRegistry.js DetailPanel):
                  same transitions BillForm's own edit-only buttons
                  already offer (draft→finalized, finalized→draft/paid,
                  paid→finalized), saved immediately via onSave, no
                  need to open Edit just to flip status. */}
              {(function () {
                var options = [];
                if (st === "draft") options = ["finalized"];
                else if (st === "finalized") options = ["draft", "paid"];
                else if (st === "paid") options = ["finalized"];
                var changeStatus = function (next) {
                  setStatusMenuOpen(false);
                  if (next === st) return;
                  onSave({ ...fresh, status: next });
                };
                return (
                  <span style={{ position: "relative", display: "inline-block" }} onClick={function (e) { e.stopPropagation(); }}>
                    <button type="button" onClick={function () { setStatusMenuOpen(!statusMenuOpen); }} title={lang === "es" ? "Cambiar estado" : "Change status"} style={{ padding: "6px 12px", borderRadius: 0, fontSize: 11, fontWeight: 700, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12)", whiteSpace: "nowrap", cursor: options.length > 0 ? "pointer" : "default", fontFamily: "inherit" }}>
                      {t[statusKey]}{options.length > 0 && <span style={{ marginLeft: 4, opacity: 0.6 }}>{"▾"}</span>}
                    </button>
                    {statusMenuOpen && options.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 140, overflow: "hidden" }}>
                        {options.map(function (opt) {
                          var optSty = BILL_STATUS_STYLES[opt] || BILL_STATUS_STYLES.draft;
                          var optKey = opt === "draft" ? "statusDraft" : opt === "finalized" ? "statusFinalized" : "statusPaid";
                          return (
                            <div key={opt} onClick={function () { changeStatus(opt); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: optSty.text, textShadow: "0 1px 0 rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #7d838c" }}
                                 onMouseEnter={function (e) { e.currentTarget.style.background = optSty.bg; }}
                                 onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                              <span style={{ width: 8, height: 8, borderRadius: 4, background: optSty.text, flexShrink: 0 }} />
                              <span>{"→ "}{t[optKey]}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </span>
                );
              })()}
              <LotActivityButton invoices={freshInvoicesLS()} lot={fresh.lot} lang={lang} t={t} style={plateActionBtnStyle} />
              <SupportingDocsButton bill={fresh} lang={lang} t={t} style={plateActionBtnStyle} onSave={onSave} />
              <button type="button" onClick={function () { setShowSalesReportPreview(true); }} title="Sales Report" style={{ ...plateActionBtnStyle, display: "inline-flex", alignItems: "center", gap: 5 }}>{"🖨"} Sales Report</button>
              <button type="button" onClick={function() { onCreateInvoice(fresh); }} style={{ ...plateActionBtnStyle, display: "inline-flex", alignItems: "center", gap: 4 }}>{"→"} {t.createInvoice}</button>
              <button type="button" onClick={tryEditMode} style={plateActionBtnStyle}>{t.editBtn}</button>
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          {/* 2026-10-20, Arturo: "algo no me gusta con esto de ser
              dinamica, porque las tarjetas se cambian de posicion
              solas... no puedo trabajar, me confunde" + "el
              autoajuste que sea solo de lo alto de las tarjetas no
              de lo ancho" - CSS multi-column auto-balanced by
              height, which meant a card could land in either
              column depending on THAT bills content (confusing,
              inconsistent across bills). Back to two FIXED
              columns (same assignment on every bill: left =
              Shipment Info/Fees/Bill Summary, right = Product/
              Pallet Manifest+Payments/My Profit) - each column is
              its own independent flex stack (gap, not a row
              pairing), so height still auto-adjusts WITHIN a
              column (a short card is immediately followed by the
              next one, no waiting on the other columns row
              partner) without ever moving a card to the other
              side. */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ flex: "0 1 38%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
          {/* 2026-10-20, Arturo: "shipmnt info debe verse como una placa
              sin colapsable, siempre visible... con un boton de editar
              y para no meter mucho codigo, al dar click para editar,
              me manda el embarque para editarlo ahi, al guardar me
              regresa el bill abierto" — unlike every other section
              here, this one is NOT a SectionBox: no collapse toggle,
              always rendered open, and its own header carries an Edit
              button instead of the usual collapse chevron. Editing
              happens on the Shipment itself (editLinkedShipment, see
              above) — never duplicated here. */}
          <div style={{ marginBottom: 18 }}>
            {/* 2026-10-20, Arturo: "esa linea no, el formato de la parte
                superior va en toda la placa... tipo 3d metalica" — the
                header and body used to be 2 stacked boxes, each with
                its own border + bevel boxShadow; stacking a header's
                bottom inset shadow against the body's top inset
                highlight drew a visible seam line right under "Edit".
                Now ONE continuous plate: the texture/gradient/border/
                bevel live once on this outer wrapper, the header row
                and body below are just padding on top of it — same
                single metal surface top to bottom, no internal line. */}
            <div style={{ ...readOnlyPlateBodyStyle }}>
              <div style={{ ...sectionHeaderStyle, background: "none", border: "none", boxShadow: "none", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1 }}>{t.shipmentInfo}</span>
                {linkedShipmentForBar && (
                  <button type="button" onClick={editLinkedShipment} title={lang === "es" ? "Editar en el Embarque" : "Edit in the Shipment"} style={{ padding: "2px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{t.editBtn}</button>
                )}
              </div>
              <div style={{ padding: "0 14px 14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 18px", fontSize: 12 }}>
                {/* 2026-10-20, Arturo: "agrega export certificate que se
                    ve en modo edicion" — same derivation BillForm's own
                    edit-mode field uses (certDisplay, line ~2253),
                    recomputed here from fresh.lines since view mode's
                    `fresh` is a different object than edit mode's `f`. */}
                {/* 2026-10-20, Arturo: "el numero de certificado en un
                    solo renglon" — exportCert is the one field long
                    enough to wrap in this narrow card; spans both grid
                    columns for the extra width and clips with an
                    ellipsis (full value still available via title=)
                    instead of wrapping to a 2nd line. */}
                {/* 2026-10-20, Arturo: "export certificate se emite
                    por commodity... no es por embarque, no debe
                    convivir en shipment info sino en products" —
                    moved to a compact per-line note inside the
                    Product table below (see that section). */}
                {[[t.billDate, fmtDate(fresh.billDate)], [t.dueDate, fmtDate(fresh.dueDate)]].map(function(arr, i) {
                  return <div key={i}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
                })}
                <div>
                  <span style={readOnlyPlateLabelStyle}>{t.shipper}</span>
                  {fresh.supplierId ? (
                    <div onClick={openLinkedSupplierFromBar} title={lang === "es" ? "Abrir proveedor" : "Open supplier"} style={{ ...readOnlyPlateValueStyle, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{resolveShipperName(fresh.supplierId) || fresh.shipperName || "—"}</div>
                  ) : (
                    <div style={readOnlyPlateValueStyle}>{fresh.shipperName || "—"}</div>
                  )}
                </div>
                {[[t.proforma, fresh.proforma, true], [t.shipperInvoice, fresh.shipperInvoice, true], [t.entryNumber, fresh.entryNumber, true]].map(function(arr, i) {
                  return <div key={i} style={arr[3] ? { gridColumn: "1 / -1" } : undefined}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div title={arr[3] ? (arr[1] || "") : undefined} style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", ...(arr[3] ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>{arr[1] || "—"}</div></div>;
                })}
              </div>
              </div>
            </div>
          </div>
          </div>
          <div>
          <SectionBox id="fees" title={t.fees} open={open} toggle={toggle}>
          {(fresh.fees || []).length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
              <tbody>{(fresh.fees || []).map(function(f, i) {
                return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "6px 6px", fontSize: 11, color: "var(--color-text)" }}>{f.feeType === "__custom__" ? f.feeCustom : f.feeType}</td>
                  <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text)" }}>{f.qty > 1 ? f.qty + " x " + fmtMoney(f.rate) : ""}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, color: "#DC2626" }}>({fmtMoney(f.amount)})</td>
                </tr>;
              })}</tbody>
            </table>
          )}
          </SectionBox>
          </div>
          <div>
          <SectionBox id="billSummary" title={lang === "es" ? "Resumen del Bill" : "Bill Summary"} open={open} toggle={toggle}>
            <div style={{ fontSize: 11, color: "var(--color-text)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.grossSales)}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>- {t.totalCommission} ({fresh.commissionRate || 5}%)</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(totals.commission)})</span></div>
              <FeeBreakdownLines bill={fresh} feeCategoryMap={feeCategoryMap} t={t} />
              {totals.totalCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.totalCredits)}</span></div>}
              {totals.totalTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: totals.totalTransfers >= 0 ? "var(--color-text)" : "#DC2626" }}><span>{totals.totalTransfers >= 0 ? "+" : "-"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{totals.totalTransfers >= 0 ? fmtMoney(totals.totalTransfers) : "(" + fmtMoney(-totals.totalTransfers) + ")"}</span></div>}
              <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.subtotal}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.subtotal)}</span></div>
              {parseFloat(fresh.amountPaid) > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, color: "#DC2626" }}><span>{t.lessPaid}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(fresh.amountPaid)})</span></div>}
              <div style={{ borderTop: "2px solid var(--color-accent)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.amountDue}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.subtotal - (parseFloat(fresh.amountPaid) || 0))}</span></div>
            </div>
          </SectionBox>
          </div>
          </div>

          <div style={{ flex: "1 1 62%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
          {/* 2026-10-20, Arturo: "tambien en la tarjeta product, que
              debes cambiar el header a PRODUCTS" — card title only
              (literal, not t.product — that same key is also the
              column header inside this table and inside Pallet
              Manifest, which stay singular). */}
          <SectionBox id="product" title={lang === "es" ? "Productos" : "Products"} open={open} toggle={toggle}>
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
            {/* 2026-10-20, Arturo: "ahora los fonts, aplica el estilo de
                embarques en modo no edicion, tamaño, color etc en todas
                las secciones, quita negritas tambien" — same recipe
                already established on ShipmentRegistry.js's own Product
                Lines table (2026-10-17 comment there): headers 9px/
                weight 600/text-muted/uppercase, data cells 11px/text-
                secondary/no bold/no monospace. Category + Sale Status
                stay their own colored pill — same carved-out exception
                Arturo gave there ("la burbuja de OG no cambia"). */}
            <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
              <th style={{ padding: "5px 6px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.product}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.category}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.cases}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Pallets" : "Pallets"}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Precio" : "Price"}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Total" : "Total"}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.lineComm}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.lineCommAmt}</th>
              <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.saleStatus}</th>
            </tr></thead>
            <tbody>{(function () {
              /* 2026-09-16 \u2014 Arturo: lot 58046's 2 POs got their real
                 status days apart, and this table kept showing the
                 stale one until the bill was re-saved. Recomputed live
                 against `linkedInvoices` (already derived above) via
                 deriveBillLineLiveStatus, same fix as the list-level
                 SALE STATUS column. */
              var invoiceLinesForLotDetail = [];
              (linkedInvoices || []).forEach(function (inv) { (inv.lines || []).forEach(function (il) { invoiceLinesForLotDetail.push(il); }); });
              /* Twin River (2026-10-15) — ClariFresh shipments report
                 Protection through the shipment itself, not invoice
                 lines (see saleStatusCalc.js twinRiverLotStatus).
                 2026-10-30, Arturo (lot 58074): "las protecciones se
                 definen por producto... no debe mezclar productos" —
                 a single lot-wide twinRiverLotStatus was being applied
                 to EVERY line here, blending zarzamora/arándano into
                 one blended status. Now per-product
                 (twinRiverProductStatus), same fix as InvoiceRegistry.js. */
              var shpForLotDetail = getShipments().find(function (s) { return String(s.lot) === String(fresh.lot); });
              return (fresh.lines || []).map(function(l, i) {
              var trLineStatusDetail = shpForLotDetail && shpForLotDetail.texasQcMode === "clarifresh" ? twinRiverProductStatus(shpForLotDetail, l.productBase || l.productName) : null;
              var liveStatus = trLineStatusDetail || deriveBillLineLiveStatus(l, invoiceLinesForLotDetail);
              var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
              var sc = SALE_STATUS_COLORS[liveStatus] || {};
              /* Same fallback as calcBillTotals \u2014 display the explicit
                 line rate when set, otherwise the bill-level default. */
              var displayRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
                ? (parseFloat(fresh.commissionRate) || 0)
                : (parseFloat(l.commissionRate) || 0);
              var lineComm = lineCommissionAmount(l, fresh.commissionRate);
              return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                <td style={{ padding: "2px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {/* Show the full presentation ("Blackberry 12/6 oz OG
                      GF Label") as the primary label. Fallback chain
                      drops to productName/productBase/product for
                      legacy lines that still lack a productDescription.
                      The catalog-lookup secondary line only renders
                      when we DIDN'T already show productDescription,
                      so we don't duplicate the same string. */}
                  <div>{l.productDescription || l.productName || l.productBase || l.product || "\u2014"}</div>
                  {!l.productDescription && resolveProductDesc(l) && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 1 }}>{resolveProductDesc(l)}</div>}
                  {/* 2026-10-20, Arturo: "export certificate se emite
                      por commodity... no debe convivir en shipment
                      info sino en products... al ser un dato muy
                      ancho no puedo agregar una columna al final" \u2014
                      compact per-line note instead of a new column. */}
                  {(l.exportCertNumber || l.exportCertManual) && <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)", marginTop: 1 }}>{t.exportCert}: {l.exportCertNumber || l.exportCertManual}</div>}
                </td>
                <td style={{ padding: "2px 4px", textAlign: "center" }}>{l.category && <span style={{ padding: "2px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: l.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{l.category}</span>}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.cases}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{parseInt(l.pallets, 10) || "—"}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney(l.reportedPrice)}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney(lineAmt)}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{displayRate ? displayRate + "%" : "\u2014"}</td>
                <td style={{ padding: "2px 4px", textAlign: "right", fontSize: 11, color: "#DC2626" }}>{lineComm > 0 ? "(" + fmtMoney(lineComm) + ")" : "\u2014"}</td>
                <td style={{ padding: "2px 4px", textAlign: "center" }}>{liveStatus && <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg || "var(--color-bg)", color: sc.text || "var(--color-text-muted)", boxShadow: SALE_STATUS_BEVEL }}>{t[liveStatus] || liveStatus}</span>}</td>
              </tr>;
              });
            })()}</tbody>
            {/* 2026-10-20, Arturo: "el unico total que necesito es para
                importe y comision" — Total (line Amount) and Comm $
                summed across every product line; Cases/Pallets/Price/
                Comm% stay per-line only, no sum (not asked for).
                Follow-up: "elimina negritas y todos los font color son
                [el mismo] gris, excepto numeros negativos" — same
                normal-weight/text-secondary treatment as the rest of
                this table's cells now; Comm $ keeps red since it's a
                deduction (the explicit exception Arturo gave). */}
            <tfoot>
              <tr style={{ background: "var(--color-bg)", borderTop: "2px solid var(--color-accent)" }}>
                <td colSpan={5} style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Total" : "Total"}</td>
                <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney((fresh.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0); }, 0))}</td>
                <td></td>
                <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, color: "#DC2626" }}>{"(" + fmtMoney((fresh.lines || []).reduce(function (s, l) { return s + lineCommissionAmount(l, fresh.commissionRate); }, 0)) + ")"}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          </div>
          </SectionBox>
          </div>
          <div>
          {/* PALLET DETAIL — read-only mirror of the same section in
             BillForm (2026-10-16, Arturo: "el pallet detail en el bill
             solo se abre el edit mode, agregale para que se pueda
             desplegar sin tener que ponerlo en edit mode, solo
             lectura"). Same data/columns/live lookups as the editable
             version (Tag ID, lost-cases note, defects, lot Regression
             all still resolved LIVE from the shipment — see the
             comments on that block for why — never from a stale
             snapshot), just without the Cases input, the "Populate
             from shipment" control, or the column header's own edit
             affordances. Nothing here is editable; "Edit" (the button
             above) is still the only way to change anything. */}
          {(fresh.palletDetail || []).length > 0 && (function () {
            var shpForLiveLookup = getShipments().find(function (s) { return String(s.lot) === String(fresh.lot); });
            var palletsByNumFromShipment = {};
            ((shpForLiveLookup && shpForLiveLookup.palletManifest) || []).forEach(function (pm) {
              if (pm && pm.palletNum) palletsByNumFromShipment[String(pm.palletNum)] = pm;
            });
            var regressionPct = shpForLiveLookup && shpForLiveLookup.texasQcMode === "clarifresh" ? shpForLiveLookup.lotRegressionPct : undefined;
            var allInvoicesForPallet = freshInvoicesLS();
            return (
              <SectionBox id="pallets" title={t.palletDetail} open={open} toggle={toggle}>
              {(regressionPct !== undefined && regressionPct !== null && regressionPct !== "") && (
                <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", fontSize: 11, color: "#B45309" }}>
                  {t.lotRegressionLabel + " " + regressionPct + "%"}
                </div>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", marginBottom: 18 }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "5%" }}>{t.palletNumCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "7%" }}>{t.tagIdCol}</th>
                  <th title={t.actualShipperInitialsHint} style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "6%" }}>{t.actualShipperInitialsCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "24%" }}>{t.product}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "12%" }}>{t.varietyCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "10%" }}>{t.cases}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "10%" }}>{t.weightCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "18%" }}>{t.palletStatusCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "8%" }}>{t.casesLostCol}</th>
                </tr></thead>
                <tbody>
                {fresh.palletDetail.map(function (p, idx) {
                  var w = docPalletWeight(p);
                  var pStatus = twinRiverPalletStatus(shpForLiveLookup, p.palletNum) || derivePalletStatus(fresh.lot, p.palletNum, allInvoicesForPallet) || resolvePendingStage(invoicesForPallet(fresh.lot, p.palletNum, allInvoicesForPallet), slPassingIndex);
                  var pSc = SALE_STATUS_COLORS[pStatus];
                  var livePallet = palletsByNumFromShipment[String(p.palletNum)] || {};
                  var qc = livePallet.qcDefects || {};
                  var qcCatsPresent = ["severeCondition", "condition", "quality", "findings"].filter(function (cat) {
                    return Array.isArray(qc[cat]) && qc[cat].length > 0;
                  });
                  /* 2026-10-20, Arturo: "la descripcion de producto
                     cambia blackberry por la descripcion completa" —
                     same auto-generated presentation used everywhere
                     else in the app ("Blackberry 12/6oz OG GF Label"),
                     built from the pallet's own fields. Prefers the
                     LIVE pallet (resolved from the shipment, same
                     source already used above for status/defects)
                     over the bill's own static copy, same precedence
                     as every other live-lookup field in this row. */
                  var descSrc = livePallet.productBase ? livePallet : p;
                  var pDescParts = [
                    descSrc.productBase,
                    (descSrc.unitsCase && descSrc.size) ? (descSrc.unitsCase + "/" + descSrc.size) : "",
                    descSrc.category,
                    descSrc.labelInitials ? (descSrc.labelInitials + " Label") : "",
                  ].filter(Boolean);
                  var pDesc = pDescParts.join(" ") || p.productBase || "—";
                  var mainRow = (
                  <tr key={idx} style={{ borderBottom: qcCatsPresent.length > 0 ? "none" : "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.palletNum || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.tagId || livePallet.tagId || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.actualShipperInitials || livePallet.actualShipperInitials || (shpForLiveLookup && shpForLiveLookup.shipperInitials) || "—"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={pDesc}>{pDesc}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.variety || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.casesPallet || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{w.lb > 0 ? w.lb.toFixed(1) + " lb" : "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>{pSc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: pSc.bg, color: pSc.text, border: "1px solid " + pSc.border, boxShadow: SALE_STATUS_BEVEL }}>{t[pStatus] || pStatus}</span> : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>{livePallet.lostCases ? <span title={livePallet.lossReason === "other" ? (livePallet.lossReasonCustom || "") : ""} style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: "rgba(217,119,6,0.12)", color: "#B45309", border: "1px solid rgba(217,119,6,0.3)" }}>{livePallet.lostCases}{livePallet.lossReason ? " · " + (t["lossReason" + livePallet.lossReason.charAt(0).toUpperCase() + livePallet.lossReason.slice(1)] || livePallet.lossReason) : ""}</span> : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                  </tr>
                  );
                  var defectsSubRow = qcCatsPresent.length > 0 ? (
                    <tr key={idx + "-defects"} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td colSpan={9} style={{ padding: "2px 6px 6px" }}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.qcDefects}</span>
                          {qcCatsPresent.map(function (cat) {
                            var entries = qc[cat];
                            /* 2026-10-15, Arturo (GFI Team/internal
                               mode): per-defect severity coloring, now
                               for both real categories (Condition/
                               Quality — internal mode no longer files
                               everything under one flat bucket) + a
                               running total so Arturo can check it
                               against the real report. */
                            if ((cat === "quality" || cat === "condition") && shpForLiveLookup && shpForLiveLookup.texasQcMode !== "clarifresh") {
                              var billCatTotal = entries.reduce(function (s, e) { var n = parseFloat(e.pct); return s + (n >= 0 ? n : 0); }, 0);
                              return (
                                <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <b style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.03em", color: "var(--color-text-muted)" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}:</b>
                                  {entries.map(function (e, ei) {
                                    var lbl = internalDefectLabel(e, t);
                                    var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
                                    var dc = internalQualityDefectColor(e.key);
                                    return (
                                      <span key={e.key + ei} style={{ padding: "2px 8px", borderRadius: 6, fontSize: 9, fontWeight: 600, background: dc.bg, color: dc.text, border: "1px solid " + dc.border }}>{(lbl || e.key) + pct}</span>
                                    );
                                  })}
                                  <b style={{ fontSize: 9, color: "var(--color-text-secondary)" }}>{"(" + t.qcDefectTotal + ": " + billCatTotal.toFixed(2) + "%)"}</b>
                                </span>
                              );
                            }
                            var c = TOLERANCE_CATEGORY_COLORS[cat];
                            var summaryText = entries.map(function (e) {
                              var lbl = e.key === "__custom__" ? (e.custom || t.qcTolCustom) : t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
                              var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
                              return (lbl || e.key) + pct;
                            }).join(", ");
                            return (
                              <span key={cat} style={{ padding: "2px 8px", borderRadius: 6, fontSize: 9, fontWeight: 600, background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                <b style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}</b>: {summaryText}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ) : null;
                  return defectsSubRow ? [mainRow, defectsSubRow] : mainRow;
                })}
                </tbody>
              </table>
              </SectionBox>
            );
          })()}
          {/* 2026-10-20, Arturo: "payments lo cambiamos de lugar debajo
              de pallet manifest y esta nueva seccion [Bill Summary]
              donde esta ahora payments" - Payments moves into THIS
              right-hand column, stacked below Pallet Manifest, instead
              of pairing with My Profit below. */}
          <SectionBox id="payments" title={t.paymentsSection} open={open} toggle={toggle}>
            <PaymentsSection payments={fresh.payments} total={totals.subtotal} t={t} bare />
          </SectionBox>
          </div>
          <div>
          {/* OWNER PROFIT SUMMARY — reads calcLotProfit(fresh.lot) verbatim,
              zero calculation of its own. calcLotProfit lives in
              profitCalc.js, the SAME shared helper ProfitTracker.js uses,
              so a split-lot-across-invoices fix (or any future fix) only
              has to happen once. */}
          {(function() {
            if (!fresh.lot) return null;
            var p = calcLotProfit(fresh.lot);
            /* 2026-10-20, Arturo: "payments & my profit, aplica lo
               mismo" — same rule as Fees & Deductions/Payments:
               standard 11px, no bold, var(--color-text) black for
               everything except a genuinely negative amount (was
               green-for-positive/red-for-negative before; green
               dropped, red kept). */
            var clr = function(v) { return v < 0 ? "#DC2626" : "var(--color-text)"; };
            var fm = function(n) { var v = parseFloat(n) || 0; return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
            /* 2026-10-20, Arturo: "esa burbuja que encuadra el total,
               color verde, y el fondo de la seccion, ajustalo al color
               de las demas tarjetas, y remueve ese highlight del
               total" — drops the teal-tinted SectionBox `style`
               override (back to the shared default #f7f8fa flat body,
               same as Fees & Deductions/Pallet Detail/etc.) and the
               Total cell's own green-tinted background. */
            return <SectionBox id="profit" title={lang === "es" ? "Mi Ganancia" : "My Profit"} open={open} toggle={toggle}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, fontSize: 11 }}>
                <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.priceDiff) }}>{fm(p.priceDiff)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Price Diff</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.commission) }}>{fm(p.commission)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Commission</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.feeMarkup) }}>{fm(p.feeMarkup)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Fee Markup</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.finance) }}>{fm(p.finance)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Finance</div></div>
                <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.total) }}>{fm(p.total)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Total ({fm(p.perBox)}/box)</div></div>
              </div>
            </SectionBox>;
          })()}
          </div>
          </div>
          </div>

          {fresh.notes && <div style={{ marginTop: 14, fontSize: 12, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap" }}>{fresh.notes}</div>}
        </div>
        </>
      )}
      {showSalesReportPreview && (
        <SalesReportPdfPreview
          bill={fresh}
          suppliers={getSuppliers()}
          lang={lang}
          onSent={function (payload) {
            onSave({ ...fresh, extra: { ...(fresh.extra || {}), salesReportSent: buildSentInfo(payload) } });
          }}
          onClose={function () { setShowSalesReportPreview(false); }}
        />
      )}
    </DraggableModal>
  );
}

/* 2026-10-20, Arturo: "todos los fees, credits, transfers usaran la
   misma logica de captura" — Balance Transfers' create flow moves
   from an inline expanding mini-form to the same popup-dialog pattern
   as FeeEditDialog/CreditEditDialog (compact DraggableModal). There's
   still no EDIT for an existing transfer — the underlying model
   writes a mirrored entry on the OTHER bill atomically at creation
   (createBalanceTransfer), and reconciling an edit across both bills
   isn't something Arturo asked for here; Remove (X) still works the
   same as before. onCreate(direction, otherBillId, amount) is
   BillForm's existing createBalanceTransfer, untouched. */
function BalanceTransferCreator({ lang, t, sameShipperBills, suggestedAmount, onCreate }) {
  var [direction, setDirection] = useState("send");
  var [otherBillId, setOtherBillId] = useState("");
  var [amount, setAmount] = useState(suggestedAmount || "");
  var [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={function() { setOpen(true); setDirection("send"); setOtherBillId(""); setAmount(suggestedAmount || ""); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
        + {t.btCreate}
      </button>
    );
  }
  var canCreate = !!otherBillId && parseFloat(amount) > 0;
  /* 2026-10-20, Arturo: "la ventana de captura esta muy alta,
     compactala y agrega el efecto placa metalica" + "como este" —
     same plate treatment as FeeEditDialog/CreditEditDialog. */
  return (
    <DraggableModal title={t.btCreate} onClose={function () { setOpen(false); }} compact compactWidth={460} compactHeight={300} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.btType}</div>
            <SearchSelect value={direction} onChange={setDirection} options={[{ value: "send", label: t.btSend }, { value: "receive", label: t.btReceive }]} style={filterFieldPlate} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.btOtherBill}</div>
            <SearchSelect value={otherBillId} onChange={setOtherBillId} placeholder={t.btOtherBill} options={sameShipperBills.map(function(b) { return { value: b.id, label: "LOT " + (b.lot || "?") + " · " + (b.shipperName || "") }; })} style={filterFieldPlate} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.btAmount}</div>
            <input type="number" min="0" step="0.01" value={amount} onChange={function(e) { setAmount(e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
          </div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button type="button" onClick={function() { setOpen(false); }} style={plateActionBtnStyle}>{t.btCancel}</button>
          <button type="button" disabled={!canCreate} onClick={function() {
            if (!canCreate) return;
            onCreate(direction, otherBillId, amount);
            setOpen(false); setOtherBillId(""); setAmount("");
          }} style={canCreate ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{t.btCreate}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* FEE EDIT DIALOG — 2026-10-20, Arturo: "los fees quiero capturarlos
   en una ventana que emerge... y los campos ya no son editables, cada
   renglon con un boton de editar... debe una x para eliminarlo y el
   drag al lado izquierdo" — Add Fee/Edit no longer inserts an inline-
   editable row; it opens this small dialog, and the Fees table (see
   BillForm) becomes a locked list (drag handle, Edit pencil, X)
   reading/writing the exact same f.fees shape as before — a fee that
   arrived via "Accept" from a pending transfer lands as just another
   locked row, Edit opens this same dialog to correct its amount,
   nothing transfer-specific needed here. `compact` (DraggableModal)
   keeps this small form from filling the whole edge-to-edge screen. */
function FeeEditDialog({ fee, feeTypes, availableInvoices, allExpenseBills, isTwinRiver, lot, lang, t, onSave, onClose }) {
  var isNew = !fee;
  var [draft, setDraft] = useState(function () { return fee ? { ...fee } : { ...EMPTY_FEE }; });
  var isFinancing = draft.feeType === "Advance Financing Fee";
  /* Ported from the old inline updateFee: on a TR-linked bill, picking/
     changing the fee type defaults passThrough to true (TR reimburses
     GFI) except customs/aduana (GFI absorbs those). The user can still
     toggle PT manually afterward — this only sets the default. */
  var setField = function (field, value) {
    setDraft(function (prev) {
      var updated = { ...prev, [field]: value };
      if (field === "qty" || field === "rate") {
        updated.amount = ((parseFloat(updated.qty) || 1) * (parseFloat(updated.rate) || 0)).toFixed(2);
      }
      if (field === "transferToInvoice" && value) {
        var match = availableInvoices.find(function (inv) { return inv.lot === lot; });
        if (match) updated.transferTargetPU = match.puNumber;
      }
      if (isTwinRiver && (field === "feeType" || field === "feeCustom")) {
        var nameKnown = updated.feeType && updated.feeType !== "__custom__"
          ? true
          : !!String(updated.feeCustom || "").trim();
        if (nameKnown) updated.passThrough = !isCustomsFee(updated.feeType, updated.feeCustom);
      }
      return updated;
    });
  };
  var canSave = draft.feeType && draft.feeType !== "" && (draft.feeType !== "__custom__" || String(draft.feeCustom || "").trim());
  var originLabel = resolveFeeOriginLabel(draft, allExpenseBills, lang);
  /* 2026-10-20, Arturo: "la ventana de captura esta muy alta,
     compactala y agrega el efecto placa metalica" + "como este"
     (screenshot of the Filters popover) — same dark-silver plate body
     (readOnlyPlateBodyStyle's gradient), embossed uppercase labels
     (plateFieldLabel) over recessed fields (filterFieldPlate), 2-col
     grid instead of stacked rows, tight gaps. titleBarVariant makes
     the window chrome itself match (same "groundfresh" silver). */
  return (
    <DraggableModal title={isNew ? (lang === "es" ? "Agregar Fee" : "Add Fee") : (lang === "es" ? "Editar Fee" : "Edit Fee")} onClose={onClose} compact compactWidth={460} compactHeight={isFinancing ? 280 : (draft.transferToInvoice ? 460 : 400)} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {originLabel && <div style={{ padding: "5px 8px", borderRadius: 0, background: "rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.15)", fontSize: 10, color: "#7a3e00", textShadow: "0 1px 0 rgba(255,255,255,0.5)" }}>{originLabel}</div>}
          <div>
            <div style={plateFieldLabel}>{t.feeType}</div>
            <SearchSelect value={draft.feeType || ""} onChange={function (v) { setField("feeType", v); }} placeholder={t.selectFee} options={[...feeTypes.filter(function (ft) { return ft !== "Advance Financing Fee"; }).map(function (ft) { return { value: ft, label: ft }; }), { value: "__custom__", label: t.customFee }]} style={filterFieldPlate} />
            {draft.feeType === "__custom__" && <input value={draft.feeCustom || ""} onChange={function (e) { setField("feeCustom", e.target.value); }} placeholder={lang === "es" ? "Nombre del gasto" : "Fee name"} style={{ ...inp, ...filterFieldPlate, marginTop: 5 }} />}
          </div>
          {isFinancing ? (
            <div style={{ padding: "7px 8px", borderRadius: 0, background: "rgba(0,0,0,0.06)", fontSize: 10, color: "#1a2636" }}>{lang === "es" ? "Advance Financing Fee se captura junto a Sales Commission, no aquí." : "Advance Financing Fee is captured next to Sales Commission, not here."}</div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <div style={plateFieldLabel}>{t.feeQty}</div>
                  <input type="number" min="1" value={draft.qty} onChange={function (e) { setField("qty", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} />
                </div>
                <div>
                  <div style={plateFieldLabel}>{t.feeRate}</div>
                  <input type="number" min="0" step="0.01" value={draft.rate} onChange={function (e) { setField("rate", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <div style={plateFieldLabel}>{t.feeAmount}</div>
                  <input type="number" min="0" step="0.01" value={draft.amount} onChange={function (e) { setField("amount", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#1a2636", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!draft.passThrough} onChange={function (e) { setField("passThrough", e.target.checked); }} style={{ accentColor: "var(--color-accent)" }} />
                  PT ({t.selectAllPT})
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#1a2636", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!draft.transferToInvoice} onChange={function (e) { setField("transferToInvoice", e.target.checked); }} style={{ accentColor: "#c65d00" }} />
                  {lang === "es" ? "Traspasar a Factura" : "Transfer to Invoice"}
                </label>
              </div>
              {draft.transferToInvoice && (
                <div>
                  <div style={plateFieldLabel}>PU#</div>
                  <SearchSelect value={draft.transferTargetPU || ""} onChange={function (v) { setField("transferTargetPU", v); }} placeholder="PU#" style={filterFieldPlate} options={[
                    ...availableInvoices.filter(function (inv) { return inv.lot === lot; }).map(function (inv) { return { value: inv.puNumber, label: "PU# " + inv.puNumber + " (LOT " + inv.lot + ")" }; }),
                    ...availableInvoices.filter(function (inv) { return inv.lot !== lot; }).map(function (inv) { return { value: inv.puNumber, label: "PU# " + inv.puNumber + " (LOT " + (inv.lot || "?") + ")" }; })
                  ]} />
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={!canSave} onClick={function () { onSave(draft); }} style={canSave ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{isNew ? t.addFee : t.save}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* CREDIT EDIT DIALOG — same pattern as FeeEditDialog, for Credits'
   smaller field set (Fee Type, Bill Reference, Amount). */
function CreditEditDialog({ credit, feeTypes, allBillsForCredit, allBills, lang, t, onSave, onClose }) {
  var isNew = !credit;
  var [draft, setDraft] = useState(function () { return credit ? { ...credit } : { feeType: "", feeCustom: "", refBillId: "", refBillLot: "", amount: "" }; });
  var canSave = draft.feeType && draft.feeType !== "" && (draft.feeType !== "__custom__" || String(draft.feeCustom || "").trim()) && parseFloat(draft.amount) > 0;
  /* 2026-10-20, Arturo: "la ventana de captura esta muy alta,
     compactala y agrega el efecto placa metalica" + "como este" —
     same plate treatment as FeeEditDialog. */
  return (
    <DraggableModal title={isNew ? (lang === "es" ? "Agregar Crédito" : "Add Credit") : (lang === "es" ? "Editar Crédito" : "Edit Credit")} onClose={onClose} compact compactWidth={460} compactHeight={340} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.feeType}</div>
            <SearchSelect value={draft.feeType || ""} onChange={function (v) { setDraft(function (prev) { return { ...prev, feeType: v }; }); }} placeholder={t.selectFee} options={[...feeTypes.map(function (ft) { return { value: ft, label: ft }; }), { value: "__custom__", label: t.customFee }]} style={filterFieldPlate} />
            {draft.feeType === "__custom__" && <input value={draft.feeCustom || ""} onChange={function (e) { setDraft(function (prev) { return { ...prev, feeCustom: e.target.value }; }); }} placeholder={lang === "es" ? "Nombre del crédito" : "Credit name"} style={{ ...inp, ...filterFieldPlate, marginTop: 5 }} />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={plateFieldLabel}>{t.creditBillRef}</div>
              <SearchSelect value={draft.refBillId || ""} onChange={function (v) {
                var pickedBill = (allBills || []).find(function (b) { return String(b.id) === String(v); });
                setDraft(function (prev) { return { ...prev, refBillId: v, refBillLot: pickedBill ? (pickedBill.lot || "") : prev.refBillLot }; });
              }} placeholder={lang === "es" ? "Selecciona bill" : "Select bill"} options={allBillsForCredit} style={filterFieldPlate} />
            </div>
            <div>
              <div style={plateFieldLabel}>{lang === "es" ? "o LOT" : "or LOT"}</div>
              <input value={draft.refBillLot || ""} onChange={function (e) { setDraft(function (prev) { return { ...prev, refBillLot: e.target.value }; }); }} placeholder="LOT" style={{ ...inp, ...filterFieldPlate, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
          </div>
          <div>
            <div style={plateFieldLabel}>{t.creditAmount}</div>
            <input type="number" min="0" step="0.01" value={draft.amount || ""} onChange={function (e) { setDraft(function (prev) { return { ...prev, amount: e.target.value }; }); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
          </div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={!canSave} onClick={function () { onSave(draft); }} style={canSave ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{isNew ? t.addCredit : t.save}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* ======================== BILL FORM ======================== */
function BillForm({ bill, bills, t, lang, onSave, onDelete, onCancel, embedded, feeCategoryMap: feeCategoryMapProp, sectionsOpen, toggleSection }) {
  var isNew = !bill;
  /* 2026-10-16, Arturo: "quiero que al entrar a editar se mantenga
     las mismas secciones segmentadas... los desplegables deben
     estar, todo igual en modo editar" — embedded inside DetailPanel,
     `sectionsOpen`/`toggleSection` are passed down from there (same
     state object view mode uses, so switching Edit↔view keeps each
     section's open/closed state instead of resetting it). The
     standalone "New Bill" form (not embedded in DetailPanel, see the
     other <BillForm> call site) has no such parent state to share,
     so it falls back to its own local state with the same defaults
     as view mode (shipmentInfo/product/fees open, the rest closed). */
  var [ownSectionsOpen, setOwnSectionsOpen] = useState({ shipmentInfo: true, product: true, pallets: false, fees: true, linked: false, payments: false, profit: false });
  var open = sectionsOpen || ownSectionsOpen;
  var toggle = toggleSection || function (k) { setOwnSectionsOpen(function (p) { return { ...p, [k]: !p[k] }; }); };
  var [feeTypes, setFeeTypes] = useState(getFeeTypes);
  /* Fee name → category, for the Summary box's per-category breakdown
     (see FeeBreakdownLines). DetailPanel already loads this and passes
     it down when this form is embedded; a directly-opened BillForm
     (new bill, or opened outside DetailPanel) loads its own copy. */
  var [feeCategoryMapOwn, setFeeCategoryMapOwn] = useState({});
  useEffect(function () {
    if (feeCategoryMapProp) return;
    var cancelled = false;
    loadFromSupabase("fee_catalog").then(function (rows) {
      if (cancelled) return;
      var map = {};
      (rows || []).forEach(function (r) { if (r && r.name) map[String(r.name).trim().toLowerCase()] = r.category || "other"; });
      setFeeCategoryMapOwn(map);
    }).catch(function () {});
    return function () { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var feeCategoryMap = feeCategoryMapProp || feeCategoryMapOwn;
  var availableInvoices = useMemo(function() {
    var invs = freshInvoicesLS();
    return invs;
  }, []);

  /* 2026-10-20, Arturo: "lo replicamos en modo edicion... linked
     invoices igual que en embarques" — read-only mirror of
     DetailPanel's Linked Invoices mini-table (same lookup, same
     amount/status math), minus the interactive "+ Link Invoice"
     picker (that mutates gfi-invoices immediately, outside this
     form's own Save flow — kept exclusive to view mode on purpose).
     Reads from the original `bill.lot` (not `f.lot`, which isn't
     editable here anyway) so it reflects what's actually saved. */
  var linkedInvoicesEdit = useMemo(function () {
    if (!bill || !bill.lot) return [];
    return findInvoicesByLot(bill.lot, availableInvoices);
  }, [bill, availableInvoices]);
  var [previewInvoiceEdit, setPreviewInvoiceEdit] = useState(null);

  /* For the Pending Pick Up / Pending Delivery display default (see
     saleStatusCalc.js resolvePendingStage) — Bill is a mirror, never sets
     status itself, but still needs to know whether any Four Seasons
     invoice on this lot has sent its Passing/BOL yet. */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function() {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function(sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function() {});
    return function() { cancelled = true; };
  }, []);

  /* Existing shipment lots, surfaced in the Lot SearchSelect when this is
     a new bill so the user can pick one and have shipper/proforma/lines
     auto-pulled (same shape produced by createFromShipment). Newest-lot
     first since that's what the user usually wants. */
  var availableShipmentLots = useMemo(function() {
    var shps = loadLS("gfi-shipments", []);
    return shps
      .filter(function(s) { return s.lot != null && s.lot !== ""; })
      .sort(function(a, b) { return (parseInt(b.lot, 10) || 0) - (parseInt(a.lot, 10) || 0); })
      .map(function(s) {
        var name = s.shipperName || resolveShipperName(s.supplierId) || "—";
        return { value: String(s.lot), label: "LOT " + s.lot + " · " + name };
      });
  }, []);

  var [f, setF] = useState(function() {
    if (bill) return {
      ...bill,
      /* Ensure every line has an explicit commissionRate when the form
         opens — old bills' lines may not have it persisted yet; surface
         the bill-level rate as the per-line default so the user can see
         and edit it inline. Save will persist whatever value is in each
         input. */
      /* 2026-10-20, Arturo: "price debe mostrar 2 decimales por
         default" — formatted once here (same one-time-on-open pattern
         already used for Fixed Pricing's fmt2), so a line's price
         reads "23.00" the instant the form opens instead of only
         after the user blurs out of that field. */
      lines: (bill.lines || []).map(function(l) {
        var withComm = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? { ...l, commissionRate: bill.commissionRate || "" }
          : { ...l };
        if (withComm.reportedPrice !== undefined && withComm.reportedPrice !== null && withComm.reportedPrice !== "" && !isNaN(parseFloat(withComm.reportedPrice))) {
          withComm.reportedPrice = parseFloat(withComm.reportedPrice).toFixed(2);
        }
        return withComm;
      }),
      fees: (bill.fees || []).map(function(fe) { return { ...EMPTY_FEE, ...fe }; }),
      credits: (bill.credits || []).map(function(c) { return { ...c }; }),
      balanceTransfers: (bill.balanceTransfers || []).map(function(t) { return { ...t }; }),
      /* Loaded verbatim — editing an EXISTING bill never auto-(re)copies
         the shipment manifest, so any manual adjustment the user made
         here survives. Historical bills with none get the opt-in
         "Populate from shipment" button instead (see below). */
      palletDetail: (bill.palletDetail || []).map(function(p) { return { ...p }; }),
    };
    return {
      id: "", shipmentId: "", lot: "", billDate: "", dueDate: "",
      supplierId: "", shipperName: "", proforma: "", shipperInvoice: "",
      entryNumber: "", reference: "", commissionRate: "5",
      status: "draft", amountPaid: "",
      lines: [], fees: [{ ...EMPTY_FEE }],
      credits: [], balanceTransfers: [],
      notes: "", exportCerts: [],
      palletDetail: [],
    };
  });

  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [dirty, setDirty] = useState(false);
  var [showExitConfirm, setShowExitConfirm] = useState(false);
  var [trVer, setTrVer] = useState(0);
  var [newFeeInput, setNewFeeInput] = useState("");

  /* 2026-10-16 — pending fee-transfer queue (Expense Bill / other Bill
     costs waiting to be Accepted here) now lives in Supabase's
     "fee_transfers" table, not just this browser's localStorage — see
     ExpenseBillRegistry.js's handleSave for the root-cause writeup.
     Fetched fresh on mount/trVer bump so Accept/Reject on another
     device, or a transfer created moments ago elsewhere, shows up
     without needing a hard reload. localStorage stays as the
     instant-paint fallback for the very first render. */
  var [feeTransfers, setFeeTransfers] = useState(function () { return loadLS("gfi-fee-transfers", []); });
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("fee_transfers", { force: true }).then(function (rows) {
      if (cancelled) return;
      setFeeTransfers(rows || []);
      try { localStorage.setItem("gfi-fee-transfers", JSON.stringify(rows || [])); } catch (e) {}
    }).catch(function () {});
    return function () { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trVer]);

  var set = function(k, v) { setF(function(prev) { return { ...prev, [k]: v }; }); setDirty(true); };

  /* When the user picks a lot from the Lot SearchSelect on a new bill,
     pull the matching shipment from gfi-shipments and prefill the same
     fields createFromShipment populates (shipper, supplierId, proforma,
     shipper invoice, entry #, due date default, product lines with
     reportedPrice/saleStatus seeded). Clearing the SearchSelect resets
     those linked fields so the user can re-pick. Manually entered fees,
     notes and dates are preserved across picks. */
  var prefillFromLot = function(lotValue) {
    if (!lotValue) {
      setF(function(prev) { return { ...prev, lot: "", shipmentId: "", supplierId: "", shipperName: "", proforma: "", shipperInvoice: "", entryNumber: "", lines: [], palletDetail: [] }; });
      setDirty(true);
      return;
    }
    var shps = loadLS("gfi-shipments", []);
    var shp = shps.find(function(s) { return String(s.lot) === String(lotValue); });
    if (!shp) { set("lot", String(lotValue)); return; }
    setF(function(prev) {
      var arrival = shp.dateArrival || shp.dateDeparture || "";
      var dueDefault = "";
      if (arrival) { var dd = new Date(arrival + "T00:00:00"); dd.setDate(dd.getDate() + 21); dueDefault = dd.toISOString().substring(0, 10); }
      return {
        ...prev,
        shipmentId: shp.id,
        lot: shp.lot != null ? String(shp.lot) : "",
        billDate: prev.billDate || new Date().toISOString().substring(0, 10),
        dueDate: prev.dueDate || dueDefault,
        supplierId: shp.supplierId || "",
        shipperName: shp.shipperName || resolveShipperName(shp.supplierId) || "",
        proforma: shp.proforma || "",
        shipperInvoice: shp.shipperInvoice || "",
        entryNumber: shp.entryNumber || "",
        /* New lines from a shipment inherit the bill-level default
           commissionRate so the user sees the same starting % per
           line that the bill carries. */
        lines: (shp.lines || []).map(function(l) { return { ...l, reportedPrice: l.price || "", saleStatus: l.saleStatus || "", commissionRate: prev.commissionRate || "" }; }),
        /* Deep copy of the shipment's pallet manifest (1A) — only on a
           NEW bill's lot pick, never on an existing one being edited. */
        palletDetail: deepCopyPalletManifest(shp),
      };
    });
    setDirty(true);
  };
  /* "Populate from shipment" — opt-in button for historical bills that
     predate palletDetail (FASE 7 pattern, reserved here since FASE 5
     needs the same action). Looks up the shipment by shipmentId first,
     falling back to lot for older records missing the FK. */
  var populateBillPalletDetail = function() {
    var shps = loadLS("gfi-shipments", []);
    var shp = (f.shipmentId && shps.find(function(s) { return String(s.id) === String(f.shipmentId); }))
      || (f.lot && shps.find(function(s) { return String(s.lot) === String(f.lot); }))
      || null;
    if (!shp) return;
    setF(function(prev) { return { ...prev, palletDetail: deepCopyPalletManifest(shp) }; });
    setDirty(true);
  };
  var updatePalletDetailCases = function(idx, value) {
    setF(function(prev) { return { ...prev, palletDetail: (prev.palletDetail || []).map(function(p, i) { return i === idx ? { ...p, casesPallet: value } : p; }) }; });
    setDirty(true);
  };
  /* 2026-10-30, Arturo: "los pallets en el bill se llenan automaticamente
     pero debiera existir la opcion de editar para corregir estos
     detalles manualmente y no requerir codigo" — lot 58074/BILL-0174's
     Blueberry pallet got copied in as "090" (deepCopyPalletManifest's
     one-time snapshot) while the shipment was later corrected to its
     real number "005", and there was no way to fix that mismatch here
     short of a direct database edit. Generic field setter (Pallet #
     and Tag ID, the 2 identity fields used to match this row against
     the shipment for every live lookup above — status/qcDefects/
     lostCases/description) lets Arturo correct it himself from now on. */
  var updatePalletDetailField = function(idx, field, value) {
    setF(function(prev) { return { ...prev, palletDetail: (prev.palletDetail || []).map(function(p, i) { return i === idx ? { ...p, [field]: value } : p; }) }; });
    setDirty(true);
  };

  var totals = useMemo(function() { return calcBillTotals(f); }, [f.lines, f.fees, f.commissionRate, f.credits, f.balanceTransfers]);

  /* 2026-10-20, Arturo: fee capture moved into FeeEditDialog (see
     "los fees quiero capturarlos en una ventana que emerge" below) —
     the TR-linked-bill passThrough auto-default that used to live in
     this inline updateFee now lives in FeeEditDialog's own setField,
     passed the same `isTwinRiver` flag computed here. */
  var billIsTR = billIsLinkedToTwinRiver(f.lot);

  var removeFee = function(idx) { setF(function(prev) { return { ...prev, fees: prev.fees.filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };
  /* 2026-10-20, Arturo: "los fees quiero capturarlos en una ventana
     que emerge... y los campos ya no son editables, cada renglon con
     un boton de editar" — editingFeeIdx: null = dialog closed,
     "new" = Add Fee dialog, a number = Edit Fee dialog for that
     f.fees index (never the filtered/displayed position — see the
     table render below). */
  var [editingFeeIdx, setEditingFeeIdx] = useState(null);
  var saveFeeDialog = function(draft) {
    if (editingFeeIdx === "new") {
      setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), draft] }; });
    } else {
      setF(function(prev) { return { ...prev, fees: prev.fees.map(function(fe, i) { return i === editingFeeIdx ? draft : fe; }) }; });
    }
    setDirty(true);
    setEditingFeeIdx(null);
  };

  /* 2026-10-20, Arturo: "advance financing fee debe estar fuera de la
     lista y aparece por default debajo de sales comission, al lado el
     porcentaje y en seguida el monto justo como aparece sales
     commission" — no longer a selectable row inside the Fees table
     (filtered out of its SearchSelect options below); it's still the
     SAME underlying fee entry in f.fees (feeType==="Advance Financing
     Fee"), just upserted/read from its own always-visible row next to
     Commission instead. An empty % removes the entry entirely rather
     than leaving a stale $0.00 row behind. */
  var updateFinancingFee = function(pctValue) {
    setF(function(prev) {
      var fees = prev.fees || [];
      var idx = fees.findIndex(function(fe) { return fe.feeType === "Advance Financing Fee"; });
      if (pctValue === "") {
        if (idx === -1) return prev;
        return { ...prev, fees: fees.filter(function(_, i) { return i !== idx; }) };
      }
      var amt = (totals.grossSales * ((parseFloat(pctValue) || 0) / 100)).toFixed(2);
      var newFees = idx === -1
        ? fees.concat([{ ...EMPTY_FEE, feeType: "Advance Financing Fee", pctRate: pctValue, amount: amt }])
        : fees.map(function(fe, i) { return i === idx ? { ...fe, pctRate: pctValue, amount: amt } : fe; });
      return { ...prev, fees: newFees };
    });
    setDirty(true);
  };
  /* 2026-10-29, Arturo: "en la factura al poner porcentaje me sugiere
     una cantidad pero yo puedo modificarla, en los bills no puedo
     modificarla y debe poder modificarse, o sea sugerir pero en un
     campo contiguo capturar" — same split as InvoiceRegistry.js's
     updateFinancingFeeAmount: the %-computed amount stays a reference
     next to "%" (updateFinancingFee above still overwrites this real
     amount with the fresh %-computed default every time the % itself
     changes), while this handler only fires on a direct edit of the
     amount field, leaving pctRate/its reference untouched so Arturo can
     charge more or less than the % literally works out to. */
  var updateFinancingFeeAmount = function(amtValue) {
    setF(function(prev) {
      var fees = prev.fees || [];
      var idx = fees.findIndex(function(fe) { return fe.feeType === "Advance Financing Fee"; });
      if (idx === -1) {
        if (amtValue === "") return prev;
        return { ...prev, fees: fees.concat([{ ...EMPTY_FEE, feeType: "Advance Financing Fee", pctRate: "", amount: amtValue }]) };
      }
      return { ...prev, fees: fees.map(function(fe, i) { return i === idx ? { ...fe, amount: amtValue } : fe; }) };
    });
    setDirty(true);
  };
  /* Bulk PT toggle — sets passThrough on every fee row at once. Also
     applies to blank rows so when the user later picks a feeType the
     row inherits the column-level PT default. */
  var setAllFeesPT = function(checked) {
    setF(function(prev) {
      var newFees = (prev.fees || []).map(function(fe) { return { ...fe, passThrough: !!checked }; });
      return { ...prev, fees: newFees };
    });
    setDirty(true);
  };
  /* Header checkbox state — checked when every fee has PT on, indeterm
     when some do, unchecked when none. Empty fees array → unchecked. */
  var ptFlags = (f.fees || []).map(function(fe) { return !!fe.passThrough; });
  var ptAllOn = ptFlags.length > 0 && ptFlags.every(Boolean);
  var ptAnyOn = ptFlags.some(Boolean);
  var ptMixed = ptAnyOn && !ptAllOn;

  /* Drag-reorder for product lines and fees. */
  var dndLines = useDragReorder(f.lines, function(next) { setF(function(p) { return { ...p, lines: next }; }); setDirty(true); });
  var dndFees = useDragReorder(f.fees, function(next) { setF(function(p) { return { ...p, fees: next }; }); setDirty(true); });

  /* Add custom fee type to the dynamic list */
  var addCustomFeeType = function(name) {
    if (!name || feeTypes.includes(name)) return;
    var updated = [...feeTypes, name];
    setFeeTypes(updated);
    saveFeeTypes(updated);
  };

  /* ─── Credits (favor the shipper — ADD to bill total) ───
     Each credit row: feeType (catalog or custom), refBillId/refBillLot
     (visual reference to another bill — does NOT modify the referenced
     bill), amount. Same popup-dialog capture as Fees. */
  var removeCredit = function(idx) { setF(function(prev) { return { ...prev, credits: (prev.credits || []).filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };
  var [editingCreditIdx, setEditingCreditIdx] = useState(null);
  var saveCreditDialog = function(draft) {
    if (editingCreditIdx === "new") {
      setF(function(prev) { return { ...prev, credits: [...(prev.credits || []), draft] }; });
    } else {
      setF(function(prev) { return { ...prev, credits: (prev.credits || []).map(function(cr, i) { return i === editingCreditIdx ? draft : cr; }) }; });
    }
    setDirty(true);
    setEditingCreditIdx(null);
  };

  /* ─── Balance Transfers (cross-bill, atomic) ───
     Each entry stamped on BOTH bills with cross-references:
       this side    other side
       ──────────   ──────────
       credit       debit       ← "send my negative" (this bill is
                                   source: receives credit; other absorbs)
       debit        credit      ← "receive negative from" (this bill is
                                   target: takes the debt; other clears)
     Persisted to gfi-bills immediately so the OTHER bill is updated
     even if the user cancels editing this one. */
  var allBills = useMemo(function() { return loadLS("gfi-bills", []); }, [trVer]);
  function findOtherBill(id) { return allBills.find(function(b) { return String(b.id) === String(id); }); }
  /* 2026-10-20, Arturo: "'From EXP-0142' no me dice nada, prefiero
     diga From Bill y el numero de factura que me dieron" — resolves a
     transferred fee's origin to the VENDOR's own invoice number
     (ExpenseBillRegistry's `reference` field), not our internal
     EXP-#### id, which only this lookup ever needs. */
  var allExpenseBills = useMemo(function() { return loadLS("gfi-expense-bills", []); }, [trVer]);
  function nextTransferId() { return "BT-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6); }
  /* direction: "send" → this bill gets credit, other gets debit
                "receive" → this bill gets debit, other gets credit  */
  var createBalanceTransfer = function(direction, otherBillId, amount) {
    var amt = parseFloat(amount) || 0;
    if (!otherBillId || amt <= 0) return;
    var other = findOtherBill(otherBillId);
    if (!other) return;
    var tid = nextTransferId();
    var today = new Date().toISOString().substring(0, 10);
    var thisType = direction === "send" ? "credit" : "debit";
    var otherType = direction === "send" ? "debit" : "credit";
    var thisEntry = { id: tid, type: thisType, otherBillId: other.id, otherBillLot: other.lot || "", otherShipperName: other.shipperName || "", amount: amt, date: today };
    var otherEntry = { id: tid, type: otherType, otherBillId: f.id || "(this bill)", otherBillLot: f.lot || "", otherShipperName: f.shipperName || "", amount: amt, date: today };
    /* Persist the OTHER bill immediately. */
    try {
      var current = loadLS("gfi-bills", []);
      var nextStore = current.map(function(b) {
        if (String(b.id) !== String(other.id)) return b;
        return { ...b, balanceTransfers: [...(b.balanceTransfers || []), otherEntry] };
      });
      saveLS("gfi-bills", nextStore);
    } catch (e) { console.error("[BalanceTransfer] failed to persist other bill", e); return; }
    /* Update this bill's in-memory state (will persist when user saves). */
    setF(function(prev) { return { ...prev, balanceTransfers: [...(prev.balanceTransfers || []), thisEntry] }; });
    setDirty(true);
    setTrVer(function(v) { return v + 1; });
  };
  var removeBalanceTransfer = function(idx) {
    var entry = (f.balanceTransfers || [])[idx];
    if (!entry) return;
    /* Reverse the persistence on the OTHER bill. Match by transfer id. */
    if (entry.otherBillId && entry.id) {
      try {
        var current = loadLS("gfi-bills", []);
        var nextStore = current.map(function(b) {
          if (String(b.id) !== String(entry.otherBillId)) return b;
          return { ...b, balanceTransfers: (b.balanceTransfers || []).filter(function(t) { return t.id !== entry.id; }) };
        });
        saveLS("gfi-bills", nextStore);
      } catch (e) { console.error("[BalanceTransfer] failed to update other bill on remove", e); }
    }
    setF(function(prev) { return { ...prev, balanceTransfers: (prev.balanceTransfers || []).filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
    setTrVer(function(v) { return v + 1; });
  };

  /* DnD hooks for the new sections. */
  var dndCredits = useDragReorder(f.credits, function(next) { setF(function(p) { return { ...p, credits: next }; }); setDirty(true); });
  var dndTransfers = useDragReorder(f.balanceTransfers, function(next) { setF(function(p) { return { ...p, balanceTransfers: next }; }); setDirty(true); });

  /* Bills selectable as a credit's reference (any bill, lot search). */
  var allBillsForCredit = useMemo(function() {
    return allBills.filter(function(b) { return b.id !== f.id; }).map(function(b) {
      return { value: b.id, label: "LOT " + (b.lot || "?") + " · " + (b.shipperName || "—") };
    });
  }, [allBills, f.id]);
  /* Bills from the SAME shipper as this bill — for balance transfers. */
  var sameShipperBills = useMemo(function() {
    return allBills.filter(function(b) {
      if (b.id === f.id) return false;
      if (f.supplierId && String(b.supplierId) === String(f.supplierId)) return true;
      if (f.shipperName && b.shipperName === f.shipperName) return true;
      return false;
    });
  }, [allBills, f.id, f.supplierId, f.shipperName]);

  var updateLineSettlement = function(idx, val) {
    setF(function(prev) {
      var newLines = prev.lines.map(function(l, i) { return i === idx ? { ...l, settlement: val } : l; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  /* Per-line commission %. Empty input is allowed (stored as ""), in
     which case the calc helper falls back to bill.commissionRate so
     totals never silently drop a line's contribution. */
  var updateLineCommission = function(idx, val) {
    setF(function(prev) {
      var newLines = prev.lines.map(function(l, i) { return i === idx ? { ...l, commissionRate: val } : l; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  /* Update product line reported price */
  var updateLinePrice = function(idx, price) {
    setF(function(prev) {
      var newLines = prev.lines.map(function(l, i) { return i === idx ? { ...l, reportedPrice: price } : l; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };
  var updateLineCases = function(idx, cases) {
    setF(function(prev) {
      var newLines = prev.lines.map(function(l, i) { return i === idx ? { ...l, cases: cases } : l; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };
  /* Editable Pallets per line — symmetric with Cases. Lets the user
     reapportion both quantities when splitting a line (e.g. 4 pallets
     / 480 cases → 2/240 + 2/240). Bill/invoice totals already reduce
     over .lines so they recompute automatically. */
  var updateLinePallets = function(idx, pallets) {
    setF(function(prev) {
      var newLines = prev.lines.map(function(l, i) { return i === idx ? { ...l, pallets: pallets } : l; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };
  /* Split a product line into two: original keeps half (rounded down),
     a clone inserted right after takes the rest. User then edits both
     cases/price/saleStatus as needed. Lines below 2 cases just clone
     with 1/0 split. Bakes the resolved productDescription onto both
     halves so the full presentation/pack/label string ("Blackberry
     12/6 oz OG GF Label") survives even if the original only carried
     a productId reference. */
  var splitLine = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      var total = parseInt(orig.cases, 10) || 0;
      var halfA = total >= 2 ? Math.floor(total / 2) : (total > 0 ? 1 : 0);
      var halfB = total - halfA;
      /* Apply the same halving to pallets so a 4-pallet/480-case split
         becomes 2/240 + 2/240 instead of having both halves still claim
         all 4 pallets. Round down on the first half. */
      var totalP = parseInt(orig.pallets, 10) || 0;
      var halfPA = totalP >= 2 ? Math.floor(totalP / 2) : (totalP > 0 ? 1 : 0);
      var halfPB = totalP - halfPA;
      var desc = orig.productDescription || resolveProductDesc(orig) || "";
      newLines[idx] = { ...orig, productDescription: desc, cases: String(halfA), pallets: totalP > 0 ? String(halfPA) : orig.pallets };
      newLines.splice(idx + 1, 0, { ...orig, productDescription: desc, cases: String(halfB), pallets: totalP > 0 ? String(halfPB) : orig.pallets });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };
  /* Clone an existing product line — same product, full description and
     reportedPrice preserved — so the user can split out a sub-quantity
     with its own sale status. Clears cases + saleStatus only (price is
     usually identical across the split; matches InvoiceRegistry behavior).
     Bakes the resolved description for the same reason as splitLine. */
  var addLineCopy = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      var desc = orig.productDescription || resolveProductDesc(orig) || "";
      /* Clear pallets too — the user fills in the cases AND pallets
         they're allocating to this clone (the original keeps its
         existing totals until the user re-balances them). */
      newLines.splice(idx + 1, 0, { ...orig, productDescription: desc, cases: "", pallets: "", saleStatus: "" });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };
  var removeLine = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.filter(function(_, i) { return i !== idx; });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  var validate = function() {
    var errs = {};
    if (!f.lot) errs.lot = true;
    if (!f.supplierId) errs.supplierId = true;
    if (!(f.lines || []).some(function(l) { return parseInt(l.cases, 10) > 0; })) errs.lines = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  var handleSave = async function() {
    if (!validate()) return;
    (f.fees || []).forEach(function(fe) { if (fe.feeType === "__custom__" && fe.feeCustom) addCustomFeeType(fe.feeCustom); });
    var toSave = { ...f, id: f.id || nextId(bills) };
    /* Create fee transfers for fees marked transferToInvoice. 2026-10-16:
       persisted to Supabase now, not just this browser's localStorage —
       same root cause/fix as the Expense Bill→Bill path, see
       ExpenseBillRegistry.js's handleSave. */
    var transferFees = (f.fees || []).filter(function(fe) { return fe.transferToInvoice && parseFloat(fe.amount) > 0 && fe.transferTargetPU; });
    if (transferFees.length > 0) {
      try {
        var transfers = await loadFromSupabase("fee_transfers", { force: true });
        var toDelete = transfers.filter(function(t) { return t.sourceType === "bill" && t.sourceBillId === toSave.id; }).map(function(t) { return t.id; });
        transfers = transfers.filter(function(t) { return !(t.sourceType === "bill" && t.sourceBillId === toSave.id); });
        var invs = freshInvoicesLS();
        var toCreate = transferFees.map(function(fe) {
          var targetInv = invs.find(function(inv) { return inv.puNumber === fe.transferTargetPU; });
          return { id: "TRF-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6), sourceType: "bill", sourceBillId: toSave.id, sourceLot: f.lot, targetType: "invoice", targetLot: targetInv ? targetInv.lot : f.lot, targetPU: fe.transferTargetPU, targetInvoiceId: targetInv ? targetInv.id : "", feeType: fe.feeType, feeCustom: fe.feeCustom || "", qty: fe.qty, rate: fe.rate, amount: fe.amount, pctRate: fe.pctRate || "", status: "pending", dateCreated: new Date().toISOString().substring(0, 10) };
        });
        transfers = transfers.concat(toCreate);
        await Promise.all(toDelete.map(function(id) { return deleteFromSupabase("fee_transfers", id).catch(function(){}); }));
        if (toCreate.length > 0) await saveManyToSupabase("fee_transfers", toCreate);
        localStorage.setItem("gfi-fee-transfers", JSON.stringify(transfers));
      } catch(e) { console.error("Fee transfer error", e); }
    }
    onSave(toSave);
  };

  var tryCancel = function() { if (dirty) setShowExitConfirm(true); else onCancel(); };
  var errS = { borderColor: "#ef4444" };

  var body = (
    <>
        {/* 2026-10-20, Arturo: "esta seccion sin formato aun y no has
            agregado received by ni linked invoices" \u2014 same brushed-
            metal strip as view mode's own top strip, same "Received
            by"/"Links:" content on the left (read-only, mirrored from
            the linked Shipment \u2014 never edited here), status pill +
            transition buttons stay on the right where they already
            were. */}
        {(function () {
          var shpForSubHeader = getShipments().find(function (s) { return String(s.lot) === String(f.lot); });
          return (
            <div style={{ padding: "10px 20px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 11 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
                {shpForSubHeader && (
                  <>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>
                      {shpForSubHeader.texasQcMode === "clarifresh" ? (lang === "es" ? "Recepci\u00f3n en Twin River" : "Received by Twin River") : (lang === "es" ? "Recepci\u00f3n en GFI" : "Received by GFI")}
                    </span>
                    <span style={{ fontWeight: 600, textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>{shpForSubHeader.receivedDate ? fmtDate(shpForSubHeader.receivedDate) + (shpForSubHeader.receivedTime ? " " + shpForSubHeader.receivedTime : "") : "\u2014"}</span>
                  </>
                )}
                {linkedInvoicesEdit.length > 0 && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
                    {linkedInvoicesEdit.map(function (inv) {
                      return (
                        <button key={"inv-" + inv.id} type="button" style={{ padding: "3px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" }} onClick={function () {
                          setPreviewInvoiceEdit(inv);
                        }}>{(lang === "es" ? "Abrir Invoice Vinculado PU " : "Open Invoice Linked PU ") + (inv.puNumber || inv.id)}</button>
                      );
                    })}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 3, alignItems: "center", flexShrink: 0 }}>
                {(function() { var sty = BILL_STATUS_STYLES[f.status] || BILL_STATUS_STYLES.draft; var key = f.status === "draft" ? "statusDraft" : f.status === "finalized" ? "statusFinalized" : "statusPaid"; return <span style={{ padding: "3px 10px", borderRadius: 0, border: "1px solid " + sty.border, background: sty.bg, color: sty.text, boxShadow: SALE_STATUS_BEVEL, fontSize: 10, fontWeight: 700 }}>{t[key]}</span>; })()}
                {f.status === "draft" && <button onClick={function() { set("status", "finalized"); }} style={statusPlateBtnStyle("teal", 6)}>{"\u2192"} {t.statusFinalized}</button>}
                {f.status === "finalized" && <>
                  <button onClick={function() { set("status", "draft"); }} style={statusPlateBtnStyle("gray", 6)}>{"\u2190"} {t.statusDraft}</button>
                  <button onClick={function() { set("status", "paid"); }} style={statusPlateBtnStyle("green", 3)}>{"\u2192"} {t.statusPaid}</button>
                </>}
                {f.status === "paid" && <button onClick={function() { set("status", "finalized"); }} style={statusPlateBtnStyle("teal", 6)}>{"\u2190"} {t.statusFinalized}</button>}
              </div>
            </div>
          );
        })()}

        {/* BODY */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* PENDING FEE TRANSFERS */}
          {(function() {
            void trVer;
            var allTransfers = feeTransfers;
            var transfers = allTransfers.filter(function(tr) {
              if (tr.status !== "pending") return false;
              if (tr.targetType !== "bill") return false;
              if (tr.targetBillId !== f.id && tr.targetLot !== f.lot) return false;
              var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
              var alreadyIn = (f.fees || []).some(function(ef) { return (ef.fromInvoice || ef.fromExpense) && (ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType) === fname; });
              return !alreadyIn;
            });
            if (transfers.length === 0) return null;
            /* Group key: invoice-sourced transfers key by sourcePU; expense-
               sourced ones (no PU) key by sourceExpenseBill instead of
               falling into a shared "undefined" bucket. */
            var grouped = {};
            transfers.forEach(function(tr) { var gk = tr.sourcePU || tr.sourceExpenseBill || "?"; if (!grouped[gk]) grouped[gk] = []; grouped[gk].push(tr); });
            return Object.keys(grouped).map(function(gk) {
              var items = grouped[gk];
              var isExpense = items[0] && items[0].sourceType === "expense";
              var totalAmt = items.reduce(function(s, tr) { return s + (parseFloat(tr.amount) || 0); }, 0);
              return <div key={gk} style={{ margin: "10px 20px 0", padding: "10px 14px", borderRadius: 8, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#D97706" }}>{isExpense ? (lang === "es" ? "Costos pendientes de Expense Bill" : "Pending costs from Expense Bill") : (lang === "es" ? "Costos pendientes del Invoice PU# " + gk : "Pending costs from Invoice PU# " + gk)} ({fmtMoney(totalAmt)})</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={function() {
                      /* Accept: add fees to bill */
                      /* TR-linked bills default to passthrough per fee, customs excepted. */
                      var isTR = billIsLinkedToTwinRiver(f.lot);
                      /* Expense-sourced transfers have no sourcePU — tagging
                         them fromInvoice:undefined left them with neither
                         fromInvoice nor fromExpense set, so the "already
                         applied" dedup check above could never recognize
                         them and kept re-adding the same fee on every
                         Accept click across resaves. */
                      var newFees = items.map(function(tr) {
                        var pt = isTR && !isCustomsFee(tr.feeType, tr.feeCustom);
                        var fee = { feeType: tr.feeType, feeCustom: tr.feeCustom, qty: tr.qty || 1, rate: tr.rate || "", amount: tr.amount, passThrough: pt };
                        if (tr.sourceType === "expense") fee.fromExpense = tr.sourceExpenseBill || true;
                        else fee.fromInvoice = tr.sourcePU;
                        return fee;
                      });
                      setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), ...newFees] }; });
                      /* Mark transfers as accepted — Supabase first (so
                         it sticks across devices/sessions), then the
                         local mirror/state for an instant repaint. */
                      var acceptedItems = items.map(function(t) { return { ...t, status: "accepted" }; });
                      saveManyToSupabase("fee_transfers", acceptedItems).catch(function(e) { console.error("Fee transfer accept failed", e); });
                      setFeeTransfers(function(prev) { return prev.map(function(t) { return items.some(function(i) { return i.id === t.id; }) ? { ...t, status: "accepted" } : t; }); });
                      try {
                        var allTr = loadLS("gfi-fee-transfers", []);
                        var updated = allTr.map(function(t) { return items.some(function(i) { return i.id === t.id; }) ? { ...t, status: "accepted" } : t; });
                        localStorage.setItem("gfi-fee-transfers", JSON.stringify(updated));
                      } catch(e) {}
                      setDirty(true);
                      setTrVer(function(v) { return v + 1; });
                    }} style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: "#059669", color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Aceptar" : "Accept"}</button>
                    <button onClick={function() {
                      /* Reject: mark as rejected — same Supabase-first
                         pattern as Accept. */
                      var rejectedItems = items.map(function(t) { return { ...t, status: "rejected" }; });
                      saveManyToSupabase("fee_transfers", rejectedItems).catch(function(e) { console.error("Fee transfer reject failed", e); });
                      setFeeTransfers(function(prev) { return prev.map(function(t) { return items.some(function(i) { return i.id === t.id; }) ? { ...t, status: "rejected" } : t; }); });
                      try {
                        var allTr = loadLS("gfi-fee-transfers", []);
                        var updated = allTr.map(function(t) { return items.some(function(i) { return i.id === t.id; }) ? { ...t, status: "rejected" } : t; });
                        localStorage.setItem("gfi-fee-transfers", JSON.stringify(updated));
                      } catch(e) {}
                      setTrVer(function(v) { return v + 1; });
                    }} style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Rechazar" : "Reject"}</button>
                  </div>
                </div>
                <div style={{ fontSize: 11 }}>{items.map(function(tr, i) {
                  var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
                  return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#92400E" }}><span>{fname}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(tr.amount)}</span></div>;
                })}</div>
              </div>;
            });
          })()}

          {/* 2026-10-20, Arturo: "algo no me gusta con esto de ser
              dinamica, porque las tarjetas se cambian de posicion
              solas... no puedo trabajar, me confunde" + "el
              autoajuste que sea solo de lo alto de las tarjetas no
              de lo ancho" - same fixed-column fix as view mode:
              two FIXED columns (left = Shipment Info/Fees/Bill
              Summary, right = Product/Pallet Detail+Payments/My
              Profit), each its own independent flex stack so
              height still auto-adjusts within a column without a
              card ever moving to the other side. */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 20px 12px" }}>
          <div style={{ flex: "0 1 38%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
          {/* 2026-10-20, Arturo: "shipment info al igual que en modo no
              edicion, es solo visible con boton para editar con la
              misma logica, en una placa igual y la informacion se
              distribuye dentro igual que en modo no edicion" — once a
              bill is linked to a shipment, EVERY field here (Bill
              Date/Due Date included — Arturo confirmed they're always
              populated FROM the shipment, "el embarque no puede tener
              fechas distintas, siempre es la misma") mirrors the same
              read-only plate + single Edit button as DetailPanel's own
              Shipment Info — no more inline inputs. "Reference" and
              "Source Shipment" dropped per Arturo: Reference was never
              actually captured by hand (he wants it derived from
              linked invoices instead, a separate follow-up — not
              implemented here) and Source Shipment was pure internal
              plumbing (SHP-#### id) the new Edit button already
              replaces. isNew keeps the old Lot picker — there's no
              shipment linked yet to redirect to. */}
          {isNew ? (
          <SectionBox id="shipmentInfo" title={t.shipmentInfo} open={open} toggle={toggle}>
            <div style={{ padding: "12px 20px 12px" }}>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.lot}</div>
              <SearchSelect value={f.lot || ""} onChange={prefillFromLot} placeholder={lang === "es" ? "Buscar lote de embarque..." : "Search shipment lot..."} options={availableShipmentLots} />
            </div>
          </SectionBox>
          ) : (function () {
            var shpForInfo = getShipments().find(function (s) { return String(s.lot) === String(f.lot); });
            var editShipmentFromForm = function () {
              window.__gfiBillReopenId = bill.id;
              window.__gfiBillReopenEditMode = true;
              var shipmentId = shpForInfo ? shpForInfo.id : f.shipmentId;
              if (window.__gfiNavigate) window.__gfiNavigate("shipments");
              setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 250);
              setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 900);
            };
            /* "me voy a dormir" — edit mode's equivalent of
               openLinkedSupplierFromBar above (view mode). */
            var openLinkedSupplierFromForm = function () {
              if (!f.supplierId) return;
              var supplierId = f.supplierId;
              window.__gfiBillReopenId = bill.id;
              window.__gfiBillReopenEditMode = true;
              if (window.__gfiNavigate) window.__gfiNavigate("suppliers");
              setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 250);
              setTimeout(function () { window.__gfiReturnModule = "bills"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 900);
            };
            return (
              <div style={{ marginBottom: 18 }}>
                <div style={{ ...readOnlyPlateBodyStyle }}>
                  <div style={{ ...sectionHeaderStyle, background: "none", border: "none", boxShadow: "none", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ flex: 1 }}>{t.shipmentInfo}</span>
                    <button type="button" onClick={editShipmentFromForm} title={lang === "es" ? "Editar en el Embarque" : "Edit in the Shipment"} style={{ padding: "2px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{t.editBtn}</button>
                  </div>
                  <div style={{ padding: "0 14px 14px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 18px", fontSize: 12 }}>
                      {[[t.billDate, fmtDate(f.billDate)], [t.dueDate, fmtDate(f.dueDate)]].map(function (arr, i) {
                        return <div key={i}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
                      })}
                      <div>
                        <span style={readOnlyPlateLabelStyle}>{t.shipper}</span>
                        {f.supplierId ? (
                          <div onClick={openLinkedSupplierFromForm} title={lang === "es" ? "Abrir proveedor" : "Open supplier"} style={{ ...readOnlyPlateValueStyle, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{f.shipperName || "—"}</div>
                        ) : (
                          <div style={readOnlyPlateValueStyle}>{f.shipperName || "—"}</div>
                        )}
                      </div>
                      {[[t.proforma, f.proforma, true], [t.shipperInvoice, f.shipperInvoice, true], [t.entryNumber, f.entryNumber, true]].map(function (arr, i) {
                        return <div key={i} style={arr[3] ? { gridColumn: "1 / -1" } : undefined}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div title={arr[3] ? (arr[1] || "") : undefined} style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", ...(arr[3] ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>{arr[1] || "—"}</div></div>;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          </div>
          <div>
          <SectionBox id="fees" title={t.fees} open={open} toggle={toggle}>
          {/* COMMISSION */}
          {/* 2026-10-20, Arturo: "reduzcamos todos los fonts de fees &
              deductions, sin negritas... pero no cambien los colores
              aun" — sizes down, bold off, every color left exactly as
              it was (a separate pass handles color later). */}
          <div style={{ padding: "8px 20px", borderTop: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>{t.commission}:</span>
            <input type="number" min="0" max="100" step="0.5" value={f.commissionRate} onChange={function(e) { set("commissionRate", e.target.value); }} style={{ ...inp, width: 60, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: capBg(f.commissionRate) }} />
            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>% = {fmtMoney(totals.commission)}</span>
          </div>

          {/* ADVANCE FINANCING FEE — 2026-10-20, Arturo: "debe estar
              fuera de la lista y aparece por default debajo de sales
              comission, al lado el porcentaje y en seguida el monto
              justo como aparece sales commission" — same row layout as
              Commission right above, always visible (not an opt-in
              "+ Add Fee" row anymore). Still the same f.fees entry
              under the hood (see updateFinancingFee). */}
          {(function () {
            var financingFee = (f.fees || []).find(function (fe) { return fe.feeType === "Advance Financing Fee"; });
            var refAmount = financingFee ? (totals.grossSales * ((parseFloat(financingFee.pctRate) || 0) / 100)) : 0;
            return (
              <div style={{ padding: "8px 20px", borderTop: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>Advance Financing Fee:</span>
                <input type="number" min="0" max="100" step="0.1" value={financingFee ? (financingFee.pctRate || "") : ""} onChange={function (e) { updateFinancingFee(e.target.value); }} style={{ ...inp, width: 60, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: capBg(financingFee && financingFee.pctRate) }} placeholder="%" />
                <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>% = {fmtMoney(refAmount)}</span>
                <span style={{ fontSize: 10, color: "var(--color-text-secondary)", marginLeft: "auto" }}>{lang === "es" ? "Monto a cobrar:" : "Amount to charge:"}</span>
                <input type="number" min="0" step="0.01" value={financingFee ? financingFee.amount : ""} onChange={function (e) { updateFinancingFeeAmount(e.target.value); }} disabled={!financingFee} style={{ ...inp, width: 90, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600 }} />
              </div>
            );
          })()}

          {/* FEES — 2026-10-20, Arturo: "el header dentro de la tarjeta
              no el del titulo que se llama fees y deductions, cambialo
              a Operating Fees" (card title, t.fees, stays "Fees &
              Deductions") — and "los fees quiero capturarlos en una
              ventana que emerge... los campos ya no son editables,
              cada renglon con un boton de editar... x para eliminarlo
              y el drag al lado izquierdo": locked list, FeeEditDialog
              does the capture (see saveFeeDialog above). */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)" }}>{lang === "es" ? "Gastos Operativos" : "Operating Fees"}</div>
              {/* Bulk PT toggle survives the move away from a table-header
                  checkbox — same tri-state behavior, now a small button. */}
              {(f.fees || []).length > 1 && <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: "var(--color-text-muted)", cursor: "pointer" }} title={t.selectAllPT}>
                <input type="checkbox" checked={ptAllOn} ref={function(el) { if (el) el.indeterminate = ptMixed; }} onChange={function(e) { setAllFeesPT(e.target.checked); }} style={{ accentColor: "var(--color-accent)", cursor: "pointer" }} />
                {t.selectAllPT}
              </label>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <tbody>
                {/* idx stays the ORIGINAL f.fees index (never the
                    filtered/displayed position) so Edit/Remove/drag
                    keep pointing at the right entry. */}
                {(f.fees || []).map(function(fee, idx) { return { fee: fee, idx: idx }; }).filter(function(x) { return x.fee.feeType !== "Advance Financing Fee"; }).map(function(x) {
                  var fee = x.fee, idx = x.idx;
                  var rp = dndFees.rowProps(idx);
                  var name = fee.feeType === "__custom__" ? (fee.feeCustom || t.customFee) : fee.feeType;
                  var originLabel = resolveFeeOriginLabel(fee, allExpenseBills, lang);
                  /* 2026-10-20, Arturo: "quiero reducir el tamaño de esa
                     tarjeta de fees... debes mover 2/$24.00 a una
                     columna al lado" — Qty × Rate gets its own column
                     instead of a second stacked line under the name,
                     so a row with no PT/→Invoice/origin tag shrinks
                     back down to one line tall. */
                  var qtyRateLabel = parseInt(fee.qty, 10) > 1 ? fee.qty + " × " + fmtMoney(fee.rate) : "";
                  var detailBits = [];
                  if (fee.passThrough) detailBits.push("PT");
                  if (fee.transferToInvoice) detailBits.push((lang === "es" ? "Factura" : "Invoice") + (fee.transferTargetPU ? " PU# " + fee.transferTargetPU : ""));
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", opacity: fee.passThrough ? 0.6 : 1, ...(rp.style || {}) }}>
                    <td style={{ padding: "6px 0", textAlign: "center", width: 20 }}><DragHandle handleProps={dndFees.handleProps()} /></td>
                    <td style={{ padding: "6px 4px" }}>
                      <div style={{ fontSize: 11, color: name ? "var(--color-text)" : "var(--color-text-muted)", fontStyle: name ? "normal" : "italic" }}>{name || t.selectFee}</div>
                      {detailBits.length > 0 && <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 1 }}>{detailBits.join(" · ")}</div>}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", width: 60 }}>{qtyRateLabel}</td>
                    {/* 2026-10-20, Arturo: "from bill tambien crea una
                        columna" — origin tag gets its own column too,
                        same reasoning as Qty × Rate right above. */}
                    <td style={{ padding: "6px 4px", fontSize: 9, color: "#B45309", whiteSpace: "nowrap" }}>{originLabel || ""}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>{fmtMoney(fee.amount)}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap", width: 50 }}>
                      <button onClick={function() { setEditingFeeIdx(idx); }} title={t.editBtn} style={{ background: "none", border: "none", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✎"}</button>
                      <button onClick={function() { removeFee(idx); }} title={t.removeFee} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✕"}</button>
                    </td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr><td colSpan={2} style={{ padding: "8px 4px" }}><button onClick={function() { setEditingFeeIdx("new"); }} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addFee}</button></td><td></td><td></td><td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#DC2626" }}>({fmtMoney(totals.totalFees)})</td><td></td></tr></tfoot>
            </table>
          </div>
          {editingFeeIdx !== null && (
            <FeeEditDialog
              fee={editingFeeIdx === "new" ? null : f.fees[editingFeeIdx]}
              feeTypes={feeTypes}
              availableInvoices={availableInvoices}
              allExpenseBills={allExpenseBills}
              isTwinRiver={billIsTR}
              lot={f.lot}
              lang={lang}
              t={t}
              onSave={saveFeeDialog}
              onClose={function() { setEditingFeeIdx(null); }}
            />
          )}

          {/* CREDITS — favor the shipper, ADD to bill total. Each credit
              row optionally references another bill by lot (visual link
              only; the referenced bill is NOT modified). Same locked-
              list + popup-dialog capture as Fees. */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#059669", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.credits}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <tbody>
                {(f.credits || []).map(function(cr, idx) {
                  var rp = dndCredits.rowProps(idx);
                  var name = cr.feeType === "__custom__" ? (cr.feeCustom || t.customFee) : cr.feeType;
                  var refBits = [];
                  if (cr.refBillLot) refBits.push("LOT " + cr.refBillLot);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "6px 0", textAlign: "center", width: 20 }}><DragHandle handleProps={dndCredits.handleProps()} /></td>
                    <td style={{ padding: "6px 4px" }}>
                      <div style={{ fontSize: 11, color: name ? "var(--color-text)" : "var(--color-text-muted)", fontStyle: name ? "normal" : "italic" }}>{name || t.selectFee}</div>
                      {refBits.length > 0 && <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 1 }}>{refBits.join(" · ")}</div>}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>{fmtMoney(cr.amount)}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap", width: 50 }}>
                      <button onClick={function() { setEditingCreditIdx(idx); }} title={t.editBtn} style={{ background: "none", border: "none", color: "#059669", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✎"}</button>
                      <button onClick={function() { removeCredit(idx); }} title={t.removeFee} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✕"}</button>
                    </td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={2} style={{ padding: "8px 4px" }}><button onClick={function() { setEditingCreditIdx("new"); }} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#059669", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addCredit}</button></td>
                <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#059669" }}>{totals.totalCredits > 0 ? "+" + fmtMoney(totals.totalCredits) : ""}</td>
                <td></td>
              </tr></tfoot>
            </table>
          </div>
          {editingCreditIdx !== null && (
            <CreditEditDialog
              credit={editingCreditIdx === "new" ? null : (f.credits || [])[editingCreditIdx]}
              feeTypes={feeTypes}
              allBillsForCredit={allBillsForCredit}
              allBills={allBills}
              lang={lang}
              t={t}
              onSave={saveCreditDialog}
              onClose={function() { setEditingCreditIdx(null); }}
            />
          )}

          {/* BALANCE TRANSFERS — bidirectional cross-bill linkage. Each
              entry is mirrored on the other bill (atomic write to
              gfi-bills on create/remove). type "credit" adds; type
              "debit" subtracts. */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.balanceTransfers}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 9, color: "var(--color-text-secondary)", width: "20%" }}>{t.btType}</th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 9, color: "var(--color-text-secondary)", width: "57%" }}>{t.btOtherBill}</th>
                <th style={{ padding: "6px 3px", textAlign: "right", fontSize: 9, color: "var(--color-text-secondary)", width: "16%" }}>{t.btAmount}</th>
                <th style={{ width: "4%" }}></th>
              </tr></thead>
              <tbody>
                {(f.balanceTransfers || []).map(function(tr, idx) {
                  var rp = dndTransfers.rowProps(idx);
                  var isCredit = tr.type === "credit";
                  var tone = isCredit ? "#059669" : "#DC2626";
                  return <tr key={tr.id || idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndTransfers.handleProps()} /></td>
                    <td style={{ padding: "5px 3px", fontSize: 10, color: tone }}>{isCredit ? t.btCredit : t.btDebit}</td>
                    <td style={{ padding: "5px 3px", fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>LOT {tr.otherBillLot || "?"} · <span style={{ color: "var(--color-text-muted)" }}>{tr.otherShipperName || ""}</span> {tr.date && <span style={{ color: "var(--color-text-muted)", fontSize: 10 }}> · {tr.date}</span>}</td>
                    <td style={{ padding: "5px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: tone }}>{isCredit ? "+" : "−"}{fmtMoney(tr.amount)}</td>
                    <td style={{ padding: "5px 1px", textAlign: "center" }}><button onClick={function() { if (window.confirm(lang === "es" ? "¿Eliminar transferencia? Esto también la quita del otro bill." : "Remove transfer? This also removes it from the other bill.")) removeBalanceTransfer(idx); }} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12 }}>{"✕"}</button></td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={5} style={{ padding: "8px 4px" }}>
                  <BalanceTransferCreator
                    lang={lang} t={t} sameShipperBills={sameShipperBills}
                    suggestedAmount={Math.max(0, -totals.subtotal).toFixed(2)}
                    onCreate={createBalanceTransfer}
                  />
                </td>
              </tr></tfoot>
            </table>
          </div>
          </SectionBox>
          </div>
          <div>
          <SectionBox id="billSummary" title={lang === "es" ? "Resumen del Bill" : "Bill Summary"} open={open} toggle={toggle}>
            <div style={{ fontSize: 11, color: "var(--color-text)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.grossSales)}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {t.totalCommission} ({f.commissionRate || 0}%)</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(totals.commission)})</span></div>
              <FeeBreakdownLines bill={f} feeCategoryMap={feeCategoryMap} t={t} />
              {totals.totalCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#059669" }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.totalCredits)}</span></div>}
              {totals.totalTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: totals.totalTransfers >= 0 ? "#059669" : "#DC2626" }}><span>{totals.totalTransfers >= 0 ? "+" : "−"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{totals.totalTransfers >= 0 ? fmtMoney(totals.totalTransfers) : "(" + fmtMoney(-totals.totalTransfers) + ")"}</span></div>}
              <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.subtotal}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.subtotal)}</span></div>
              {parseFloat(f.amountPaid) > 0 && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, color: "#DC2626" }}>
                <span>− {t.lessPaid}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(f.amountPaid)})</span>
              </div>}
              <div style={{ borderTop: "2px solid var(--color-accent)", paddingTop: 8, marginTop: 8, display: "flex", justifyContent: "space-between", color: "var(--color-accent)" }}>
                <span>{t.amountDue}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totals.subtotal - (parseFloat(f.amountPaid) || 0))}</span>
              </div>
            </div>
          </SectionBox>
          </div>
          </div>

          <div style={{ flex: "1 1 62%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
          {/* PRODUCT LINES WITH SALE STATUS */}
          {/* 2026-10-20, Arturo: "tambien en la tarjeta product, que
              debes cambiar el header a PRODUCTS" — card title only,
              same as view mode. */}
          <SectionBox id="product" title={lang === "es" ? "Productos" : "Products"} open={open} toggle={toggle}>
          <div style={{ overflowX: "auto" }}>
            {/* 2026-10-20, Arturo: "se repite product, elimina el
                primero que no tiene ninguna funcion" — this was a
                redundant plain-text "PRODUCT" label duplicating the
                SectionBox plate title right above it. */}
            {/* 2026-10-20, Arturo: "esta muy a la derecha, necesito
                compactar las columnas para que se vea todo sin barra
                scroll" — dropped the fixed 900px floor that forced
                this table wider than its 62% column, so it shrinks to
                fit instead of scrolling. */}
            {/* 2026-10-20, Arturo: "los campos deben ser dinamicos, si un
                campo se amplia al seleccionar contenido, empuja las
                columnas para que todo sea visible" — auto layout instead
                of fixed: the declared widths below are still honored as
                a starting point, but a column (e.g. Result once a longer
                Settlement option is picked) can grow and push its
                neighbors rather than clipping its own content. */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "auto", marginTop: 8 }}>
              <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "2px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "20%" }}>{t.product}</th>
                {/* 2026-10-20, Arturo: "no dije que la quitaras, dije
                    que la movieras... agrega el header CAT" — stays a
                    real column (reverting the earlier merge-into-cell
                    attempt), just left-aligned with tight padding so
                    its content sits close (~5px) to the right edge of
                    Product's column instead of centered further out. */}
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "5%" }}>{t.category}</th>
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "6%" }}>{t.cases}</th>
                {/* Pallets — editable input so the user can reapportion
                    pallets when splitting a line (totals reduce over
                    .lines so they recompute automatically). */}
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "5%" }}>{t.pallets}</th>
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "6%" }}>{lang === "es" ? "Precio" : "Price"}</th>
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "8%" }}>{lang === "es" ? "Venta" : "Sale"}</th>
                {/* New per-line commission inputs (Rate % + computed $).
                    Compresses adjacent columns slightly to make room. */}
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "6%" }} title={lang === "es" ? "Comisión por línea" : "Per-line commission"}>{t.lineComm}</th>
                <th style={{ padding: "2px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "#DC2626", whiteSpace: "nowrap", width: "8%" }}>{t.lineCommAmt}</th>
                <th style={{ padding: "2px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "9%" }}>{lang === "es" ? "Llegada" : "Arrival"}</th>
                <th style={{ padding: "2px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "18%" }}>{lang === "es" ? "Resultado" : "Result"}</th>
                <th style={{ padding: "2px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", whiteSpace: "nowrap", width: "6%" }}></th>
              </tr></thead>
              <tbody>{(f.lines || []).map(function(line, idx) {
                var lineAmt = (parseInt(line.cases, 10) || 0) * (parseFloat(line.reportedPrice) || 0);
                var isLoss = line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer";
                var rp = dndLines.rowProps(idx);
                return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", opacity: isLoss ? 0.6 : 1, ...(rp.style || {}) }}>
                  <td style={{ padding: "1px 0", textAlign: "center" }}><DragHandle handleProps={dndLines.handleProps()} /></td>
                  <td style={{ padding: "1px 4px" }}>
                    {/* Edit-mode mirror of the detail-view cell — full
                        presentation as the primary label, commodity-
                        only fallback when productDescription is empty,
                        catalog-lookup secondary suppressed in the
                        productDescription-present case. */}
                    <div style={{ fontSize: 11, color: "var(--color-text)" }}>{line.productDescription || line.productName || line.productBase || line.product || "—"}</div>
                    {!line.productDescription && resolveProductDesc(line) && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 1 }}>{resolveProductDesc(line)}</div>}
                    {/* 2026-10-20, Arturo: "export certificate se emite
                        por commodity... no debe convivir en shipment
                        info sino en products... al ser un dato muy
                        ancho no puedo agregar una columna al final" —
                        compact per-line note instead of its own wide
                        column (was a narrow but still space-costing
                        13% column before this). */}
                    {/* 2026-10-20, Arturo: "ya se desbordo el export
                        certificate a 2 renglones, quita export y solo
                        deja Cert: para que se ajuste a un renglon" —
                        Product narrowed (pushed-left ask above) left
                        less room for this line; "Export Cert:" wraps,
                        "Cert:" doesn't. */}
                    {(line.exportCertNumber || line.exportCertManual) && <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={line.exportCertNumber || line.exportCertManual}>Cert: {line.exportCertNumber || line.exportCertManual}</div>}
                  </td>
                  <td style={{ padding: "1px 3px", textAlign: "center" }}>{line.category && <span style={{ padding: "2px 5px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: line.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: line.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{line.category}</span>}</td>
                  {/* 2026-10-20, Arturo: "el campo cases no se ajusto al
                      ancho... dejalo por default de 8 caracteres" — a
                      per-keystroke ch-width tied to value.length clipped
                      the last digit or two (didn't account for the
                      input's own padding/border in the ch math); a
                      fixed 11ch (~8 visible digits + the padding/border
                      chrome) comfortably fits without the per-keystroke
                      recalculation, same fixed width for all 4 numeric
                      inputs on this row (Cases/Pallets/Price/Comm %). */}
                  <td style={{ padding: "1px 3px" }}><input className="gfi-num-input" type="number" min="0" step="1" value={line.cases || ""} onChange={function(e) { updateLineCases(idx, e.target.value); }} style={{ ...inp, padding: "1px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: capBg(line.cases), width: "12ch" }} /></td>
                  {/* 2026-10-20, Arturo: "pallets reducelo, el numero
                      mayor que puedo utilizar ahi es 30" — narrower
                      (6ch: 2 visible digits + the same chrome), since
                      pallets never realistically needs more than 2
                      digits. */}
                  <td style={{ padding: "1px 3px" }}><input className="gfi-num-input" type="number" min="0" step="1" value={line.pallets || ""} onChange={function(e) { updateLinePallets(idx, e.target.value); }} style={{ ...inp, padding: "1px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: capBg(line.pallets), width: "6ch" }} /></td>
                  {/* 2026-10-20, Arturo: "price dejalo en 6 caracteres de
                      ancho" — narrower than the other 3 (9ch: 6 visible
                      digits + the same padding/border chrome). */}
                  <td style={{ padding: "1px 3px" }}><input className="gfi-num-input" type="number" min="0" step="0.01" value={line.reportedPrice || ""} onChange={function(e) { updateLinePrice(idx, e.target.value); }} onBlur={function(e) { if (e.target.value !== "") updateLinePrice(idx, parseFloat(e.target.value).toFixed(2)); }} style={{ ...inp, padding: "1px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: capBg(line.reportedPrice), width: "9ch" }} /></td>
                  <td style={{ padding: "1px 3px", textAlign: "right", fontSize: 10, color: isLoss ? "#DC2626" : "var(--color-text)" }}>{isLoss ? "$0.00" : fmtMoney(lineAmt)}</td>
                  {/* Per-line Comm % editable + Comm $ readout. The
                      $ readout reflects the current rate × line amount,
                      respecting the dumped/donated/samples exclusion. */}
                  {/* 2026-10-20, Arturo: "comm% el numero maximo es 10%"
                      — narrower (7ch: up to ~4 visible chars like "9.5"
                      + the same chrome), max="100" left unchanged in
                      case a one-off exception is ever needed, but the
                      box itself is sized for the realistic range. */}
                  <td style={{ padding: "1px 3px" }}><input className="gfi-num-input" type="number" min="0" max="100" step="0.5" value={line.commissionRate === undefined || line.commissionRate === null ? "" : line.commissionRate} onChange={function(e) { updateLineCommission(idx, e.target.value); }} style={{ ...inp, padding: "1px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: capBg(line.commissionRate === undefined || line.commissionRate === null ? "" : line.commissionRate), width: "7ch" }} /></td>
                  <td style={{ padding: "1px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#DC2626" }}>{(function() { var lc = lineCommissionAmount(line, f.commissionRate); return lc > 0 ? "(" + fmtMoney(lc) + ")" : "—"; })()}</td>
                  {(function() {
                      /* 2026-09-12 — Bill is a mirror of its linked
                         invoice(s), never the place status is set (Arturo:
                         "el cliente informa todos los status", and that
                         only happens on the Invoice) — ALWAYS read-only
                         here, computed delivery statuses and the 4 manual
                         dispositions alike. See handleSave's recompute. */
                      var shownStatus = line.saleStatus || resolvePendingStage(findInvoicesByLot(f.lot, freshInvoicesLS()), slPassingIndex);
                      var sc = SALE_STATUS_COLORS[shownStatus];
                      /* 2026-10-20, Arturo: "vas a mover protection al
                         lado y eliminar note" — badge moves beside the
                         Settlement dropdown (was stacked above it) in
                         one row; the free-text Note input is dropped.
                         "arriba de settlement agrega header Result" —
                         Arrival/Result now their own columns so each
                         header lines up over its own content. */
                      return (
                        <>
                          <td style={{ padding: "1px 3px" }}>
                            {sc
                              ? <span title={line.saleStatus ? t.computedFromPallets : ""} style={{ display: "inline-block", padding: "1px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, cursor: line.saleStatus ? "help" : "default", whiteSpace: "nowrap" }}>{t[shownStatus] || shownStatus}</span>
                              : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}
                          </td>
                          <td style={{ padding: "1px 3px" }}>
                            <SearchSelect value={line.settlement || ""} onChange={v => updateLineSettlement(idx, v)} placeholder={lang === "es" ? "Liquidación..." : "Settlement..."} options={[{ value: "paidInFull", label: t.paidInFull }, { value: "settled", label: t.settled }]} />
                          </td>
                        </>
                      );
                    })()}
                  <td style={{ padding: "1px 2px" }}>
                    {/* 2026-10-20, Arturo: "remieve sus cuadros, alinealos
                        vertical en el extremo derecho para que el drop
                        de sale status se amplie" — no more bordered
                        button boxes, stacked vertically instead of in a
                        row, so this column can shrink and Status/
                        Settlement gets the freed width. */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end" }}>
                      <button type="button" onClick={function() { addLineCopy(idx); }} title={lang === "es" ? "Agregar línea del mismo producto" : "Add row (same product)"} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"+"}</button>
                      <button type="button" onClick={function() { removeLine(idx); }} title={lang === "es" ? "Eliminar línea" : "Remove line"} style={{ padding: 0, border: "none", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"×"}</button>
                      <button type="button" onClick={function() { splitLine(idx); }} title={lang === "es" ? "Dividir línea" : "Split line"} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"⇆"}</button>
                    </div>
                  </td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          </SectionBox>
          </div>
          <div>
          {/* PALLET DETAIL — deep copy of the shipment's pallet manifest,
              snapshotted when the bill is created from a shipment/lot
              (1A). No Lot column — a bill is always a single lot.
              Editable per row (the shipper may have billed pallets
              differently than the manifest); no over-allocation
              validation here, this represents what the shipper charged. */}
          <SectionBox id="pallets" title={t.palletDetail} open={open} toggle={toggle} headerExtra={
            /* 2026-11-02, Arturo (PO 7230, lote 58084: capturó el
               manifiesto del Embarque DESPUÉS de que el Bill ya se
               creó, mostrando 3 filas en blanco): "no existe eso que
               me dices" — the "+ Traer del embarque" button
               (populateBillPalletDetail) only ever rendered when
               f.palletDetail was COMPLETELY empty; a Bill created
               before the shipment's manifest had pallet#/cases
               already has 3+ (blank) rows, so that fallback button
               never showed — the only way to recover was typing each
               pallet # and case count by hand into these now-editable
               cells (2026-10-30 fix). Always-visible refresh icon
               instead, so re-syncing from a shipment corrected after
               the Bill was created never again requires hand-typing
               or a direct DB edit — confirms first since it discards
               any pallet detail already captured/edited here. */
            !!(f.shipmentId || f.lot) && (
              <button type="button" title={t.populateFromShipment} onClick={function (e) {
                e.stopPropagation();
                var msg = lang === "es" ? "¿Traer de nuevo el manifiesto de pallets del embarque? Esto reemplaza lo ya capturado aquí." : "Re-pull the pallet manifest from the shipment? This replaces what's already captured here.";
                if (window.confirm(msg)) populateBillPalletDetail();
              }} style={{ padding: "2px 8px", border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600, borderRadius: 4 }}>{"↻ " + t.populateFromShipment}</button>
            )
          }>
            {/* Lot-level Regression total (2026-10-16, Arturo: "la
               regresion que capture en el embarque global... donde se
               va a visualizar en el bill?") — unlike the per-pallet
               defects above (which DO live on each pallet row and
               replicate via deepCopyPalletManifest), Regression for
               ClariFresh/Blackberry is a single figure captured once
               on the SHIPMENT itself (f.lotRegressionPct, not part of
               palletManifest), so it never had anywhere to show up on
               the Bill. Read live from the shipment (never stored on
               the Bill) — same "always live, never a stale snapshot"
               principle as the Tag ID/lost-cases/defects fields right
               below, and the exact same read-only badge style/copy as
               the shipment's own view-mode display. */}
            {(function () {
              var shpForRegression = getShipments().find(function (s) { return String(s.lot) === String(f.lot); });
              if (!shpForRegression || shpForRegression.texasQcMode !== "clarifresh") return null;
              var pct = shpForRegression.lotRegressionPct;
              if (pct === undefined || pct === null || pct === "") return null;
              return (
                <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", fontSize: 11, color: "#B45309" }}>
                  {t.lotRegressionLabel + " " + pct + "%"}
                </div>
              );
            })()}
            {(f.palletDetail || []).length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", marginBottom: 10 }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "5%" }}>{t.palletNumCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "7%" }}>{t.tagIdCol}</th>
                  <th title={t.actualShipperInitialsHint} style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "6%" }}>{t.actualShipperInitialsCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "24%" }}>{t.product}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "12%" }}>{t.varietyCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.cases}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.weightCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "18%" }}>{t.palletStatusCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "8%" }}>{t.casesLostCol}</th>
                </tr></thead>
                <tbody>
                {/* Case-loss note source (2026-09-21) — resolved from
                   the LIVE shipment, not from `p` (bill.palletDetail),
                   because palletDetail is a one-time deep copy taken
                   whenever the lot was picked (see deepCopyPalletManifest
                   above) — a Bill created before the loss was noted on
                   the shipment keeps a stale copy forever, and (unlike
                   an empty palletDetail) "Populate from shipment" never
                   shows once palletDetail already has rows, so there's
                   no way to manually refresh it either. 2026-09-21,
                   Arturo: "no traslado el dato de cajas perdidad, la
                   columna si esta pero por ejemplo embarque 58050 con
                   24 cajas perdidas en el shipment, el bill no las
                   muestra" — mirrors the pallet-status column right
                   next to it, which already reads live instead of
                   trusting a stored value, same principle. Only the
                   loss fields are read live here; casesPallet etc.
                   stay exactly as stored on the Bill (the shipper may
                   have billed differently than the manifest). */}
                {(function () {
                  var shpForLiveLookup = getShipments().find(function (s) { return String(s.lot) === String(f.lot); });
                  var palletsByNumFromShipment = {};
                  ((shpForLiveLookup && shpForLiveLookup.palletManifest) || []).forEach(function (pm) {
                    if (pm && pm.palletNum) palletsByNumFromShipment[String(pm.palletNum)] = pm;
                  });
                  return f.palletDetail.map(function(p, idx) {
                  var w = docPalletWeight(p);
                  /* 2026-09-12 — read-only, pulled live from whichever
                     invoice(s) currently allocate this exact pallet
                     (see saleStatusCalc.js derivePalletStatus). Bills
                     have no pallet-level status of their own to set —
                     that only happens on the Invoice, per Arturo. */
                  var allInvoicesForPallet = freshInvoicesLS();
                  /* Twin River (2026-10-15) — ClariFresh shipments
                     report Protection through the shipment's own
                     tolerance sheet, not the Invoice — see
                     saleStatusCalc.js's twinRiverPalletStatus. */
                  var pStatus = twinRiverPalletStatus(shpForLiveLookup, p.palletNum) || derivePalletStatus(f.lot, p.palletNum, allInvoicesForPallet) || resolvePendingStage(invoicesForPallet(f.lot, p.palletNum, allInvoicesForPallet), slPassingIndex);
                  var pSc = SALE_STATUS_COLORS[pStatus];
                  var livePallet = palletsByNumFromShipment[String(p.palletNum)] || {};
                  /* Tolerance-sheet defects (2026-10-15) — Arturo:
                     "esto hay que hacerlo solo visible en cada pallet
                     igual, debajo en el bill del embarque... que se
                     propague" — same read-only, one-rectangle-per-
                     category treatment as ShipmentRegistry.js's
                     locked edit row and its view-mode DetailPanel sub-
                     row, now mirrored here. Always read live from the
                     shipment (livePallet), never from the Bill's own
                     one-time palletDetail snapshot — same reasoning
                     as Tag ID/lost cases just above: Texas QC can be
                     captured or corrected on the shipment after this
                     Bill was created. */
                  var qc = livePallet.qcDefects || {};
                  var qcCatsPresent = ["severeCondition", "condition", "quality", "findings"].filter(function (cat) {
                    return Array.isArray(qc[cat]) && qc[cat].length > 0;
                  });
                  /* 2026-10-20, Arturo: "ya me eliminaste el product
                     description de pallet manifest" — this edit-mode
                     table only ever showed the bare productBase
                     ("Blackberry"), never got the same full auto-
                     generated description view mode's own Pallet
                     Manifest already has (2026-10-15, "la descripcion
                     de producto cambia blackberry por la descripcion
                     completa") — same logic ported verbatim, same
                     live-shipment-over-stale-bill-copy precedence. */
                  var descSrc = livePallet.productBase ? livePallet : p;
                  var pDescParts = [
                    descSrc.productBase,
                    (descSrc.unitsCase && descSrc.size) ? (descSrc.unitsCase + "/" + descSrc.size) : "",
                    descSrc.category,
                    descSrc.labelInitials ? (descSrc.labelInitials + " Label") : "",
                  ].filter(Boolean);
                  var pDesc = pDescParts.join(" ") || p.productBase || "—";
                  var mainRow = (
                  <tr key={idx} style={{ borderBottom: qcCatsPresent.length > 0 ? "none" : "1px solid var(--color-border)" }}>
                    {/* 2026-10-30, Arturo: "los pallets en el bill se
                       llenan automaticamente pero debiera existir la
                       opcion de editar para corregir estos detalles
                       manualmente y no requerir codigo" — Pallet # (and
                       Tag ID, right below) are the 2 fields every live
                       lookup above matches against the shipment by;
                       when the copied-in snapshot drifts out of sync
                       with a pallet number corrected later on the
                       shipment (lot 58074's Blueberry pallet, 090 vs
                       005), status/qcDefects/lostCases/description all
                       silently come back empty with no way to fix it
                       here short of editing the database directly.
                       Now a normal input, same pattern as Cases below. */}
                    <td style={{ padding: "6px 3px" }}><input type="text" value={p.palletNum || ""} onChange={function(e) { updatePalletDetailField(idx, "palletNum", e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    {/* Tag ID — same live-from-shipment fallback as the
                       loss note below (2026-09-21, Arturo: "ese mismo
                       bill, no esta mostrando el warehouse tag id"):
                       the stored bill.palletDetail copy for this real
                       bill (BILL-0148/lot 58050) had every tagId blank,
                       confirming it's the same stale-snapshot issue,
                       not specific to loss data. Prefers the bill's own
                       stored value first (never overrides an edit made
                       directly on the Bill), falls back to the live
                       shipment only when the Bill's copy is empty. */}
                    <td style={{ padding: "6px 3px" }}><input type="text" value={p.tagId || ""} placeholder={livePallet.tagId || ""} onChange={function(e) { updatePalletDetailField(idx, "tagId", e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }} /></td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.actualShipperInitials || livePallet.actualShipperInitials || (shpForLiveLookup && shpForLiveLookup.shipperInitials) || "—"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={pDesc}>{pDesc}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)" }}>{p.variety || "—"}</td>
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="1" value={p.casesPallet === undefined || p.casesPallet === null ? "" : p.casesPallet} onChange={function(e) { updatePalletDetailCases(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{w.lb > 0 ? w.lb.toFixed(1) + " lb" : "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>{pSc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: pSc.bg, color: pSc.text, border: "1px solid " + pSc.border, boxShadow: SALE_STATUS_BEVEL }}>{t[pStatus] || pStatus}</span> : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                    {/* Case-loss note (2026-09-21) — always read from
                       `livePallet` (the shipment, resolved above), never
                       from `p.lostCases` — see the comment above this
                       IIFE for why the stored bill.palletDetail copy
                       can't be trusted here. */}
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>{livePallet.lostCases ? <span title={livePallet.lossReason === "other" ? (livePallet.lossReasonCustom || "") : ""} style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: "rgba(217,119,6,0.12)", color: "#B45309", border: "1px solid rgba(217,119,6,0.3)" }}>{livePallet.lostCases}{livePallet.lossReason ? " · " + (t["lossReason" + livePallet.lossReason.charAt(0).toUpperCase() + livePallet.lossReason.slice(1)] || livePallet.lossReason) : ""}</span> : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                  </tr>
                  );
                  var defectsSubRow = qcCatsPresent.length > 0 ? (
                    <tr key={idx + "-defects"} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td colSpan={9} style={{ padding: "2px 6px 6px" }}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.qcDefects}</span>
                          {qcCatsPresent.map(function (cat) {
                            var entries = qc[cat];
                            /* 2026-10-15, Arturo (GFI Team/internal
                               mode): per-defect severity coloring, now
                               for both real categories + running total,
                               same exception as the list view above. */
                            if ((cat === "quality" || cat === "condition") && shpForLiveLookup && shpForLiveLookup.texasQcMode !== "clarifresh") {
                              var billDetailCatTotal = entries.reduce(function (s, e) { var n = parseFloat(e.pct); return s + (n >= 0 ? n : 0); }, 0);
                              return (
                                <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}:</span>
                                  {entries.map(function (e, ei) {
                                    var lbl = internalDefectLabel(e, t);
                                    var pct = e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "";
                                    var dc = internalQualityDefectColor(e.key);
                                    return (
                                      <span key={e.key + ei} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 600, background: dc.bg, color: dc.text, border: "1px solid " + dc.border }}>{lbl + pct}</span>
                                    );
                                  })}
                                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-secondary)" }}>{"(" + t.qcDefectTotal + ": " + billDetailCatTotal.toFixed(2) + "%)"}</span>
                                </span>
                              );
                            }
                            var c = TOLERANCE_CATEGORY_COLORS[cat];
                            var summaryText = entries.map(function (e) {
                              var lbl = e.key === "__custom__" ? (e.custom || t.qcTolCustom) : t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
                              return lbl + (e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "");
                            }).join(", ");
                            return (
                              <span key={cat} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 600, background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}</span>
                                {summaryText && (": " + summaryText)}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ) : null;
                  return defectsSubRow ? [mainRow, defectsSubRow] : mainRow;
                  });
                })()}</tbody>
              </table>
            ) : (
              <button type="button" onClick={populateBillPalletDetail} disabled={!f.shipmentId && !f.lot} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: (f.shipmentId || f.lot) ? "var(--color-accent)" : "var(--color-text-muted)", cursor: (f.shipmentId || f.lot) ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 600, marginBottom: 10 }}>+ {t.populateFromShipment}</button>
            )}
          </SectionBox>
          {/* 2026-10-20, Arturo: "payments lo cambiamos de lugar debajo
              de pallet manifest y esta nueva seccion [Bill Summary]
              donde esta ahora payments" — same move as view mode:
              Payments stacks below Pallet Detail in this same column
              instead of pairing with My Profit further down. */}
          <SectionBox id="payments" title={t.paymentsSection} open={open} toggle={toggle}>
            <PaymentsSection payments={f.payments} total={totals.subtotal} t={t} bare />
          </SectionBox>
          </div>
          <div>
          {/* OWNER PROFIT SUMMARY — reads calcLotProfit(bill.lot)
              verbatim, same as DetailPanel's own "My Profit" (zero
              calculation of its own here either). Uses the saved
              `bill.lot`, not `f`, since lot isn't edited in this
              form. "al final my profit" — last section before Notes. */}
          {!isNew && bill.lot && (function () {
            var p = calcLotProfit(bill.lot);
            var clr = function(v) { return v < 0 ? "#DC2626" : "var(--color-text)"; };
            var fm = function(n) { var v = parseFloat(n) || 0; return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
            return (
              <SectionBox id="profit" title={lang === "es" ? "Mi Ganancia" : "My Profit"} open={open} toggle={toggle}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, fontSize: 11 }}>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.priceDiff) }}>{fm(p.priceDiff)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Price Diff</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.commission) }}>{fm(p.commission)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Commission</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.feeMarkup) }}>{fm(p.feeMarkup)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Fee Markup</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.finance) }}>{fm(p.finance)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Finance</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: clr(p.total) }}>{fm(p.total)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)" }}>Total ({fm(p.perBox)}/box)</div></div>
                </div>
              </SectionBox>
            );
          })()}
          </div>
          </div>
          </div>

          {/* NOTES */}
          <div style={{ padding: "6px 20px 12px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 3, textTransform: "uppercase" }}>{t.notes}</div>
            <textarea value={f.notes || ""} onChange={function(e) { set("notes", e.target.value); }} rows={2} style={{ ...inp, resize: "vertical", fontSize: 11 }} />
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {!isNew && !showDel && <button onClick={function() { setShowDel(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}
            {showDel && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmDel}</span><button onClick={function() { onDelete(bill.id); }} style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button><button onClick={function() { setShowDel(false); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>{t.no}</button></div>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={tryCancel} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.cancel}</button>
            <button onClick={handleSave} style={{ padding: "7px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.save}</button>
          </div>
        </div>

        {showExitConfirm && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}><div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 300, textAlign: "center" }}><p style={{ fontSize: 13, marginBottom: 14 }}>{t.confirmExit}</p><div style={{ display: "flex", gap: 8, justifyContent: "center" }}><button onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button><button onClick={function() { setShowExitConfirm(false); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button></div></div></div>}
        {previewInvoiceEdit && <InvoicePreviewModal invoice={previewInvoiceEdit} lang={lang} onClose={function () { setPreviewInvoiceEdit(null); }} />}
    </>
  );
  if (embedded) return body;
  return (
    <DraggableModal title={(isNew ? t.add : t.edit) + (f.lot ? "  ·  LOT " + f.lot : "")} onClose={tryCancel} initialWidth={820} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {body}
    </DraggableModal>
  );
}

/* ======================== BATCH PAYMENT MODAL ======================== */
var PAYMENT_METHODS = ["wire", "check", "ach", "transfer", "cash", "other"];

function buildBillRef(bill) {
  var parts = [];
  if (bill.lot) parts.push("LOT " + bill.lot);
  if (bill.proforma) parts.push(bill.proforma);
  if (bill.shipperInvoice) parts.push(bill.shipperInvoice);
  return parts.join(" · ");
}

function todayISO() { return new Date().toISOString().substring(0, 10); }

function nextPaymentId(items) {
  var nums = (items || []).map(function (i) { return parseInt((i.id || "").replace("PAY-", ""), 10); }).filter(function (n) { return !isNaN(n); });
  return "PAY-" + String(Math.max(0, ...nums) + 1).padStart(4, "0");
}

function BatchPaymentModal({ bills, t, lang, onConfirm, onCancel }) {
  /* Compute due per bill */
  var initRows = useMemo(function () {
    return bills.map(function (b) {
      var totals = calcBillTotals(b);
      var paid = parseFloat(b.amountPaid) || 0;
      var due = +(totals.subtotal - paid).toFixed(2);
      return { bill: b, subtotal: totals.subtotal, paid: paid, due: due, amountStr: due > 0 ? due.toFixed(2) : "", concept: "" };
    });
  }, [bills]);

  var [rows, setRows] = useState(initRows);
  var [paymentDate, setPaymentDate] = useState(todayISO());
  var [paymentMethod, setPaymentMethod] = useState("wire");
  var [reference, setReference] = useState("");
  var [notes, setNotes] = useState("");
  var [error, setError] = useState("");
  var [sortCol, setSortCol] = useState("ref"); /* ref | shipper | dueDate | amount */
  var [sortDir, setSortDir] = useState("asc");
  /* 2026-09-15 — Arturo: "también pago shippers deben tener ambas
     fechas" — same Processing Date / Delivery Date pair already added
     to ExpenseBillRegistry.js's Batch Payment, mirrored here so
     shipper payments carry them too. */
  var [processingDate, setProcessingDate] = useState(todayISO());
  var [deliveryDate, setDeliveryDate] = useState("");

  var openRows = useMemo(function () { return rows.filter(function (r) { return r.due > 0.01; }); }, [rows]);
  var total = useMemo(function () { return openRows.reduce(function (s, r) { return s + (parseFloat(r.amountStr) || 0); }, 0); }, [openRows]);

  /* Sorted-for-display rows, hoisted out of the render IIFE below so
     handleConfirm can also use it — otherwise the saved allocations
     array (and the printed Payment Confirmation PDF, which just walks
     it in array order) never matches whatever order Arturo arranged
     on screen with the sortable headers. 2026-09-18, Arturo: "el pdf
     que se imprime debe mantener el orden que se tiene en la ventana
     de pagos" (reported against the SweetLeaf equivalent of this
     modal, same bug class here). */
  var sortedOpenRows = useMemo(function () {
    var sortKeyRef = function (r) {
      var n = parseInt(r.bill.lot, 10);
      if (!isNaN(n)) return { kind: 0, n: n, s: "" };
      return { kind: 1, n: 0, s: String(buildBillRef(r.bill) || r.bill.id || "") };
    };
    var compare = function (a, b) {
      var cmp;
      if (sortCol === "shipper") {
        cmp = String(resolveShipperName(a.bill.supplierId) || a.bill.shipperName || "")
          .localeCompare(String(resolveShipperName(b.bill.supplierId) || b.bill.shipperName || ""));
      } else if (sortCol === "dueDate") {
        cmp = String(a.bill.dueDate || "").localeCompare(String(b.bill.dueDate || ""));
      } else if (sortCol === "amount") {
        cmp = (parseFloat(a.amountStr) || 0) - (parseFloat(b.amountStr) || 0);
      } else {
        var ka = sortKeyRef(a), kb = sortKeyRef(b);
        cmp = ka.kind - kb.kind;
        if (cmp === 0) cmp = ka.kind === 0 ? (ka.n - kb.n) : ka.s.localeCompare(kb.s);
      }
      return sortDir === "asc" ? cmp : -cmp;
    };
    return openRows.slice().sort(compare);
  }, [openRows, sortCol, sortDir]);

  /* Amount as RAW STRING during typing — no parseFloat/toLocaleString
     per keystroke, otherwise the cursor jumps past decimals and typing
     $5,000.00 takes 10s. Normalize to 2 decimals only on blur. Same
     pattern as PaymentRegistry.EditPaymentModal. */
  var setRowAmountStr = function (billId, raw) {
    setRows(function (prev) { return prev.map(function (r) { return r.bill.id === billId ? { ...r, amountStr: raw } : r; }); });
    setError("");
  };
  var setRowConcept = function (billId, val) {
    setRows(function (prev) { return prev.map(function (r) { return r.bill.id === billId ? { ...r, concept: val } : r; }); });
  };
  var normalizeRowAmount = function (billId) {
    setRows(function (prev) { return prev.map(function (r) {
      if (r.bill.id !== billId) return r;
      var raw = r.amountStr;
      if (raw === undefined || raw === null || String(raw).trim() === "") return { ...r, amountStr: "" };
      var n = parseFloat(raw);
      if (!isFinite(n)) return r;
      return { ...r, amountStr: n.toFixed(2) };
    }); });
  };

  var validate = function () {
    if (!paymentDate) return lang === "es" ? "Selecciona una fecha" : "Select a date";
    if (!processingDate) return lang === "es" ? "Selecciona la fecha de procesamiento" : "Select the processing date";
    if (!deliveryDate) return lang === "es" ? "Selecciona la fecha de entrega" : "Select the delivery date";
    for (var i = 0; i < openRows.length; i++) {
      var r = openRows[i];
      var amt = parseFloat(r.amountStr) || 0;
      if (amt < 0) return t.overApply + " (" + r.bill.id + ")";
      if (amt > r.due + 0.01) return t.overApply + " (" + r.bill.id + ")";
    }
    if (total <= 0) return lang === "es" ? "Captura al menos un monto > 0" : "Enter at least one amount > 0";
    return null;
  };

  var handleConfirm = function () {
    var err = validate();
    if (err) { setError(err); return; }
    var allocations = sortedOpenRows
      .filter(function (r) { return (parseFloat(r.amountStr) || 0) > 0; })
      .map(function (r) { return { billId: r.bill.id, lot: r.bill.lot, shipperName: r.bill.shipperName || "", amount: +(parseFloat(r.amountStr) || 0).toFixed(2), concept: r.concept || "" }; });
    onConfirm({
      paymentDate: paymentDate,
      processingDate: processingDate,
      deliveryDate: deliveryDate,
      paymentMethod: paymentMethod,
      reference: reference,
      notes: notes,
      allocations: allocations,
    });
  };

  var methodLabel = function (m) { return t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m; };

  return (
    <DraggableModal title={t.batchPaymentTitle} onClose={onCancel} initialWidth={920} initialHeight={680} zIndex={10005}>
        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          {/* Top fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentDate} *</label>
              <input type="date" value={paymentDate} onChange={function (e) { setPaymentDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentMethod}</label>
              <SearchSelect value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHODS.map(m => ({ value: m, label: methodLabel(m) }))} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.paymentReference}</label>
              <input type="text" value={reference} onChange={function (e) { setReference(e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="e.g. WIRE-2026-0123" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.processingDate} *</label>
              <input type="date" value={processingDate} onChange={function (e) { setProcessingDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.deliveryDate} *</label>
              <input type="date" value={deliveryDate} onChange={function (e) { setDeliveryDate(e.target.value); }} style={inp} />
            </div>
          </div>

          {/* Bills table */}
          {openRows.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, background: "var(--color-bg)", borderRadius: 10, border: "1px dashed var(--color-border)" }}>{t.noOpenBills}</div>
          ) : (
            <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden" }}>
              {(function () {
                var sortedRows = sortedOpenRows;
                var arrow = function (col) { return sortCol !== col ? " ⇅" : (sortDir === "asc" ? " ▲" : " ▼"); };
                var toggle = function (col) {
                  if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
                  else { setSortCol(col); setSortDir("asc"); }
                };
                var thStyle  = { padding: "8px 10px", textAlign: "left",   fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
                var thStyleC = { padding: "8px 10px", textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
                var thStyleR = { padding: "8px 10px", textAlign: "right",  fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" };
                return (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
                      <th style={thStyle}  onClick={function () { toggle("shipper"); }} title={lang === "es" ? "Ordenar por shipper" : "Sort by shipper"}>{t.shipper}{arrow("shipper")}</th>
                      <th style={thStyle}  onClick={function () { toggle("ref"); }}     title={lang === "es" ? "Ordenar por referencia" : "Sort by reference"}>{t.refColumn}{arrow("ref")}</th>
                      <th style={thStyleC} onClick={function () { toggle("dueDate"); }} title={lang === "es" ? "Ordenar por fecha de vencimiento" : "Sort by due date"}>{t.dueDate}{arrow("dueDate")}</th>
                      <th style={thStyleR}>{t.amountDue}</th>
                      <th style={thStyleR}>{t.paymentAmount}</th>
                      <th style={{ ...thStyleC, cursor: "default" }}>{t.concept}</th>
                    </tr></thead>
                    <tbody>{sortedRows.map(function (r) {
                      var b = r.bill;
                      return <tr key={b.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "10px", fontSize: 12, fontWeight: 600 }}>{resolveShipperName(b.supplierId) || b.shipperName || "—"}</td>
                        <td style={{ padding: "10px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{buildBillRef(b)}</td>
                        <td style={{ padding: "10px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(b.dueDate)}</td>
                        <td style={{ padding: "10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "var(--color-accent)" }}>{fmtMoney(r.due)}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right" }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={r.amountStr === undefined ? "" : r.amountStr}
                            onChange={function (e) { setRowAmountStr(b.id, e.target.value); }}
                            onBlur={function () { normalizeRowAmount(b.id); }}
                            style={{ ...inp, width: 130, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: "auto" }}
                          />
                        </td>
                        <td style={{ padding: "6px 10px", width: 130 }}>
                          <SearchSelect value={r.concept || ""} onChange={function (v) { setRowConcept(b.id, v); }} options={[{ value: "", label: t.conceptPayment }, { value: "advance", label: t.conceptAdvance }]} />
                        </td>
                      </tr>;
                    })}</tbody>
                  </table>
                );
              })()}
            </div>
          )}

          {/* Total + notes */}
          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.notes}</label>
              <textarea value={notes} onChange={function (e) { setNotes(e.target.value); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, alignSelf: "end" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 4 }}>{t.totalPayment}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "var(--color-accent)" }}>{fmtMoney(total)}</div>
            </div>
          </div>

          {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, color: "#DC2626", fontSize: 12, fontWeight: 600 }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
          <button onClick={handleConfirm} disabled={openRows.length === 0 || total <= 0} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: (openRows.length === 0 || total <= 0) ? "var(--color-border)" : "var(--color-accent)", color: "var(--color-accent-text)", cursor: (openRows.length === 0 || total <= 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{t.makePayments}</button>
        </div>
    </DraggableModal>
  );
}

/* ======================== MAIN REGISTRY ======================== */
export default function BillRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [bills, setBills] = useState([]);
  /* For the Pending Pick Up / Pending Delivery SALE-column display
     default on the list (see saleStatusCalc.js resolvePendingStage). */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* Force-fetch invoices into the Supabase in-memory cache (getCached
     ("invoices")) so this screen's live sale-status math (see
     freshInvoicesLS above) is accurate even if InvoiceRegistry.js was
     never opened in this tab to prime it. One-time fetch, re-render
     triggered via bumpStatusRefresh so the table/dialogs recompute
     once it lands. */
  var [, bumpStatusRefresh] = useState(0);
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("invoices").then(function () {
      if (!cancelled) bumpStatusRefresh(function (n) { return n + 1; });
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* 2026-10-23, Arturo: the internal "actual shipper" note he sets on
     a Shipment's pallet "no se esta replicando al bill" — same root
     cause as the invoices force-fetch right above (getShipments() now
     prefers getCached("shipments"), see its comment), just never had
     its own force-fetch, so in a tab where ShipmentRegistry.js was
     never opened this session, that cache stayed empty and Pallet
     Detail's live lookup fell back to a possibly-stale/empty
     localStorage mirror. Same one-time-fetch-then-rerender pattern. */
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("shipments").then(function () {
      if (!cancelled) bumpStatusRefresh(function (n) { return n + 1; });
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* 2026-09-27 (Arturo: "agregar tambien que pueda seleccionar mas de
     un filtro de un concepto, ejemplo si semanas quiero ver la 39 y la
     40, tener casillas de seleccion") — Shipper/Sale Status/Week are
     now multi-select arrays (same SearchSelect `multi` checkbox UI the
     Season filter already uses), not single strings. Empty array =
     no restriction, same convention as Season. */
  var [search, setSearch] = useState(""); var [filterShipper, setFilterShipper] = useState([]); var [filterStatus, setFilterStatus] = useState(""); var [filterSaleStatus, setFilterSaleStatus] = useState([]); var [filterCert, setFilterCert] = useState(""); var [filterWeek, setFilterWeek] = useState([]);
  /* Whether the filter popover above the table is open. The bar of
     filters that used to live always-visible now lives inside this
     popover (Xero-style "Filters" button on the header row). */
  var [showFilters, setShowFilters] = useState(false);
  var filtersRef = useRef(null);
  /* Brushed Steel redesign (2026-10-20 pattern, ported from
     ShipmentRegistry.js) — consolidates the document/export actions
     (Sales Report PDF / Email Sales Report / Export Excel) into one
     kebab menu, same anchored-popover pattern as showFilters above. */
  var [showMoreMenu, setShowMoreMenu] = useState(false);
  var moreMenuRef = useRef(null);
  /* Cert filter options — read once from gfi-export-certs. Option value
     is the cert id; label combines cert number + shipper for clarity. */
  var certFilterOptions = useMemo(function () {
    var raw = loadLS("gfi-export-certs", []);
    var seen = {};
    var arr = [];
    raw.forEach(function (c) {
      if (!c || seen[c.id]) return;
      seen[c.id] = true;
      arr.push({ value: c.id, label: (c.certNumber || c.id) + (c.shipperName ? " · " + c.shipperName : "") });
    });
    arr.sort(function (a, b) { return String(a.label).localeCompare(String(b.label)); });
    return arr;
  }, []);
  var certIdToNum = useMemo(function () {
    var m = {};
    loadLS("gfi-export-certs", []).forEach(function (c) { if (c && c.id) m[c.id] = nrmCertNum(c.certNumber); });
    return m;
  }, []);
  /* Season filter — multi-select grouped (Open / Closed) per CLAUDE.md
     "REGLA DE FILTRO DE SEASON". Default state pre-selects all Open
     seasons so the first render only shows current data. */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  var seasons = useMemo(function() { return loadLS("gfi-seasons", []); }, []);
  /* Resolves the season for a bill: bill.billDate is the primary key; for
     historical imports we also accept a stored seasonId. */
  var seasonNameForBill = useCallback(function(billDate, sid) {
    if (sid) { var byId = seasons.find(function(s) { return String(s.id) === String(sid); }); if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4); }
    if (!billDate) return "";
    var d = new Date(billDate + "T00:00:00");
    var s = seasons.find(function(s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  var [sortCol, setSortCol] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-bills-sort") || "null"); return (s && s.column) || "lot"; } catch { return "lot"; } });
  var [sortDir, setSortDir] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-bills-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(function() { try { localStorage.setItem("gfi-bills-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  /* Close the filter popover on outside click + Escape. */
  useEffect(function() {
    if (!showFilters) return;
    var onDown = function(e) { if (filtersRef.current && !filtersRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setShowFilters(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);
  /* Close the "⋮" more-actions menu on outside click + Escape — same
     pattern as the filter popover right above. */
  useEffect(function() {
    if (!showMoreMenu) return;
    var onDown = function(e) { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowMoreMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showMoreMenu]);
  var [viewing, setViewing] = useState(null); var [editing, setEditing] = useState(null); var [showForm, setShowForm] = useState(false);
  /* 2026-10-20, Arturo: Shipment Info's Edit button inside BillForm
     (edit mode) now does "lo mismo que en modo no edicion, va al
     embarque, se edita y regresa al bill en modo edicion" — same
     reopen mechanism as DetailPanel's own Edit button, but the Bill
     must come back in EDIT mode, not view mode. Consumed alongside
     window.__gfiBillReopenId below, threaded into DetailPanel as
     initialMode so the reopened panel mounts straight into edit. */
  var [reopenInEditMode, setReopenInEditMode] = useState(false);
  /* 2026-09-14 — Arturo: "cualquier cosa que abra desde el dashboard, al
     cerrar que me regrese al dashboard". window.__gfiReturnModule is set
     by Dashboard.js's navigateAndOpen/navigateAndCall right before it
     calls __gfiBillOpen/__gfiBillNew — captured here and consumed on the
     matching Close/Cancel/Save/Delete so a bill opened via a normal
     in-module click (which never touches this global) behaves exactly
     as before. */
  var returnModuleRef = useRef(null);
  var closeToOrigin = useCallback(function() {
    if (returnModuleRef.current && window.__gfiNavigate) window.__gfiNavigate(returnModuleRef.current);
    returnModuleRef.current = null;
  }, []);
  var [toast, setToast] = useState(null); var [loading, setLoading] = useState(true);
  var [selected, setSelected] = useState({});
  var [salesReportPreviewBill, setSalesReportPreviewBill] = useState(null);
  var [emailTargetBills, setEmailTargetBills] = useState(null);
  var [showBatchPay, setShowBatchPay] = useState(false);
  var [confirmationPayment, setConfirmationPayment] = useState(null);
  var [cardFilter, setCardFilter] = useState(null); /* draft | finalized | awaiting | paid */
  var [lockedRecord, setLockedRecord] = useState(null);
  /* Invoice preview opened from the INVOICE / PU column links in the
     main bills table. Same modal the DetailPanel uses; rendered at
     the main level so it works whether or not a bill is open. */
  var [previewInvoice, setPreviewInvoice] = useState(null);

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var billsRef = useRef([]);
  useEffect(function () { billsRef.current = bills; }, [bills]);

  /* Source of truth = Supabase `bills`. Dual-write to localStorage
     `gfi-bills` so legacy sync consumers (ProfitTracker, Settlements,
     SalesReports, computeCertUsage, etc. — all in subsequent paso 3c
     batches) keep working until they migrate. */
  var writeBillsCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  var reload = async function () {
    setError(null); setLoading(true);
    try {
      var rows = await loadFromSupabase("bills", { force: true });
      if (aliveRef.current) { setBills(rows); writeBillsCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load bills: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("bills")
      .then(function (rows) { if (aliveRef.current) { setBills(rows); writeBillsCache(rows); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load bills: " + (e.message || String(e))); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);
  /* Legacy localStorage-only migrations preserved here for new
     installs that bootstrap from a backup JSON. Once Supabase is the
     primary store, these run as no-ops thanks to the flag-gated guards. */
  // eslint-disable-next-line no-unused-vars
  var _legacyMigrationsBlock = function() {
    var loaded = loadLS(STORAGE_KEY, []);
    /* One-time migration: copy bill.commissionRate down to each line
       that doesn't already have its own commissionRate. Mathematically
       a no-op for totals (rate × Σamts = Σ(rate × amts) by linearity)
       so balances are guaranteed identical. Flag in localStorage so
       this only runs once even if the user clears the gfi-bills
       cache and reloads. */
    try {
      var migDone = localStorage.getItem("gfi-mig-line-commission-v1");
      if (!migDone) {
        var changed = false;
        loaded = loaded.map(function (b) {
          var rate = b.commissionRate;
          if (!b.lines || b.lines.length === 0) return b;
          var newLines = b.lines.map(function (l) {
            if (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") {
              changed = true;
              return { ...l, commissionRate: rate };
            }
            return l;
          });
          return changed ? { ...b, lines: newLines } : b;
        });
        if (changed) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-line-commission-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    /* "partialProtection" → "protection" one-time migration REMOVED
       2026-09-12 — that status is back, this time computed from
       pallet-level status on the invoice (see saleStatusCalc.js)
       instead of ever being manually chosen. Leaving the old migration
       running would have silently stripped every freshly-computed
       Partial Protection value back down to Protection on next load. */
    /* One-time migration: normalize legacy raw-label saleStatus values
       on bill lines to the canonical keys used by SALE_STATUSES.
       Pre-fix Excel imports stored the human label ("Protection",
       "Good Delivery") which the read-only badge can render but the
       edit-mode SearchSelect can't match against its canonical option
       values, leaving the dropdown empty. Mirror of the normalizer
       in ImportFromExcel.normalizeSaleStatus(). Flag-gated. */
    try {
      var nsDoneBill = localStorage.getItem("gfi-mig-ss-norm-bill-v1");
      if (!nsDoneBill) {
        var SS_CANON_BILL = { goodDelivery: 1, protection: 1, pas: 1, partialProtection: 1, samples: 1, dumped: 1, donated: 1, transfer: 1 };
        var SS_MAP_BILL = { gooddelivery: "goodDelivery", protection: "protection", pas: "pas", sample: "samples", samples: "samples", dumped: "dumped", donated: "donated", transfer: "transfer" };
        var anyNS = false;
        loaded = loaded.map(function (b) {
          if (!b.lines || b.lines.length === 0) return b;
          var hit = false;
          var nl = b.lines.map(function (l) {
            if (!l || !l.saleStatus) return l;
            if (SS_CANON_BILL[l.saleStatus]) return l;
            var key = String(l.saleStatus).toLowerCase().replace(/[^a-z0-9]/g, "");
            var mapped = SS_MAP_BILL[key];
            if (mapped && mapped !== l.saleStatus) { hit = true; anyNS = true; return { ...l, saleStatus: mapped }; }
            return l;
          });
          return hit ? { ...b, lines: nl } : b;
        });
        if (anyNS) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-ss-norm-bill-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    /* One-time migration: backfill productDescription on lines from
       legacy Excel imports (lots ~57922-58024) that only carry the
       commodity name ("Blackberry") without the full presentation
       ("Blackberry 12/6 oz OG GF Label"). Match strategy walks from
       most specific to most permissive, and skips lines it can't
       resolve unambiguously rather than risk writing a wrong desc.
       Flag-gated so it runs once per browser. */
    try {
      var descMigDone = localStorage.getItem("gfi-bills-description-migrated");
      if (!descMigDone) {
        var products = loadLS("gfi-products", []);
        if (products.length > 0) {
          /* Build the "full description" once per product. Prefer the
             stored desc; fall back to assembled fields so freshly
             seeded catalogs without a saved description still work. */
          var catCode = function (c) { return (c === "Organic" || c === "OG") ? "OG" : "CV"; };
          var fullDescOf = function (p) {
            if (p.description) return String(p.description).trim();
            var parts = [p.product, (p.unitsPerCase || p.unitsCase || "") + (p.unitsPerCase || p.unitsCase ? "/" : "") + (p.size || ""), catCode(p.category), p.labelInitials || "", "Label"].filter(Boolean);
            return parts.join(" ").trim();
          };
          /* Indexes for fast lookup. byId / bySku are unique. byNameCat
             may have collisions when several SKUs share the same
             (commodity, category) — those go through the size/units/
             label refinement below. */
          var byId = {}; var bySku = {}; var byNameCat = {};
          products.forEach(function (p) {
            if (p.id != null) byId[String(p.id)] = p;
            if (p.sku) bySku[String(p.sku).toUpperCase()] = p;
            var nk = (String(p.product || "").trim().toLowerCase()) + "|" + catCode(p.category);
            if (!byNameCat[nk]) byNameCat[nk] = [];
            byNameCat[nk].push(p);
          });
          var billsTouched = 0;
          var linesTouched = 0;
          var linesSkipped = 0;
          var migrated = loaded.map(function (b) {
            if (!b.lines || b.lines.length === 0) return b;
            var hit = false;
            var newLines = b.lines.map(function (l) {
              /* Already has a non-empty description → leave alone. */
              if (l && typeof l.productDescription === "string" && l.productDescription.trim().length > 0) return l;
              if (l && typeof l.description === "string" && l.description.trim().length > 0) return l;
              var match = null;
              if (l && l.productId != null && byId[String(l.productId)]) match = byId[String(l.productId)];
              if (!match && l && l.sku && bySku[String(l.sku).toUpperCase()]) match = bySku[String(l.sku).toUpperCase()];
              if (!match && l) {
                var commodity = String(l.productName || l.productBase || "").trim().toLowerCase();
                var cat = catCode(l.category);
                if (commodity) {
                  var bucket = byNameCat[commodity + "|" + cat] || [];
                  if (bucket.length === 1) {
                    match = bucket[0];
                  } else if (bucket.length > 1) {
                    /* Refine with whatever distinguishing fields the
                       line happens to carry. Each filter only fires
                       when the line provides that field, so partially-
                       populated lines still narrow down. */
                    var narrowed = bucket.slice();
                    if (l.size) narrowed = narrowed.filter(function (p) { return String(p.size || "").toLowerCase() === String(l.size).toLowerCase(); });
                    var lu = l.unitsPerCase || l.unitsCase;
                    if (lu) narrowed = narrowed.filter(function (p) { return String(p.unitsPerCase || p.unitsCase || "") === String(lu); });
                    if (l.labelInitials) narrowed = narrowed.filter(function (p) { return String(p.labelInitials || "").toLowerCase() === String(l.labelInitials).toLowerCase(); });
                    if (narrowed.length === 1) match = narrowed[0];
                  }
                }
              }
              if (!match) { linesSkipped += 1; return l; }
              var desc = fullDescOf(match);
              if (!desc) { linesSkipped += 1; return l; }
              hit = true;
              linesTouched += 1;
              /* Also seed productId when the line lacked one — makes
                 future resolveProductDesc() calls fast and survives
                 a later wipe of productDescription. */
              var patch = { productDescription: desc };
              if ((l.productId == null || l.productId === "") && match.id != null) patch.productId = match.id;
              if (!l.sku && match.sku) patch.sku = match.sku;
              return { ...l, ...patch };
            });
            if (hit) { billsTouched += 1; return { ...b, lines: newLines }; }
            return b;
          });
          if (billsTouched > 0) {
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); loaded = migrated; } catch (e) {}
          }
          try { console.log("[BillRegistry] productDescription backfill: " + billsTouched + " bill(s), " + linesTouched + " line(s) updated, " + linesSkipped + " line(s) skipped (unmatched)."); } catch (e) {}
        }
        try { localStorage.setItem("gfi-bills-description-migrated", "1"); } catch (e) {}
      }
    } catch (e) {}
    setBills(loaded);
    setLoading(false);
  }; /* end _legacyMigrationsBlock \u2014 dead code, kept for reference */

  /* CRUD helpers \u2014 replace the old `persist(fullArray)` pattern with
     row-targeted Supabase writes that keep the local cache and the
     dual-write localStorage mirror in sync. */
  var persistSingle = async function (b) {
    var saved = await saveToSupabase("bills", b);
    var current = billsRef.current || [];
    var ex = current.find(function (x) { return x.id === b.id; });
    var next = ex ? current.map(function (x) { return x.id === b.id ? saved : x; }) : current.concat([saved]);
    setBills(next); writeBillsCache(next);
    return saved;
  };
  var persistMany = async function (records) {
    if (records.length === 0) return [];
    var saved = await saveManyToSupabase("bills", records);
    var map = {};
    saved.forEach(function (s) { map[s.id] = s; });
    var next = (billsRef.current || []).map(function (x) { return map[x.id] || x; });
    setBills(next); writeBillsCache(next);
    return saved;
  };
  var persistDeleteBill = async function (id) {
    await deleteFromSupabase("bills", id);
    var next = (billsRef.current || []).filter(function (x) { return x.id !== id; });
    setBills(next); writeBillsCache(next);
  };
  /* Resolve shipper names */
  var resolvedBills = useMemo(function() {
    var sups = getSuppliers(); void sups;
    var invs = freshInvoicesLS();
    return bills.map(function(b) {
      var resolved = { ...b };
      /* Resolve shipper name */
      if (!resolved.shipperName) {
        var name = resolveShipperName(b.supplierId);
        if (name) resolved.shipperName = name;
      }
      /* Auto-derived linked invoices (PU# list). Uses the shared
         findInvoicesByLot helper so the matching logic is identical
         to what the Linked Invoices section in DetailPanel computes
         on click — single source of truth for what "linked" means. */
      var linkedInvs = findInvoicesByLot(b.lot, invs);
      /* Two derived strings: full PU# list for the INVOICES column,
         and a comma-joined version for the auto-populated reference
         field. Stash on the resolved record so per-row renders don't
         repeat the lookup. */
      resolved.linkedInvoiceObjs = linkedInvs;
      resolved.linkedInvoices = linkedInvs.length > 0 ? formatLinkedInvoiceRef(linkedInvs) : "";
      return resolved;
    });
  }, [bills]);

  var handleSave = async function(b) {
    if (saving) return;
    setSaving(true); setError(null);
    /* Auto-populate the reference field with the linked invoice PU#s
       (per spec: Arturo shouldn't add them manually). Computed from
       gfi-invoices on save so the saved record carries the value too
       — useful for print/export contexts that don't re-resolve on the
       fly. If there are no linked invoices yet, leave reference alone
       so any user-typed note is preserved. */
    var invsForRef = freshInvoicesLS();
    var linkedRef = formatLinkedInvoiceRef(findInvoicesByLot(b.lot, invsForRef));
    if (linkedRef) b = { ...b, reference: linkedRef };
    try {
      /* 2026-09-12 — REPLACES the old Bill→Invoice saleStatus push
         (removed: it pushed a single bill-line status onto every
         invoice line paired to it, which is backwards now that pallet
         status is assigned on the INVOICE and everything above it is
         computed — pushing the bill's aggregate down would have
         overwritten a correct PO like 17441's "Good Delivery" with the
         lot's overall "Partial Protection" the moment the bill was
         saved). Direction is now Invoice → Bill: recompute this bill's
         own line statuses from whatever its linked invoices' lines
         currently say. See saleStatusCalc.js's deriveDeliveryStatus and
         InvoiceRegistry.js's matching push (invoice save → bill
         recompute) for the other half — that one fires immediately when
         a pallet status changes; this one is a defensive re-sync so
         opening/saving the bill directly also stays fresh. */
      /* Twin River (2026-10-15) — ClariFresh shipments never get a
         saleStatus from the invoice side (Arturo never sets it there
         for them), so skip the invoice-matching recompute below and
         persist the shipment's own tolerance-sheet status instead —
         same source the live displays above already prefer. */
      /* 2026-10-30, Arturo (lot 58074): "las protecciones se definen
         por producto... no debe mezclar productos" — this persisted
         ONE lot-wide twinRiverLotStatus onto every line, which is
         exactly how BILL-0174 ended up with both Blackberry and
         Blueberry saved as "partialProtection" even though the
         shipment shows Blackberry fully protected and Blueberry fully
         good. Now computed per line (twinRiverProductStatus, scoped
         to that line's own productBase). */
      var shpForSaveStatus = getShipments().find(function (s) { return String(s.lot) === String(b.lot); });
      var isClariFreshSave = shpForSaveStatus && shpForSaveStatus.texasQcMode === "clarifresh";
      var invsForStatus = freshInvoicesLS();
      var linkedForStatus = findInvoicesByLot(b.lot, invsForStatus);
      var invoiceLinesForLot = [];
      linkedForStatus.forEach(function(inv) { (inv.lines || []).forEach(function(il) { invoiceLinesForLot.push(il); }); });
      var recomputedLines = (b.lines || []).map(function(bl) {
        if (DISPOSITION_STATUSES.indexOf(bl.saleStatus) !== -1) return bl;
        if (isClariFreshSave) {
          var trLineStatusSave = twinRiverProductStatus(shpForSaveStatus, bl.productBase || bl.productName);
          return trLineStatusSave && trLineStatusSave !== bl.saleStatus ? { ...bl, saleStatus: trLineStatusSave } : bl;
        }
        var billKey = String(bl.productName || bl.productBase || "").trim().toLowerCase();
        var billCat = String(bl.category || "").trim().toLowerCase();
        var billSize = String(bl.size || "").trim().toLowerCase();
        var matched = invoiceLinesForLot.filter(function(il) {
          var k = String(il.productName || il.productBase || "").trim().toLowerCase();
          if (!k || k !== billKey) return false;
          if (billCat && String(il.category || "").trim().toLowerCase() !== billCat) return false;
          if (billSize && String(il.size || "").trim().toLowerCase() !== billSize) return false;
          return true;
        });
        var derived = deriveDeliveryStatus(matched);
        if (derived === null || derived === bl.saleStatus) return bl;
        return { ...bl, saleStatus: derived };
      });
      b = { ...b, lines: recomputedLines };
    } catch (e) { /* best-effort — a failed recompute keeps the bill's existing statuses rather than blocking the save */ }
    try {
      await persistSingle(b);
      setShowForm(false); setEditing(null); setToast(t.saved);
      /* Only the standalone new/edit form (showForm) is truly closing
         here — the DetailPanel's own onSave re-opens viewing right
         after calling handleSave, so a save made there must never
         redirect away. */
      if (showForm) closeToOrigin();
    } catch (e) {
      setError("Could not save bill: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleDelete = async function(id) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistDeleteBill(id);
      setShowForm(false); setEditing(null); setViewing(null); setToast(t.deleted);
      closeToOrigin();
    } catch (e) {
      setError("Could not delete bill: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Confirm batch payment: append to bill.payments, update amountPaid + status, and log to gfi-payments */
  var handleBatchPaymentConfirm = async function(payload) {
    var allPayments = loadLS("gfi-payments", []);
    var newPayId = nextPaymentId(allPayments);
    var paymentRecord = {
      id: newPayId,
      paymentDate: payload.paymentDate,
      processingDate: payload.processingDate || "",
      deliveryDate: payload.deliveryDate || "",
      type: "outbound",
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: payload.allocations,
      shipperName: [...new Set(payload.allocations.map(function(a) { return a.shipperName; }).filter(Boolean))].join(", "),
      createdAt: new Date().toISOString(),
    };
    /* Update bills — concept is per-allocation (per bill row), not
       per whole payment, so each bill's payments[] entry carries its
       own concept for the confirmation PDF to read back later. */
    var allocByBillId = {};
    payload.allocations.forEach(function(a) { allocByBillId[a.billId] = a; });
    var toUpdate = bills.filter(function(b) { return allocByBillId[b.id]; }).map(function(b) {
      var alloc = allocByBillId[b.id];
      var amt = alloc.amount;
      /* Dedupe by paymentId — defensive against retries / double-
         clicks. If a row with this paymentId already exists (e.g.
         the user submitted twice), drop it before appending. */
      var existingPays = (b.payments || []).filter(function (p) { return p.paymentId !== newPayId; });
      var newPayments = [...existingPays, {
        paymentId: newPayId,
        date: payload.paymentDate,
        amount: amt,
        method: payload.paymentMethod,
        reference: payload.reference,
        concept: alloc.concept || "",
      }];
      var totalPaid = newPayments.reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var totals = calcBillTotals(b);
      var due = totals.subtotal - totalPaid;
      var newStatus = b.status;
      if (b.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
      return { ...b, payments: newPayments, amountPaid: +totalPaid.toFixed(2), status: newStatus };
    });
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistMany(toUpdate);
      /* Try to also push the central payment record to Supabase
         (best-effort — payments table migrates in GRUPO 3). */
      try { await saveToSupabase("payments", paymentRecord); } catch (e) { /* swallow */ }
      /* Dual-write to localStorage so PaymentRegistry / ProfitTracker
         keep reading the central payment log sync until they migrate. */
      saveLS("gfi-payments", [...allPayments, paymentRecord]);
      setSelected({});
      setShowBatchPay(false);
      setToast(t.paymentSaved + " (" + newPayId + ")");
      setConfirmationPayment(paymentRecord);
    } catch (e) {
      setError("Could not record payment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Create bill from shipment — called externally */
  var createFromShipment = useCallback(function(shipment) {
    /* Preserve per-line saleStatus from the shipment so the new bill
       inherits the quality classification. Coerce lot to string so an
       integer-stored lot still populates the read-only Lot field. */
    var newBill = {
      id: "", shipmentId: shipment.id, lot: shipment.lot != null ? String(shipment.lot) : "",
      billDate: new Date().toISOString().substring(0, 10),
      dueDate: (function() { var arr = shipment.dateArrival || shipment.dateDeparture || ""; if (!arr) return ""; var d = new Date(arr + "T00:00:00"); d.setDate(d.getDate() + 21); return d.toISOString().substring(0, 10); })(),
      supplierId: shipment.supplierId, shipperName: shipment.shipperName || resolveShipperName(shipment.supplierId),
      proforma: shipment.proforma, shipperInvoice: shipment.shipperInvoice,
      entryNumber: shipment.entryNumber, reference: "",
      /* 2026-10-01 — carries the FOB/Importación flag through for
         traceability; nothing on the Bill side reads broker/transport
         fields so FOB shipments (empty proforma/entryNumber) already
         flow through this pipeline unmodified. */
      shipmentType: shipment.shipmentType || "import",
      commissionRate: "5", status: "draft", amountPaid: "",
      lines: (shipment.lines || []).map(function(l) {
        return {
          ...l,
          reportedPrice: l.price || "",
          saleStatus: l.saleStatus || "",
        };
      }),
      fees: [{ ...EMPTY_FEE }],
      notes: "",
      /* Deep copy of the shipment's pallet manifest (1A). */
      palletDetail: deepCopyPalletManifest(shipment),
    };
    setEditing(null);
    setShowForm(false);
    setTimeout(function() { setEditing(newBill); setShowForm(true); }, 50);
  }, []);

  /* Expose createFromShipment via window for cross-module access */
  useEffect(function() { window.__gfiBillCreate = createFromShipment; return function() { delete window.__gfiBillCreate; }; }, [createFromShipment]);

  /* Open-by-id hook for global search */
  useEffect(function() {
    window.__gfiBillOpen = function(id) {
      var ret = window.__gfiReturnModule; window.__gfiReturnModule = null;
      var b = (billsRef.current || []).find(function(x) { return x.id === id; });
      if (b) { returnModuleRef.current = ret; setReopenInEditMode(false); setViewing(b); }
    };
    return function() { delete window.__gfiBillOpen; };
  }, []);

  /* Blank-form create hook for Create New menu */
  useEffect(function() {
    window.__gfiBillNew = function() {
      returnModuleRef.current = window.__gfiReturnModule; window.__gfiReturnModule = null;
      setEditing(null); setShowForm(true);
    };
    return function() { delete window.__gfiBillNew; };
  }, []);

  /* 2026-10-20 — Arturo: Shipment Info's "Edit" button sends the user to
     the linked Shipment to edit it there (not meant to be re-edited in
     2 places), and on Save/Close should land back on this same Bill.
     window.__gfiBillReopenId is stashed right before navigating away
     (see DetailPanel's Shipment Info Edit button below); consumed here
     once `bills` has actually loaded, same 200ms-mount convention used
     elsewhere for cross-module window globals (App.js's doc comment). */
  useEffect(function() {
    if (!window.__gfiBillReopenId) return;
    if (!bills || bills.length === 0) return;
    var id = window.__gfiBillReopenId; window.__gfiBillReopenId = null;
    var wantsEdit = !!window.__gfiBillReopenEditMode; window.__gfiBillReopenEditMode = false;
    var b = bills.find(function(x) { return x.id === id; });
    if (b) { setReopenInEditMode(wantsEdit); setViewing(b); }
  }, [bills]);

  /* Season scope, isolated from the other row filters — same pattern
     validated in PaymentRegistry.js. `summary` (the status tabs below)
     keys off THIS array, not the fully-filtered `filtered`: the tabs
     set cardFilter on click, so if summary read from a list that
     already had cardFilter baked in, clicking a tab would collapse
     its own count instead of letting the user compare Draft vs
     Finalized vs Awaiting vs Paid within the chosen season(s). Empty
     selection = all seasons (no restriction). */
  var seasonFilteredBills = useMemo(function() {
    if (filterSeasons.length === 0) return resolvedBills;
    return resolvedBills.filter(function(x) { return filterSeasons.indexOf(resolveSeasonId(seasons, x.billDate, x.seasonId)) >= 0; });
  }, [resolvedBills, filterSeasons, seasons]);

  /* Live sale-status set for one bill — same deriveBillLineLiveStatus +
     resolvePendingStage fallback the multi-badge Status column renders
     (2026-09-27, Arturo: pendingPickUp/pendingDelivery/partialProtection
     never matched the filter — they're never stored on a line, only
     computed, so comparing raw l.saleStatus always missed those 3). */
  var liveStatusesForBill = useCallback(function(x) {
    /* Twin River (2026-10-15) — see twinRiverLotStatus comment above.
       2026-10-30 — per-product, not one lot-wide value, same fix as
       the badges above. */
    var shpForStatus = getShipments().find(function (s) { return String(s.lot) === String(x.lot); });
    if (shpForStatus && shpForStatus.texasQcMode === "clarifresh") {
      var trStatusesForFilter = (x.lines || []).map(function(l) { return twinRiverProductStatus(shpForStatus, l.productBase || l.productName); }).filter(Boolean);
      return trStatusesForFilter.length > 0 ? trStatusesForFilter : ["pendingPickUp"];
    }
    var lotInvoicesForStatus = freshInvoicesLS().filter(function(inv) { return String(inv.lot) === String(x.lot); });
    var invoiceLinesForLot = [];
    lotInvoicesForStatus.forEach(function(inv) { (inv.lines || []).forEach(function(il) { invoiceLinesForLot.push(il); }); });
    var statuses = [];
    (x.lines || []).forEach(function(l) { var live = deriveBillLineLiveStatus(l, invoiceLinesForLot); if (live) statuses.push(live); });
    if (statuses.length === 0) statuses = [resolvePendingStage(lotInvoicesForStatus, slPassingIndex)];
    return statuses;
  }, [slPassingIndex]);

  var shpsForWeekCache = useMemo(function() { return loadLS("gfi-shipments", []); }, []);
  var weekOfBill = useCallback(function(x) {
    var shp = shpsForWeekCache.find(function(s) { return String(s.lot) === String(x.lot); });
    var d = shp ? (shp.dateArrival || shp.dateDeparture || x.billDate) : x.billDate;
    return d ? ("W" + getISOWeek(d)) : "";
  }, [shpsForWeekCache]);

  /* Excel-style live filter set (2026-09-27, Arturo: "todos los filtros
     deben ser dinamicos y actualizarse en vivo... si filtro la
     temporada y solo estoy viendo 2026-2027 y apenas tengo fruta de un
     shipper, no es necesario me muestre nombres de shippers que no
     aparecen en la tabla, clientes es lo mismo, semanas igual, sale
     status igual"). Each predicate is applied to `seasonFilteredBills`
     EXCEPT the one matching its own dropdown, so every option list
     reflects "what would show up if I picked this value, given every
     OTHER filter currently active" — same mechanic as Excel/Google
     Sheets' own AutoFilter dropdowns. `filtered` (the actual table)
     applies all of them. */
  var applyBillFilters = useCallback(function(list, exclude) {
    if (exclude !== "search" && search) { var s = search.toLowerCase(); list = list.filter(function(x) { return String(x.lot || "").includes(s) || (x.shipperName || "").toLowerCase().includes(s) || (x.reference || "").toLowerCase().includes(s) || (x.proforma || "").toLowerCase().includes(s); }); }
    if (exclude !== "shipper" && filterShipper.length > 0) list = list.filter(function(x) { return filterShipper.indexOf(x.shipperName) !== -1; });
    if (exclude !== "status" && filterStatus) list = list.filter(function(x) { return x.status === filterStatus; });
    if (exclude !== "saleStatus" && filterSaleStatus.length > 0) list = list.filter(function(x) { var live = liveStatusesForBill(x); return filterSaleStatus.some(function(fs) { return live.indexOf(fs) !== -1; }); });
    if (exclude !== "cert" && filterCert) {
      var wantNum = certIdToNum[filterCert] || "";
      list = list.filter(function(x) {
        return (x.lines || []).some(function(l) {
          if (l.exportCertId && String(l.exportCertId) === String(filterCert)) return true;
          if (wantNum && nrmCertNum(l.exportCertNumber) === wantNum) return true;
          if (wantNum && nrmCertNum(l.exportCertManual) === wantNum) return true;
          return false;
        });
      });
    }
    if (exclude !== "date" && (dateFrom || dateTo)) list = list.filter(function(x) { var d = x.billDate || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    if (exclude !== "week" && filterWeek.length > 0) list = list.filter(function(x) { return filterWeek.indexOf(weekOfBill(x)) !== -1; });
    if (exclude !== "card" && cardFilter) {
      if (cardFilter === "awaiting") {
        list = list.filter(function(x) {
          if (x.status !== "finalized") return false;
          var tot = calcBillTotals(x);
          var paid = paidAmount(x);
          return (tot.subtotal - paid) > 0.01;
        });
      } else if (cardFilter === "paid") {
        list = list.filter(function(x) {
          if ((x.status || "draft") === "paid") return true;
          var tot = calcBillTotals(x);
          var paid = paidAmount(x);
          return paid > 0 && (tot.subtotal - paid) <= 0.01;
        });
      } else {
        list = list.filter(function(x) { return (x.status || "draft") === cardFilter; });
      }
    }
    return list;
  }, [search, filterShipper, filterStatus, filterSaleStatus, filterCert, certIdToNum, dateFrom, dateTo, filterWeek, cardFilter, liveStatusesForBill, weekOfBill]);

  var uniqueShippers = useMemo(function() { return [...new Set(applyBillFilters(seasonFilteredBills, "shipper").map(function(b) { return b.shipperName; }).filter(Boolean))].sort(); }, [seasonFilteredBills, applyBillFilters]);

  var dynamicSaleStatusOptions = useMemo(function() {
    var present = new Set();
    applyBillFilters(seasonFilteredBills, "saleStatus").forEach(function(x) { liveStatusesForBill(x).forEach(function(s) { present.add(s); }); });
    return FILTER_SALE_STATUSES.filter(function(s) { return present.has(s); });
  }, [seasonFilteredBills, applyBillFilters, liveStatusesForBill]);

  var filtered = useMemo(function() { return applyBillFilters(seasonFilteredBills, null); }, [seasonFilteredBills, applyBillFilters]);
  var uniqueSeasons = useMemo(function() { return [...new Set(seasons.map(function(s) { return s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4); }).filter(Boolean))].sort(); }, [seasons]);
  /* Sorted list of ISO weeks present in the current bill set, used
     to populate the new Week filter dropdown. Built off the same
     shipment-linked rule the W## table column uses so the dropdown
     entries always exist in the rendered list. */
  var uniqueWeeks = useMemo(function() {
    var shpsForWeeks = loadLS("gfi-shipments", []);
    var weeks = new Set();
    seasonFilteredBills.forEach(function(b) {
      var shp = shpsForWeeks.find(function(s) { return String(s.lot) === String(b.lot); });
      var d = shp ? (shp.dateArrival || shp.dateDeparture || b.billDate) : b.billDate;
      if (d) weeks.add("W" + getISOWeek(d));
    });
    return Array.from(weeks).sort(function(a, b) { return (parseInt(b.slice(1), 10) || 0) - (parseInt(a.slice(1), 10) || 0); });
  }, [seasonFilteredBills]);

  var sorted = useMemo(function() {
    return [...filtered].sort(function(a, b) {
      var va, vb;
      switch (sortCol) {
        case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "season": va = seasonNameForBill(a.billDate, a.seasonId); vb = seasonNameForBill(b.billDate, b.seasonId); break;
        case "shipperName": va = a.shipperName || ""; vb = b.shipperName || ""; break;
        case "billDate": va = a.billDate || ""; vb = b.billDate || ""; break;
        case "dueDate": va = a.dueDate || ""; vb = b.dueDate || ""; break;
        case "cases":
          va = (a.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
          vb = (b.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
          return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir, seasonNameForBill]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  var allSelected = sorted.length > 0 && sorted.every(function(b) { return selected[b.id]; });
  var toggleAll = function() { var ns = {}; if (!allSelected) sorted.forEach(function(b) { ns[b.id] = true; }); setSelected(ns); };
  var toggleOne = function(id, e) { e.stopPropagation(); setSelected(function(p) { var ns = Object.assign({}, p); if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  var selectedCount = Object.keys(selected).length;
  var selectedBills = useMemo(function() { return resolvedBills.filter(function(b) { return selected[b.id]; }); }, [resolvedBills, selected]);
  /* Bulk "Email Sales Report" is disabled when the selection spans more
     than one shipper — EmailSalesReportModal resolves recipients/
     subject/filename off a single shipper (bills[0].supplierId), so a
     mixed-shipper batch would silently email the wrong supplier's
     contacts (fix-dashboard.md, "Simplificar el flujo Email Sales
     Report"). */
  var selectedShipperIds = useMemo(function() {
    var seen = {}; var ids = [];
    selectedBills.forEach(function(b) { var k = String(b.supplierId || ""); if (!seen[k]) { seen[k] = true; ids.push(k); } });
    return ids;
  }, [selectedBills]);
  var mixedShippersSelected = selectedShipperIds.length > 1;
  /* Aggregate breakdown for the selection bar AND module-total chip:
     { gross, fees (commission + fees combined as a single "what GFI
     keeps before transfers" bucket), paid, due }. The "Total Fees"
     bucket the user requested includes the commission line because
     that is also a deduction from the shipper's gross sales. */
  function aggregateBills(list) {
    var gross = 0, fees = 0, paid = 0, due = 0;
    (list || []).forEach(function(b) {
      var tot = calcBillTotals(b);
      var p = paidAmount(b);
      gross += tot.grossSales;
      /* fees bucket = commission + fee charges − credits (credits add
         back to the shipper). transfers excluded — they're a netting
         operation, not a deduction. */
      fees += tot.commission + tot.totalFees - tot.totalCredits;
      paid += p;
      due += (tot.subtotal - p);
    });
    return { gross: gross, fees: fees, paid: paid, due: due };
  }
  var selectedTotals = useMemo(function() { return aggregateBills(selectedBills); }, [selectedBills]);
  /* Footer module-total chip — reflects exactly what's on screen right
     now (season + every other active filter), same choice validated
     in PaymentRegistry.js's grandTotals. Not clickable, so there's no
     circularity risk like the tabs above. Previously read from
     resolvedBills directly, ignoring EVERY filter (not just season). */
  var grandTotals = useMemo(function() { return aggregateBills(sorted); }, [sorted]);
  var selectedDueTotal = selectedTotals.due;
  var grandDueTotal = grandTotals.due;
  var clearSelection = function() { setSelected({}); };
  var [showBulkDelete, setShowBulkDelete] = useState(false);
  var [showBulkStatus, setShowBulkStatus] = useState(false);

  /* Excel export. Convention: if there is a selection, export that;
     otherwise export the full filtered view. Spec for the Summary sheet
     is fixed by the user (one row per bill, totals per concept,
     Advance Financing Fee broken out into its own column). The Detail
     sheet expands every bill into Product / Commission / Fee / Credit /
     Transfer rows discriminated by the Type column. */
  var billsToExport = function () { return selectedBills.length > 0 ? selectedBills : sorted; };
  var buildBillSummaryRows = function () {
    return billsToExport().map(function (b) {
      var totals = calcBillTotals(b);
      var advFee = 0; var otherFees = 0;
      (b.fees || []).forEach(function (fe) {
        var amt = parseFloat(fe.amount) || 0;
        if (isAdvanceFinancingFee(fe)) advFee += amt; else otherFees += amt;
      });
      var due = totals.subtotal - paidAmount(b);
      var totalCases = (b.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
      return {
        Lot: s(b.lot),
        Date: s(b.billDate),
        Shipper: s(resolveShipperName(b.supplierId) || b.shipperName),
        Proforma: s(b.proforma),
        "Shipper Invoice": s(b.shipperInvoice),
        Cases: intOr(totalCases),
        "Gross Sales": num2(totals.grossSales),
        Commission: num2(totals.commission),
        "Advance Financing Fee": num2(advFee),
        Fees: num2(otherFees),
        Credits: num2(totals.totalCredits),
        "Balance Transfers": num2(totals.totalTransfers),
        "Amount Due": num2(due),
        Status: s(b.status),
      };
    });
  };
  var buildBillDetailRows = function () {
    var out = [];
    billsToExport().forEach(function (b) {
      var meta = { Lot: s(b.lot), Date: s(b.billDate), Shipper: s(resolveShipperName(b.supplierId) || b.shipperName), Proforma: s(b.proforma), "Shipper Invoice": s(b.shipperInvoice) };
      (b.lines || []).forEach(function (l) {
        var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
        /* Product = commodity (Blackberry / Blueberry / Raspberry).
           Description = FULL presentation (e.g. "Blackberry 12/6oz OG
           GF Label"). Use resolveProductDesc() — the same helper the
           bill detail panel uses — so empty productDescription falls
           back to a catalog lookup by productId, NOT to the commodity
           name (that would defeat the whole point of the column). */
        var fullDesc = resolveProductDesc(l) || s(l.productDescription);
        /* Per-line commission columns. Falls back to bill-level rate
           when the line doesn't carry its own (legacy data). */
        var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? (parseFloat(b.commissionRate) || 0)
          : (parseFloat(l.commissionRate) || 0);
        var lineComm = lineCommissionAmount(l, b.commissionRate);
        out.push({ ...meta, Type: "Product", Product: s(l.productBase || l.productName), Description: fullDesc, Category: s(l.category), Cases: intOr(l.cases), Pallets: intOr(l.pallets), Rate: num2(l.reportedPrice), Amount: num2(amt), "Comm %": lineRate, "Comm $": num2(-lineComm), "Sale Status": s(l.saleStatus) });
      });
      var totals = calcBillTotals(b);
      /* Aggregate Commission row at the bottom for quick totals view —
         sum of per-line commissions. */
      if (totals.commission > 0) {
        out.push({ ...meta, Type: "Commission", Product: "", Description: "Total Commission", Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(-totals.commission), "Comm %": "", "Comm $": "", "Sale Status": "" });
      }
      (b.fees || []).forEach(function (fe) {
        out.push({ ...meta, Type: "Fee", Product: "", Description: feeName(fe), Category: "", Cases: "", Pallets: intOr(fe.qty), Rate: num2(fe.rate), Amount: num2(-Math.abs(parseFloat(fe.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (b.credits || []).forEach(function (cr) {
        var label = cr.type === "fromBill" ? ("Credit from " + (cr.refLot || "—")) : (cr.concept || "Credit");
        out.push({ ...meta, Type: "Credit", Product: "", Description: label, Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(Math.abs(parseFloat(cr.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (b.balanceTransfers || []).forEach(function (tr) {
        var amt = parseFloat(tr.amount) || 0;
        var signed = tr.type === "credit" ? amt : -amt;
        out.push({ ...meta, Type: "Transfer", Product: "", Description: (tr.type === "credit" ? "Credit from LOT " : "Debit to LOT ") + (tr.otherLot || "?"), Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(signed), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
    });
    return out;
  };
  var bulkLocked = useMemo(function() {
    return selectedBills.filter(function(b) { return (b.payments || []).length > 0; }).map(function(b) { return { id: b.id, label: "LOT " + (b.lot || b.id) }; });
  }, [selectedBills]);
  /* Bulk status change. Records with payments can only move to "paid";
     for any other target they are skipped (filtered out via lockedIds). */
  var handleBulkStatusChange = async function(newStatus) {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    if (newStatus !== "paid") bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toUpdate = bills.filter(function(b) { return selected[b.id] && !lockedIds[b.id]; }).map(function(b) { return { ...b, status: newStatus }; });
    try {
      await persistMany(toUpdate);
      setShowBulkStatus(false); setSelected({}); setToast(t.saved);
    } catch (e) {
      setError("Could not bulk update: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleBulkDelete = async function() {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toDelete = bills.filter(function(b) { return selected[b.id] && !lockedIds[b.id]; });
    try {
      await Promise.all(toDelete.map(function (b) { return deleteFromSupabase("bills", b.id); }));
      var deletedSet = {};
      toDelete.forEach(function (b) { deletedSet[b.id] = true; });
      var keep = bills.filter(function (b) { return !deletedSet[b.id]; });
      setBills(keep); writeBillsCache(keep);
      setShowBulkDelete(false);
      setSelected({});
      setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Summary cards: Draft (count) | Finalized (count) $balance | Awaiting Payment (count) $due rojo | Paid (count)
     Paid amount uses sum of payments[] (authoritative). Records with balance <= 0 count as Paid
     regardless of stored status. Finalized/Awaiting amounts use the pending balance. */
  var summary = useMemo(function() {
    var r = { draft: 0, finalized: 0, finalizedAmt: 0, awaiting: 0, awaitingAmt: 0, paid: 0 };
    seasonFilteredBills.forEach(function(b) {
      var tot = calcBillTotals(b);
      var paid = paidAmount(b);
      var due = +(tot.subtotal - paid).toFixed(2);
      var st = b.status || "draft";
      if (st === "draft") { r.draft++; return; }
      if (paid > 0 && due <= 0.01) { r.paid++; return; }
      if (st === "paid") { r.paid++; return; }
      r.finalized++; r.finalizedAmt += due;
      if (due > 0.01) { r.awaiting++; r.awaitingAmt += due; }
    });
    return r;
  }, [seasonFilteredBills]);

  if (loading) return null;
  /* Brushed Steel metal-plate spec (see App.css "BRUSHED STEEL" /
     ShipmentRegistry.js list header) — brushed-metal gradient + 1px
     #b9bcc0 border, embossed text (dark #1a2636 fill, light-below/
     dark-above text-shadow), square corners, 9px column headers.
     Background lives on the <thead><tr> itself (see thead render
     below) for one continuous bar, same gradient as EditSectionHeader's
     title bar; `th` keeps only text + the shared border. */
  var th = { padding: "7px 5px", textAlign: "left", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: "transparent", borderTop: "1px solid #b9bcc0", borderBottom: "1px solid #b9bcc0" };

  /* Xero-style layout: tabs replace the big summary cards; the
     visible filter bar collapses into a Filters popover; module
     totals move to a tight 2-column block above the table.

     Tabs config — labels + counts. The All tab clears cardFilter;
     each other tab pins cardFilter to the corresponding status so
     the existing `filtered` useMemo keeps doing the work. */
  var tabsCfg = [
    { key: "all",       label: lang === "es" ? "Todas" : "All",         count: seasonFilteredBills.length, filterValue: null },
    { key: "draft",     label: t.statusDraft,                            count: summary.draft || 0,    filterValue: "draft" },
    { key: "finalized", label: t.statusFinalized,                        count: summary.finalized,     filterValue: "finalized" },
    { key: "awaiting",  label: t.statusAwaiting,                         count: summary.awaiting,      filterValue: "awaiting" },
    { key: "paid",      label: t.statusPaid,                             count: summary.paid || 0,     filterValue: "paid" },
  ];
  var activeTabKey = cardFilter || "all";
  /* Active filter count — drives the badge next to the Filters
     button. `cardFilter` (the tabs) is NOT counted, since the tabs
     are now the visible way to filter by status. `search` is also
     excluded — it lives in its own always-visible row, so a value
     there is already self-evident and doesn't need badge surfacing. */
  var activeFilterCount =
      (filterShipper.length > 0 ? 1 : 0)
    + (filterSaleStatus.length > 0 ? 1 : 0)
    + (filterCert ? 1 : 0)
    + (filterSeasons.length > 0 ? 1 : 0)
    + ((dateFrom || dateTo) ? 1 : 0)
    + (filterWeek.length > 0 ? 1 : 0)
    + (filterStatus ? 1 : 0);
  var clearAllFilters = function() {
    setSearch(""); setFilterShipper([]); setFilterStatus(""); setFilterSaleStatus([]);
    setFilterCert(""); setFilterSeasons([]); setDateFrom(""); setDateTo(""); setFilterWeek([]);
  };
  /* Brushed Steel action button — same spec as ShipmentRegistry.js's
     list toolbar (square corners, brushed-metal gradient, embossed
     text). */
  var actBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var actBtnDanger  = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.1)" });
  /* Dark-silver "title plate" variant — same gradient as the title
     bar / table header bar, used for the Filters + "⋮" triggers so
     each trigger reads as one continuous plate with the popover it
     opens. */
  var titlePlateBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var totalsLabel = { textAlign: "right", color: "var(--color-text-muted)", fontSize: 11, whiteSpace: "nowrap" };
  var totalsValue = { textAlign: "right", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };

  return (
    <div>
      {/* HEADER ROW — title (left); search + filters + kebab + new (right);
          module totals ledger right below. Brushed Steel metal-plate
          treatment ported from ShipmentRegistry.js's list header. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        {/* Title + "showing N of M" wrapped in the same dark-silver
            title-bar plate as the table's header bar / window title bar
            (#c7cbd1→#9aa0a8), black embossed text. */}
        <div style={{ lineHeight: 1.1, padding: "8px 14px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{t.title}</h2>
          <span style={{ fontSize: 10, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.showing} {sorted.length} {t.of} {seasonFilteredBills.length} {t.total}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action group: search + filters + kebab (document/export
              actions) + Make Payment + New Bill. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            {/* SEARCH — relocated before Filters, neumorphic white pill
                with an embossed magnifier icon (was its own full row
                below the tabs). */}
            <span style={{ position: "relative", display: "inline-block" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.9)) drop-shadow(0 -1px 1px rgba(0,0,0,0.3))" }}>
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                placeholder={t.search}
                style={{ width: 200, padding: "6px 10px 6px 28px", fontSize: 12, borderRadius: 999, border: "none", background: "#ffffff", boxShadow: "inset 1px 1px 3px rgba(0,0,0,0.15), inset -1px -1px 2px rgba(255,255,255,0.8), 0 1px 1px rgba(255,255,255,0.9), 0 -1px 1px rgba(0,0,0,0.1)", outline: "none", fontFamily: "inherit", color: "var(--color-text-secondary)" }}
              />
            </span>
            {search && (
              <button
                type="button"
                onClick={function() { setSearch(""); }}
                style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}
                title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}
              >×</button>
            )}
            <button
              type="button"
              onClick={function() { setShowFilters(function(v) { return !v; }); }}
              style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              <span>{lang === "es" ? "Filtros" : "Filters"}</span>
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            {/* KEBAB — document/export actions (Sales Report PDF /
                Email Sales Report / Export Excel). These are
                secondary/less-frequent actions compared to Make
                Payment, which stays its own visible button. Export
                Excel embeds the real ExportButton component as its
                own row (reused as-is, never re-implemented). */}
            <span style={{ position: "relative", display: "inline-block" }} ref={moreMenuRef}>
              <button
                type="button"
                onClick={function() { setShowMoreMenu(function(v) { return !v; }); }}
                style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", padding: "5px 10px" })}
                title={lang === "es" ? "Más acciones" : "More actions"}
              >
                <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              </button>
              {showMoreMenu && (
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 220, overflow: "visible", padding: 4 }}>
                  <button
                    type="button"
                    disabled={selectedCount === 0}
                    onClick={function() {
                      if (selectedBills.length === 0) return;
                      setShowMoreMenu(false);
                      /* Single bill selected → route into the PDF-blob
                         preview (Download/Send by Email/Close).
                         SalesReportPdfPreview only takes one `bill`, so
                         a multi-bill selection keeps the old direct-
                         download combined PDF. */
                      if (selectedBills.length === 1) {
                        setSalesReportPreviewBill(selectedBills[0]);
                        return;
                      }
                      printMultipleSalesReports(selectedBills, getSuppliers(), function(err) {
                        if (err === "popup-blocked") {
                          setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
                        }
                      });
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: selectedCount === 0 ? "not-allowed" : "pointer", opacity: selectedCount === 0 ? 0.5 : 1, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={function(e) { if (selectedCount > 0) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}
                  ><span>📄</span><span>Sales Report PDF</span></button>
                  <button
                    type="button"
                    disabled={selectedCount === 0 || mixedShippersSelected}
                    title={mixedShippersSelected ? (lang === "es" ? "Todos los bills deben ser del mismo shipper para enviar por email" : "All selected bills must belong to the same shipper to send by email") : undefined}
                    onClick={function() {
                      if (selectedBills.length === 0 || mixedShippersSelected) return;
                      setShowMoreMenu(false);
                      setEmailTargetBills(selectedBills);
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: (selectedCount === 0 || mixedShippersSelected) ? "not-allowed" : "pointer", opacity: (selectedCount === 0 || mixedShippersSelected) ? 0.5 : 1, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={function(e) { if (selectedCount > 0 && !mixedShippersSelected) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}
                  ><span>✉</span><span>{lang === "es" ? "Enviar Reporte por Email" : "Email Sales Report"}</span></button>
                  <div style={{ padding: "4px 10px" }}>
                    {/* 2026-10-20, Arturo: "export excel quedó en
                        color verde con marco verde, dale el mismo
                        formato que los demás elementos de esa misma
                        ventana" — btnStyle matches the Sales Report
                        PDF / Email Sales Report menu items above
                        exactly (transparent, black embossed text, no
                        border), instead of ExportButton's own default
                        green pill. */}
                    <ExportButton lang={lang} filenameBase="Bills" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildBillSummaryRows} buildDetail={buildBillDetailRows} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                </div>
              )}
            </span>
            {/* + New Bill — Brushed Steel's "colored/active plate"
                variant (same blue gradient used for Save/active toggles
                elsewhere in this design), square corners. */}
            <button onClick={function() { returnModuleRef.current = null; setEditing(null); setShowForm(true); }} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 14, lineHeight: 1 }}>+</span>{t.add}</button>
            {/* FILTER POPOVER — absolute under the trigger row, same
                dark-silver plate as the title plate; fields inside
                (SearchSelect, date inputs) stay their own white boxes
                sitting on the plate. */}
            {showFilters && (
              /* 2026-10-20, Arturo: "la ventana filters quedo muy
                 amplia, debe ser igual que en embarques" — width/
                 padding/gaps/font-sizes now match ShipmentRegistry.js's
                 own Filters popover exactly (440px, not 720px). */
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 440, maxWidth: "94vw", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={function() { setShowFilters(false); }} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div>
                    {/* 2026-10-20, Arturo: "en filters los headers son
                        de color gris y no estar marcado en la placa"
                        — labels were still using the plain muted-gray
                        color meant for a light background; embossed
                        black text (same as the plate's title/totals)
                        to match once sitting on the dark-silver
                        Filters plate. Same fix in both modules. */}
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Exportador" : "Shipper"}</div>
                    <SearchSelect multi value={filterShipper} onChange={setFilterShipper} placeholder={t.allShippers} options={uniqueShippers.map(function(s) { return { value: s, label: s }; })} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                    <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Semana" : "Week"}</div>
                    <SearchSelect multi value={filterWeek} onChange={setFilterWeek} placeholder={lang === "es" ? "Todas las semanas" : "All weeks"} options={uniqueWeeks.map(function(w) { return { value: w, label: w }; })} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Status venta" : "Sale status"}</div>
                    <SearchSelect multi value={filterSaleStatus} onChange={setFilterSaleStatus} placeholder={t.allSaleStatuses} options={dynamicSaleStatusOptions.map(function(s) { return { value: s, label: t[s] || s }; })} style={filterFieldPlate} />
                  </div>
                  <div style={{ gridColumn: "span 2" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Certificado" : "Certificate"}</div>
                    <SearchSelect value={filterCert} onChange={setFilterCert} placeholder={t.allCerts} limit={20} options={certFilterOptions} style={filterFieldPlate} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — same dark title-bar silver plate as the
              header bar / title plate above. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 10, rowGap: 1, alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Ventas" : "Sales"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.gross)}</span>
            {/* 2026-10-20, Arturo: "el color font de los fees se
                mantiene rojo aqui" — Fees is a deduction, unlike the
                other 3 totals; keeps its own red instead of the
                uniform black the rest of this plate uses. */}
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.totalFees}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#DC2626", textShadow: "none" }}>{fmtMoney(grandTotals.fees)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Pagado" : "Paid"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.paid)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "A pagar" : "Amount Due"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.due)}</span>
          </div>
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 10px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 6, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button></div>}
      {/* TABS — All / Draft / Finalized / Awaiting Payment / Paid.
          Restyled as inset/pressed segments (no underline). Change
          Status / Delete Selected / Clear used to be a separate
          ACTION BAR row below (along with the standalone search row
          above, now relocated into the header action group) — both
          moved here, right after the Paid tab, same compact Brushed
          Steel metal plate buttons (actBtn/actBtnDanger) as the
          module totals ledger above. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(function(tab) {
          var isActive = activeTabKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={function() { setCardFilter(tab.filterValue); }}
              style={{
                padding: "5px 12px",
                background: isActive ? "#ffffff" : "transparent",
                boxShadow: isActive ? "inset 1px 1px 2px rgba(0,0,0,0.15)" : "none",
                border: "none",
                color: "#1a2636",
                fontWeight: 500,
                fontSize: 11,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 10, marginBottom: 2 }}>
          {/* 2026-10-20, Arturo: "ese make a payment junto a new bill
              cambialo a antes de change status" — moved here from the
              header action group, right before Change Status. */}
          <button type="button" onClick={function() { setShowBatchPay(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{t.makePayment}</button>
          <button type="button" onClick={function() { setShowBulkStatus(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Cambiar Estado" : "Change Status"}</button>
          <button type="button" onClick={function() { setShowBulkDelete(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button type="button" onClick={clearSelection} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>
        {selectedCount > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginBottom: 2 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Ventas" : "Gross Sales"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(selectedTotals.gross)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Total cargos" : "Total Fees"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.fees)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Pagado" : "Paid"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.paid)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "A Pagar" : "Amount Due"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{fmtMoney(selectedTotals.due)}</span></span>
          </div>
        )}
      </div>
      {sorted.length === 0 ? <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div> : (
        <div style={{ overflowX: "auto", borderRadius: 0, border: "1px solid #b9bcc0", background: "#ffffff" }}>
          <table className="gfi-bills-list-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead><tr style={{ background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)" }}>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th onClick={function() { toggleSort("lot"); }} style={{ ...th, textAlign: "center" }}>{t.lot}{arrow("lot")}</th>
              <th onClick={function() { toggleSort("season"); }} style={th}>{lang === "es" ? "Temporada" : "Season"}{arrow("season")}</th>
              <th onClick={function() { toggleSort("billDate"); }} style={th}>{t.date}{arrow("billDate")}</th>
              <th onClick={function() { toggleSort("dueDate"); }} style={th}>{t.dueDate}{arrow("dueDate")}</th>
              <th style={{ ...th, textAlign: "center" }}>Wk</th>
              <th onClick={function() { toggleSort("shipperName"); }} style={th}>{t.shipper}{arrow("shipperName")}</th>
              <th style={th}>{t.proforma}</th>
              <th style={th}>{t.entryNumber}</th>
              {/* 2026-10-20, Arturo: "invoice y pu alineado a la
                  izquierda, centralos" */}
              <th style={{ ...th, textAlign: "center" }}>{t.linkedInvHeader}</th>
              <th style={{ ...th, textAlign: "center" }} title={lang === "es" ? "Total de pallets del bill" : "Total pallets on the bill"}>{t.pallets || (lang === "es" ? "Pallets" : "Pallets")}</th>
              <th onClick={function() { toggleSort("cases"); }} style={{ ...th, textAlign: "right" }} title={lang === "es" ? "Total de cajas del bill" : "Total cases on the bill"}>{t.cases}{arrow("cases")}</th>
              {/* 2026-10-20, Arturo: "gross sales reported solo sales"
                  — shortened just in this column header (to narrow
                  the column); t.grossSales itself ("Gross Sales
                  (reported)") is left untouched everywhere else, that
                  longer label still matters inside the Bill's own
                  detail breakdown. */}
              <th style={{ ...th, textAlign: "right" }}>{lang === "es" ? "Ventas" : "Sales"}</th>
              {/* 2026-10-20, Arturo: "tambien total fees solo fees" —
                  same column-header shortening as Sales; t.totalFees
                  itself untouched everywhere else. */}
              <th style={{ ...th, textAlign: "right" }}>{lang === "es" ? "Gastos" : "Fees"}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.paidCol}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.amountDue}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.status}</th>
              <th style={{ ...th, textAlign: "center", cursor: "default" }}>{lang === "es" ? "Reporte Enviado" : "Report Sent"}</th>
            </tr></thead>
            <tbody>{sorted.map(function(b) {
              var totals = calcBillTotals(b);
              var paidSum = paidAmount(b);
              var due = +(totals.subtotal - paidSum).toFixed(2);
              var st = b.status || "draft";
              if (st !== "draft" && paidSum > 0 && due <= 0.01) st = "paid";
              var ss = BILL_STATUS_STYLES[st] || BILL_STATUS_STYLES.draft;
              var statusKey = st === "draft" ? "statusDraft" : st === "finalized" ? "statusFinalized" : "statusPaid";
              /* 2026-10-16, Arturo: "solo ciertas partes del renglon son
                 clickeables... el renglon completo debe ser clickeable"
                 — every plain cell below now carries the SAME onClick
                 directly instead of relying only on bubbling from the
                 <tr>. Cells with their own interactive content
                 (checkbox, linked-invoice links, sent icon) keep their
                 own stopPropagation. */
              var openRow = function() { returnModuleRef.current = null; setReopenInEditMode(false); setViewing(b); };
              return (
                <tr key={b.id} onClick={openRow} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[b.id] ? "rgba(185,188,192,0.3)" : "transparent" }} onMouseEnter={function(e) { e.currentTarget.style.background = selected[b.id] ? "rgba(185,188,192,0.4)" : "rgba(185,188,192,0.2)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = selected[b.id] ? "rgba(185,188,192,0.3)" : "transparent"; }}>
                  <td style={{ padding: "6px 8px" }} onClick={function(e) { e.stopPropagation(); }}><input type="checkbox" checked={!!selected[b.id]} onChange={function(e) { toggleOne(b.id, e); }} /></td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "#1a2636", textAlign: "center" }}>{b.lot}<RejectionBadge cases={rejectedCasesForLot(b.lot, freshInvoicesLS())} /></td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{seasonNameForBill(b.billDate, b.seasonId) || "—"}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{fmtDate(b.billDate)}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{fmtDate(b.dueDate)}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-muted)", textAlign: "center" }}>W{(function() { var shps = loadLS("gfi-shipments", []); var shp = shps.find(function(s) { return String(s.lot) === String(b.lot); }); return shp ? getISOWeek(shp.dateArrival || shp.dateDeparture || b.billDate) : getISOWeek(b.billDate); })()}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, whiteSpace: "nowrap" }}>{resolveShipperName(b.supplierId) || b.shipperName || "\u2014"}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{b.proforma || "\u2014"}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{b.entryNumber || "\u2014"}</td>
                  {/* INVOICE / PU \u2014 clickable PU# links. Each PU number
                      opens the read-only InvoicePreviewModal (same modal
                      as the Linked Invoices mini-table inside the bill
                      detail); THAT span stops propagation so it doesn't
                      also open the bill underneath it. 2026-10-16,
                      Arturo: "entre invoice/pu y pallets tampoco
                      interactua" \u2014 the cell itself used to swallow
                      EVERY click (including its own blank space, no PU
                      links there at all) to protect the links above;
                      now only the links themselves stop propagation, so
                      the rest of the cell opens the bill like every
                      other column. */}
                  {/* 2026-10-20, Arturo: "hay un espacio grande entre
                      invoice/pu y pallets" — maxWidth:180 reserved
                      more room than most rows' actual PU text needs
                      (table-layout:auto sizes the column to its widest
                      row), leaving visible slack before Pallets on
                      every shorter row. Tightened to 110 — still wide
                      enough for "PU 17474", multi-PU rows just ellipsis
                      sooner (title= already shows the full list). */}
                  <td onClick={openRow} style={{ padding: "6px 8px", fontSize: 9.5, maxWidth: 110, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" }} title={b.linkedInvoices || ""}>
                    {((b.linkedInvoiceObjs || []).length === 0) ? <span style={{ color: "var(--color-text-muted)" }}>{"\u2014"}</span> : (b.linkedInvoiceObjs || []).map(function(inv, i) {
                      var label = "PU " + (inv.puNumber || inv.id || "?");
                      return <span key={inv.id || i}>{i > 0 ? <span style={{ color: "var(--color-text-muted)", margin: "0 4px" }}>{","}</span> : null}<span onClick={function(e) { e.stopPropagation(); setPreviewInvoice(inv); }} onMouseEnter={function(e) { e.currentTarget.style.textDecoration = "underline"; }} onMouseLeave={function(e) { e.currentTarget.style.textDecoration = "none"; }} style={{ color: "#2563EB", cursor: "pointer", textDecoration: "none" }} title={lang === "es" ? "Ver factura" : "View invoice"}>{label}</span></span>;
                    })}
                  </td>
                  {/* 2026-10-20, Arturo: "los datos en pallets estan
                      alineados a la derecha, centralos" — Pallets is
                      the only one of these numeric columns centered
                      instead of right-aligned (the rest — Cases,
                      Sales, Fees, Paid, Amount Due — are real money/
                      quantity figures meant to line up on their
                      decimal/ones place; Pallets is a small count,
                      same criterion already used for it elsewhere in
                      the app, e.g. Shipments' own Pallets column). */}
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center", fontSize: 9.5 }}>{(b.lines || []).reduce(function (acc, l) { return acc + (parseInt(l.pallets, 10) || 0); }, 0) || "—"}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5 }}>{(function () { var c = (b.lines || []).reduce(function (acc, l) { return acc + (parseInt(l.cases, 10) || 0); }, 0); return c > 0 ? c.toLocaleString("en-US") : "—"; })()}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5 }}>{fmtMoney(totals.grossSales)}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: "#DC2626" }}>({fmtMoney(totals.totalFees + totals.commission)})</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: paidSum > 0 ? "#059669" : "var(--color-text-muted)" }}>{paidSum > 0 ? fmtMoney(paidSum) : "—"}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: "var(--color-accent)" }}>{fmtMoney(due)}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center" }}>{(function() {
                    /* Show ALL distinct sale-status values across the
                       bill's product lines as separate badges (same
                       pattern as the invoices list). Set-dedup so two
                       lines with the same status render one badge.
                       flex-wrap keeps the row stable when 3+ badges.
                       2026-09-16 — Arturo: lot 58046 had 2 POs whose
                       real status (Good Delivery vs Protection) never
                       land at the same moment, so `l.saleStatus` as
                       PERSISTED on the bill line can be stale between
                       those two events — recompute live via
                       deriveBillLineLiveStatus against every invoice
                       actually tied to this lot right now, same
                       self-healing rule as pallet-level status
                       everywhere else in the app. */
                    /* Twin River (2026-10-15) — see twinRiverLotStatus
                       comment above (liveStatusesForBill).
                       2026-10-30, Arturo (lot 58074): per-product
                       (twinRiverProductStatus), not one lot-wide badge
                       — same fix as the detail table/InvoiceRegistry.js. */
                    var shpForBadgeStatus = getShipments().find(function (s) { return String(s.lot) === String(b.lot); });
                    var isClariFreshBadge = shpForBadgeStatus && shpForBadgeStatus.texasQcMode === "clarifresh";
                    var lotInvoicesForStatus = freshInvoicesLS().filter(function(inv) { return String(inv.lot) === String(b.lot); });
                    var invoiceLinesForLot = [];
                    lotInvoicesForStatus.forEach(function(inv) { (inv.lines || []).forEach(function(il) { invoiceLinesForLot.push(il); }); });
                    var seen = {}; var statuses = [];
                    (b.lines || []).forEach(function(l) {
                      var live = isClariFreshBadge
                        ? (twinRiverProductStatus(shpForBadgeStatus, l.productBase || l.productName) || "pendingPickUp")
                        : deriveBillLineLiveStatus(l, invoiceLinesForLot);
                      if (live && !seen[live]) { seen[live] = true; statuses.push(live); }
                    });
                    if (statuses.length === 0) {
                      statuses = [resolvePendingStage(lotInvoicesForStatus, slPassingIndex)];
                    }
                    var fallbackSc = { bg: "rgba(107,114,128,0.10)", text: "#374151", border: "rgba(107,114,128,0.25)" };
                    return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>{statuses.map(function(s) {
                      var sc = SALE_STATUS_COLORS[s] || fallbackSc;
                      return <span key={s} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: sc.bg, color: sc.text, border: "1px solid " + (sc.border || "transparent"), boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[s] || s}</span>;
                    })}</span>;
                  })()}</td>
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center" }}><span style={{ padding: "3px 9px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: SALE_STATUS_BEVEL }}>{t[statusKey]}</span></td>
                  {/* 2026-10-16, Arturo: "en bills es en report sent" —
                      same fix as Payment's Confirmation Sent column:
                      SentStatusIcon is a plain display icon with just a
                      tooltip, no click handler of its own, so this
                      stopPropagation was blocking the row-open click for
                      no actual reason. */}
                  <td onClick={openRow} style={{ padding: "6px 8px", textAlign: "center" }}>
                    <SentStatusIcon sent={!!(b.extra && b.extra.salesReportSent)} details={b.extra && b.extra.salesReportSent} lang={lang} />
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {viewing && <DetailPanel
        key={viewing.id}
        bill={viewing}
        bills={bills}
        t={t} lang={lang}
        initialMode={reopenInEditMode ? "edit" : "view"}
        onSave={function(updated) { handleSave(updated); setViewing(updated); }}
        onDelete={handleDelete}
        onEditLock={function(rec) { setViewing(null); setLockedRecord(rec); }}
        onClose={function() { setViewing(null); closeToOrigin(); }}
        onCreateInvoice={function(bill) {
          setViewing(null);
          returnModuleRef.current = null;
          if (window.__gfiNavigate) window.__gfiNavigate("invoices");
          setTimeout(function() { if (window.__gfiInvoiceCreate) window.__gfiInvoiceCreate(bill); }, 200);
        }} />}
      {showForm && !viewing && <BillForm bill={editing} bills={bills} t={t} lang={lang} onSave={handleSave} onDelete={handleDelete} onCancel={function() { setShowForm(false); setEditing(null); closeToOrigin(); }} />}
      {previewInvoice && <InvoicePreviewModal invoice={previewInvoice} lang={lang} onClose={function() { setPreviewInvoice(null); }} />}
      {showBatchPay && <BatchPaymentModal bills={selectedBills} t={t} lang={lang} onConfirm={handleBatchPaymentConfirm} onCancel={function() { setShowBatchPay(false); }} />}
      {lockedRecord && <EditLockDialog record={lockedRecord} t={t} onClose={function() { setLockedRecord(null); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lockedRecords={bulkLocked} lang={lang} onCancel={function() { setShowBulkDelete(false); }} onConfirm={handleBulkDelete} />}
      {showBulkStatus && <BulkStatusDialog count={selectedCount} lockedRecords={bulkLocked} statuses={BILL_STATUSES.map(function(s) { return { value: s, label: t[s === "draft" ? "statusDraft" : s === "finalized" ? "statusFinalized" : "statusPaid"] || s }; })} lang={lang} onCancel={function() { setShowBulkStatus(false); }} onConfirm={handleBulkStatusChange} />}
      {confirmationPayment && (function() { var pr = partyAndRowsForBillPayment(confirmationPayment); return <PaymentConfirmationPreview payment={confirmationPayment} party={pr.party} rows={pr.rows} lang={lang} onClose={function() { setConfirmationPayment(null); }} />; })()}
      {salesReportPreviewBill && (
        <SalesReportPdfPreview
          bill={salesReportPreviewBill}
          suppliers={getSuppliers()}
          lang={lang}
          onSent={function (payload) {
            var sentInfo = buildSentInfo(payload);
            persistSingle({ ...salesReportPreviewBill, extra: { ...(salesReportPreviewBill.extra || {}), salesReportSent: sentInfo } })
              .catch(function (e) { console.warn("[BillRegistry] could not stamp salesReportSent:", e && e.message); });
          }}
          onClose={function() { setSalesReportPreviewBill(null); }}
        />
      )}
      {emailTargetBills && emailTargetBills.length > 0 && (
        <EmailSalesReportModal
          bills={emailTargetBills}
          suppliers={getSuppliers()}
          lang={lang}
          onSent={function (payload) {
            var sentInfo = buildSentInfo(payload);
            persistMany(emailTargetBills.map(function (b) { return { ...b, extra: { ...(b.extra || {}), salesReportSent: sentInfo } }; }))
              .catch(function (e) { console.warn("[BillRegistry] could not stamp salesReportSent (bulk):", e && e.message); });
          }}
          onClose={function() { setEmailTargetBills(null); }}
        />
      )}
      {toast && <Toast message={toast} onClose={function() { setToast(null); }} />}
    </div>
  );
}